import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, switchMap, throwError } from 'rxjs';

import { CommunityApplicationService } from '@offconon/core-api';

@Injectable({
  providedIn: 'root',
})
export class AppHelperService {
  private activatedRoute = inject(ActivatedRoute);
  private communityApplicationService = inject(CommunityApplicationService);
  private translateService = inject(TranslateService);

  community_application_type__in: any;

  getAppConfig(app_link: any) {
    let app_info: any = null;

    switch (app_link) {
      case 'botanix':
      case 'garden':
        app_info = {
          visibility: true,
          icon: 'yard',
          app_url: '/botanix',
          app_brand_name: 'Botanix',
          community_application_type: 'garden',
          app_brand_img_url: '/assets/images/app_meta/garden.jpg',
          app_brand_short_desc:
            'A marketplace for plant lovers and gardening enthusiasts. Discover a wide range of plants, gardening tools, seeds, and expert tips to transform your green space.',
        };
        break;

      case 'language_exchange':
      case 'speakup':
        app_info = {
          visibility: true,
          icon: 'translate',
          app_url: '/speakup',
          app_brand_name: 'SpeakUp',
          community_application_type: 'language_exchange',
          app_brand_img_url: '/assets/images/app_meta/language_exchange.jpg',
          app_brand_short_desc:
            'Connect with people from around the world to practice and learn new languages. Join a global community where language learners share knowledge, culture, and conversation.',
        };
        break;

      case 'business_networking':
      case 'bizconnect':
        app_info = {
          visibility: true,
          icon: 'handshake',
          app_url: '/bizconnect',
          app_brand_name: 'BizConnect',
          community_application_type: 'business_networking',
          app_brand_img_url: '/assets/images/app_meta/business_networking.jpg',
          app_brand_short_desc:
            'A platform for professionals to connect, collaborate, and grow their businesses. Discover networking events, business services, and valuable connections to elevate your career.',
        };
        break;

      case 'doctor_finder':
      case 'medspot':
        app_info = {
          visibility: true,
          icon: 'vaccines',
          app_url: '/medspot',
          app_brand_name: 'MedSpot',
          community_application_type: 'doctor_finder',
          app_brand_img_url: '/assets/images/app_meta/doctor_finder.jpg',
          app_brand_short_desc:
            'Find and book appointments with trusted healthcare professionals in your area. Browse by specialty, location, and patient reviews to get the best care possible.',
        };
        break;

      case 'beautywave':
      case 'wellness_beauty':
        app_info = {
          visibility: false,
          icon: 'tsunami',
          app_url: '/beauty-wave',
          app_brand_name: 'BeautyWave',
          community_application_type: 'wellness_beauty',
          app_brand_img_url: '/assets/images/app_meta/wellness_beauty.jpg',
          app_brand_short_desc:
            'Your one-stop shop for wellness, beauty products, and services. Explore spa treatments, skincare products, and wellness tips to look and feel your best.',
        };
        break;

      case 'pets':
      case 'petnest':
        app_info = {
          visibility: true,
          icon: 'pets',
          app_url: '/petnest',
          app_brand_name: 'PetNest',
          community_application_type: 'pets',
          app_brand_img_url: '/assets/images/app_meta/pets.jpg',
          app_brand_short_desc:
            'A dedicated marketplace for pet lovers. Find pet supplies, accessories, grooming services, and even adoption opportunities to give your furry friends the best life.',
        };
        break;

      case 'skilllink':
      case 'skill_link':
      case 'skill-link':
        app_info = {
          visibility: true,
          icon: 'add_link',
          app_url: '/skill-link',
          app_brand_name: 'Skill Link',
          community_application_type: 'skill_link',
          app_brand_img_url: '/assets/images/app_meta/skill_link.jpg',
          app_brand_short_desc:
            'A hub for learning and sharing skills. Access courses, workshops, and consulting services across various fields to enhance your knowledge and expertise.',
        };
        break;

      case 'begreen':
      case 'be_green':
      case 'be-green':
        app_info = {
          visibility: true,
          icon: 'eco',
          app_url: '/be-green',
          app_brand_name: 'Be Green',
          community_application_type: 'be_green',
          app_brand_img_url: '/assets/images/app_meta/be_green.jpg',
          app_brand_short_desc:
            'Discover eco-friendly products and services that promote a sustainable lifestyle. From zero-waste items to green solutions for your home, Be Green is your guide to a better planet.',
        };
        break;

      case 'secondchance':
      case 'second-chance':
      case 'second_chance':
        app_info = {
          visibility: true,
          icon: 'volunteer_activism',
          app_url: '/second-chance',
          app_brand_name: 'Second Chance',
          community_application_type: 'second_chance',
          app_brand_img_url: '/assets/images/app_meta/second_chance.jpg',
          app_brand_short_desc:
            'A charity commmunity dedicated to saving animals and helping people in need. Support animal rescue, rehabilitation, and adoption efforts, while also contributing to causes that uplift individuals and communities. Every interaction helps give animals a new lease on life and provides vital support to people facing challenging circumstances.',
        };
        break;

      case 'events':
      case 'eventix':
        app_info = {
          visibility: true,
          icon: 'event',
          app_url: '/eventix',
          app_brand_name: 'Eventix',
          community_application_type: 'events',
          app_brand_img_url: '/assets/images/app_meta/events.jpg',
          app_brand_short_desc:
            'Find and book tickets for events, concerts, and activities happening near you. Create and promote your own events to connect with a larger audience.',
        };
        break;

      case 'renting':
      case 'rentspace':
        app_info = {
          visibility: true,
          icon: 'car_rental',
          app_url: '/rentspace',
          app_brand_name: 'RentSpace',
          community_application_type: 'renting',
          app_brand_img_url: '/assets/images/app_meta/renting.jpg',
          app_brand_short_desc:
            'The ultimate marketplace for renting everything you need. From equipment to real estate, find rental options that suit your budget and needs.',
        };
        break;

      case 'booking':
      case 'scheduly':
        app_info = {
          visibility: true,
          icon: 'calendar_month',
          app_url: '/scheduly',
          app_brand_name: 'Scheduly',
          community_application_type: 'booking',
          app_brand_img_url: '/assets/images/app_meta/booking.jpg',
          app_brand_short_desc:
            'A seamless platform for booking appointments, services, and reservations. From restaurants to travel accommodations, plan your activities effortlessly.',
        };
        break;

      case 'job':
      case 'careerpath':
      case 'career-path':
        app_info = {
          visibility: false,
          icon: 'add_road',
          app_url: '/careerpath',
          app_brand_name: 'CareerPath',
          community_application_type: 'job',
          app_brand_img_url: '/assets/images/app_meta/job.jpg',
          app_brand_short_desc:
            'A dynamic job marketplace connecting employers and job seekers. Browse job listings, upload resumes, and find the perfect career opportunity tailored to your skills.',
        };
        break;

      case 'digitalproducts':
      case 'digital_downloads':
      case 'digital_product':
      case 'pixelmarket':
        app_info = {
          visibility: false,
          icon: 'camera_enhance',
          app_url: '/pixelmarket',
          app_brand_name: 'PixelMarket',
          community_application_type: 'digital_downloads',
          app_brand_img_url: '/assets/images/app_meta/digital_downloads.jpg',
          app_brand_short_desc:
            'Buy, sell, and explore digital goods like software, eBooks, templates, and more. A marketplace for creators to showcase their digital products and for buyers to find what they need.',
        };
        break;

      case 'taskly':
      case 'services':
        app_info = {
          visibility: true,
          icon: 'task',
          app_url: '/taskly',
          app_brand_name: 'Taskly',
          community_application_type: 'taskly',
          app_brand_img_url: '/assets/images/app_meta/taskly.jpg',
          app_brand_short_desc:
            'Taskly is the ultimate online marketplace to connect with skilled professionals for any task you need. From home repairs and personal services to digital design and professional consulting, Taskly makes it easy to find, compare, and hire the right expert for your needs—all in one platform.',
        };
        break;

      case 'market-space':
      case 'market_space':
      case 'physical_product':
        app_info = {
          visibility: true,
          icon: 'shopping_cart_checkout',
          app_url: '/market-space',
          app_brand_name: 'MarketSpace',
          community_application_type: 'market_space',
          app_brand_img_url: '/assets/images/app_meta/market_space.jpg',
          app_brand_short_desc:
            'MarketSpace is your one-stop marketplace for quality products that inspire. From everyday essentials to standout pieces, discover items crafted for life moments, big and small.',
        };
        break;

      case 'bucket-list':
      case 'bucket_list':
        app_info = {
          visibility: true,
          icon: 'checklist',
          app_url: '/bucket-list',
          app_brand_name: 'Bucket list',
          community_application_type: 'bucket_list',
          app_brand_img_url: '/assets/images/app_meta/bucket_list.jpg',
          app_brand_short_desc: 'The BucketList idea where people share goals and swap favors',
        };
        break;

      case 'travelbuddies':
      case 'travel-buddies':
      case 'travel_buddies':
        app_info = {
          visibility: true,
          icon: 'travel_explore',
          app_url: '/travel-buddies',
          app_brand_name: 'Travel Buddies',
          community_application_type: 'travel_buddies',
          app_brand_img_url: '/assets/images/app_meta/travel_buddies.jpg',
          app_brand_short_desc:
            'Find your perfect travel companion. Connect with like-minded explorers and embark on unforgettable adventures together.',
        };
        break;

      case 'collectors':
        app_info = {
          visibility: true,
          icon: 'collections_bookmark',
          app_url: '/collectors',
          app_brand_name: 'Collectors',
          community_application_type: 'collectors',
          app_brand_img_url: '/assets/images/app_meta/collectors.jpg',
          app_brand_short_desc:
            'Buy, sell, and showcase unique collectibles. A dedicated platform for enthusiasts to connect, trade, and grow their collections.',
        };
        break;

      case 'realestate':
      case 'real-estate':
      case 'real_estate':
        app_info = {
          visibility: true,
          icon: 'home_work',
          app_url: '/real-estate',
          app_brand_name: 'Real estate',
          community_application_type: 'real_estate',
          app_brand_img_url: '/assets/images/app_meta/real_estate.jpg',
          app_brand_short_desc:
            'Find your dream home or sell your property with ease. A comprehensive platform for buying, selling, and renting real estate.',
        };
        break;

      case 'boat':
      case 'boats-yachts':
        app_info = {
          visibility: true,
          icon: 'directions_boat',
          app_url: '/boats-yachts',
          app_brand_name: 'Boats and Yachts',
          community_application_type: 'boat',
          app_brand_img_url: '/assets/images/app_meta/boat.jpg',
          app_brand_short_desc:
            'Buy, sell, or rent boats with ease. A dedicated platform for boating enthusiasts to connect and explore their passion.',
        };
        break;

      case 'cars-wheels':
      case 'cars':
        app_info = {
          visibility: false,
          icon: 'directions_car',
          app_url: '/cars-wheels',
          app_brand_name: 'Cars and wheels',
          community_application_type: 'cars',
          app_brand_img_url: '/assets/images/app_meta/cars.jpg',
          app_brand_short_desc:
            'Buy, sell, or rent cars effortlessly. A trusted platform to explore vehicles for every need and budget.',
        };
        break;

      case 'cooking':
        app_info = {
          visibility: true,
          icon: 'flatware',
          app_url: '/cooking',
          app_brand_name: 'Cooking and recipes',
          community_application_type: 'cooking',
          app_brand_img_url: '/assets/images/app_meta/cooking.jpg',
          app_brand_short_desc:
            'Buy, sell, and share your love for cooking. A platform for foodies to explore recipes, cooking tools, and culinary experiences.',
        };
        break;

      case 'fitness':
        app_info = {
          visibility: true,
          icon: 'directions_run',
          app_url: '/fitness',
          app_brand_name: 'Fitness and longevity',
          community_application_type: 'fitness',
          app_brand_img_url: '/assets/images/app_meta/fitness.jpg',
          app_brand_short_desc:
            'Find fitness programs, personal trainers, and wellness solutions tailored to your needs. A platform for fitness enthusiasts to achieve their goals with expert guidance.',
        };
        break;

      case 'volunteer':
        app_info = {
          visibility: false,
          icon: 'add_reaction',
          app_url: '/volunteer',
          app_brand_name: 'Volunteer',
          community_application_type: 'volunteer',
          app_brand_img_url: '/assets/images/app_meta/volunteer.jpg',
          app_brand_short_desc:
            'Connect with opportunities to make a difference by joining volunteer initiatives in your community or worldwide.',
        };
        break;

      case 'agriculture-livestock':
      case 'agriculture_livestock':
        app_info = {
          visibility: false,
          icon: 'agriculture',
          app_url: '/agriculture-livestock',
          app_brand_name: 'Agriculture / Livestock',
          community_application_type: 'agriculture_livestock',
          app_brand_img_url: '/assets/images/app_meta/agriculture_livestock.jpg',
          app_brand_short_desc:
            'A marketplace connecting agricultural producers and livestock businesses for trading, resources, and collaboration.',
        };
        break;

      case 'office-meeting':
      case 'coworking_space':
      case 'meetingpoint':
        app_info = {
          visibility: false,
          icon: 'meeting_room',
          app_url: '/office-meeting',
          app_brand_name: 'MeetingPoint',
          community_application_type: 'coworking_space',
          app_brand_img_url: '/assets/images/app_meta/coworking_space.jpg',
          app_brand_short_desc:
            'A professional space booking platform for meetings, conferences, and co-working spaces.',
        };
        break;

      case 'kids':
      case 'kids_space':
        app_info = {
          visibility: false,
          icon: 'child_care',
          app_url: '/kids',
          app_brand_name: 'Kids',
          community_application_type: 'kids_space',
          app_brand_img_url: '/assets/images/app_meta/kids_space.jpg',
          app_brand_short_desc:
            "A dedicated marketplace for kids' products, services, and activities to make childhood special.",
        };
        break;

      case 'one-love':
      case 'dating':
        app_info = {
          visibility: false,
          icon: 'supervisor_account',
          app_url: '/one-love',
          app_brand_name: 'Dating',
          community_application_type: 'dating',
          app_brand_img_url: '/assets/images/app_meta/dating.jpg',
          app_brand_short_desc:
            'A platform to foster connections and celebrate inclusivity, diversity, and love.',
        };
        break;

      case 'sport':
      case 'team-up':
        app_info = {
          visibility: false,
          icon: 'groups',
          app_url: '/team-up',
          app_brand_name: 'Team Up',
          community_application_type: 'sport',
          app_brand_img_url: '/assets/images/app_meta/sport.jpg',
          app_brand_short_desc:
            'A collaborative platform for teams to connect, share ideas, and achieve goals together.',
        };
        break;

      case 'mindset_loading':
      case 'mindset':
        app_info = {
          visibility: false,
          icon: 'self_improvement',
          app_url: '/mindset',
          app_brand_name: 'Mindset loading',
          community_application_type: 'mindset',
          app_brand_img_url: '/assets/images/app_meta/mindset.jpg',
          app_brand_short_desc:
            'A platform offering tools, resources, and guidance for personal development and positive thinking.',
        };
        break;

      case 'grocery':
      case 'grocery_to_home':
        app_info = {
          visibility: false,
          icon: 'egg',
          app_url: '/grocery',
          app_brand_name: 'Grocery to home',
          community_application_type: 'grocery',
          app_brand_img_url: '/assets/images/app_meta/grocery.jpg',
          app_brand_short_desc:
            'A convenient online marketplace for fresh produce, daily essentials, and household goods.',
        };
        break;

      case 'passionist':
      case 'diy-handmade':
      case 'diy_handmade':
        app_info = {
          visibility: true,
          icon: 'handyman',
          app_url: '/diy-handmade',
          app_brand_name: 'Passionist',
          community_application_type: 'diy_handmade',
          app_brand_img_url: '/assets/images/app_meta/diy_handmade.jpg',
          app_brand_short_desc:
            'A platform to showcase and sell unique handmade crafts and DIY projects.',
        };
        break;

      case 'fashion_and_style':
      case 'fashion':
        app_info = {
          visibility: false,
          icon: 'auto_awesome',
          app_url: '/fashion',
          app_brand_name: 'Fashion and Style',
          community_application_type: 'fashion',
          app_brand_img_url: '/assets/images/app_meta/fashion.jpg',
          app_brand_short_desc:
            'An online hub for fashion trends, clothing, and accessories for every style.',
        };
        break;

      case 'tech':
      case 'technical_madness':
        app_info = {
          visibility: false,
          icon: 'computer',
          app_url: '/tech',
          app_brand_name: 'Technical madness',
          community_application_type: 'tech',
          app_brand_img_url: '/assets/images/app_meta/tech.jpg',
          app_brand_short_desc:
            'A marketplace for the latest technology products, gadgets, and services.',
        };
        break;

      case 'crypto-blockchain':
      case 'crypto_blockchain':
      case 'crypto_nitro':
        app_info = {
          visibility: false,
          icon: 'currency_bitcoin',
          app_url: '/crypto-blockchain',
          app_brand_name: 'Crypto nitro',
          community_application_type: 'crypto_blockchain',
          app_brand_img_url: '/assets/images/app_meta/crypto_blockchain.jpg',
          app_brand_short_desc:
            'A hub for blockchain enthusiasts to trade, learn, and explore cryptocurrency innovations.',
        };
        break;

      case 'b-to-b':
      case 'b_to_b':
      case 'bsquare':
        app_info = {
          visibility: false,
          icon: 'business',
          app_url: '/b-to-b',
          app_brand_name: 'B square',
          community_application_type: 'b_to_b',
          app_brand_img_url: '/assets/images/app_meta/b_to_b.jpg',
          app_brand_short_desc:
            'A platform for businesses to connect, trade, and collaborate seamlessly.',
        };
        break;

      case 'home_and_style':
      case 'home-design':
      case 'home_design':
        app_info = {
          visibility: false,
          icon: 'home',
          app_url: '/home-design',
          app_brand_name: 'Home and Style',
          community_application_type: 'home_design',
          app_brand_img_url: '/assets/images/app_meta/home_design.jpg',
          app_brand_short_desc:
            'A marketplace for home décor, furniture, and interior design inspiration.',
        };
        break;

      case 'media-channels':
      case 'media_channels':
        app_info = {
          visibility: false,
          icon: 'video_collection',
          app_url: '/media-channels',
          app_brand_name: 'Media channels',
          community_application_type: 'media_channels',
          app_brand_img_url: '/assets/images/app_meta/media_channels.jpg',
          app_brand_short_desc:
            'A platform to explore and manage media services, channels, and advertising opportunities.',
        };
        break;

      case 'independentcitizens':
      case 'digital-nomads':
      case 'digital_nomads':
        app_info = {
          visibility: false,
          icon: 'accessibility_new',
          app_url: '/digital-nomads',
          app_brand_name: 'Independent citizens',
          community_application_type: 'digital_nomads',
          app_brand_img_url: '/assets/images/app_meta/digital_nomads.jpg',
          app_brand_short_desc:
            'A platform designed for remote workers to find tools, resources, and a global community.',
        };
        break;

      case 'gaming':
      case 'gamerworld':
        app_info = {
          visibility: false,
          icon: 'videogame_asset',
          app_url: '/gaming',
          app_brand_name: 'Gamer world',
          community_application_type: 'gaming',
          app_brand_img_url: '/assets/images/app_meta/gaming.jpg',
          app_brand_short_desc:
            'A community hub for gamers to discover games, tournaments, and connect with other players.',
        };
        break;

      case 'erotic':
      case 'sensualshudder':
        app_info = {
          visibility: false,
          icon: 'cruelty_free',
          app_url: '/erotic',
          app_brand_name: 'Sensual shudder',
          community_application_type: 'erotic',
          app_brand_img_url: '/assets/images/app_meta/erotic.jpg',
          app_brand_short_desc:
            'A tasteful and discreet platform for exploring sensual products and services.',
        };
        break;

      case 'roommatefinder':
      case 'roommate':
        app_info = {
          visibility: false,
          icon: 'bedroom_parent',
          app_url: '/roommate',
          app_brand_name: 'Roommate finder',
          community_application_type: 'roommate',
          app_brand_img_url: '/assets/images/app_meta/roommate.jpg',
          app_brand_short_desc:
            'A platform to connect individuals looking for shared living arrangements.',
        };
        break;

      case 'aviation':
        app_info = {
          visibility: false,
          icon: 'flight_takeoff',
          app_url: '/aviation',
          app_brand_name: 'Aviation',
          community_application_type: 'aviation',
          app_brand_img_url: '/assets/images/app_meta/aviation.jpg',
          app_brand_short_desc:
            'A specialized marketplace for aviation enthusiasts, professionals, and services.',
        };
        break;

      case 'taxi':
        app_info = {
          visibility: false,
          icon: 'local_taxi',
          app_url: '/taxi',
          app_brand_name: 'Taxi',
          community_application_type: 'taxi',
          app_brand_img_url: '/assets/images/app_meta/taxi.jpg',
          app_brand_short_desc:
            'A convenient platform to book taxis and rideshares for safe and reliable transportation.',
        };
        break;

      case 'goalmanager':
      case 'goal_manager':
        app_info = {
          visibility: false,
          icon: 'ads_click',
          app_url: '/goal-manager',
          app_brand_name: 'Goal Manager',
          community_application_type: 'goal_manager',
          app_brand_img_url: '/assets/images/app_meta/goal_manager.jpg',
          app_brand_short_desc:
            'Plan, track, and achieve your goals. A platform designed to help you stay focused and turn your ambitions into accomplishments.',
        };
        break;

      case 'calendar':
        app_info = {
          visibility: true,
          icon: 'calendar_month',
          app_url: '/calendar',
          app_brand_name: 'Calendar',
          community_application_type: 'calendar',
          app_brand_img_url: '/assets/images/app_meta/calendar.jpg',
          app_brand_short_desc:
            'Stay organized and on top of your schedule with a dynamic calendar that simplifies your day-to-day planning.',
        };
        break;

      case 'activity_stream':
        app_info = {
          visibility: true,
          icon: 'mark_unread_chat_alt',
          app_url: '/activity-stream',
          app_brand_name: 'Activity Stream',
          community_application_type: 'activity_stream',
          app_brand_img_url: '/assets/images/app_meta/activity_stream.jpg',
          app_brand_short_desc:
            'Share, connect, and engage with others through a vibrant stream of posts, comments, and interactions.',
        };
        break;

      case 'task_manager':
        app_info = {
          visibility: true,
          icon: 'add_task',
          app_url: '/task-manager',
          app_brand_name: 'Task Manager',
          community_application_type: 'task_manager',
          app_brand_img_url: '/assets/images/app_manager/task_manager.jpg',
          app_brand_short_desc:
            'Prioritize, manage, and complete your tasks efficiently with an intuitive tool designed to enhance productivity.',
        };
        break;

      case 'notes':
        app_info = {
          visibility: true,
          icon: 'note_add',
          app_url: '/notes',
          app_brand_name: 'Notes',
          community_application_type: 'notes',
          app_brand_img_url: '/assets/images/app_manager/notes.jpg',
          app_brand_short_desc:
            'Capture ideas, jot down thoughts, and organize information with a smart, simple note-taking tool.',
        };
        break;

      case 'contacts':
        app_info = {
          visibility: true,
          icon: 'contacts',
          app_url: '/contacts',
          app_brand_name: 'Contacts',
          community_application_type: 'contacts',
          app_brand_img_url: '/assets/images/app_meta/contacts.jpg',
          app_brand_short_desc:
            'Effortlessly manage and organize your personal and professional connections in one central hub.',
        };
        break;

      case 'email_manager':
        app_info = {
          visibility: true,
          icon: 'email',
          app_url: '/email-manager',
          app_brand_name: 'Email Manager',
          community_application_type: 'email_manager',
          app_brand_img_url: '/assets/images/app_meta/email_manager.jpg',
          app_brand_short_desc:
            'Streamline your communications with an intelligent, user-friendly email platform designed for productivity.',
        };
        break;

      case 'drive':
        app_info = {
          visibility: true,
          icon: 'drive_file_move_outline',
          app_url: '/drive',
          app_brand_name: 'Drive',
          community_application_type: 'drive',
          app_brand_img_url: '/assets/images/app_manager/drive.jpg',
          app_brand_short_desc:
            'Store, share, and collaborate on files securely with a cloud-based drive tailored to your needs.',
        };
        break;

      case 'academy':
        app_info = {
          visibility: true,
          icon: 'cast_for_education',
          app_url: '/academy',
          app_brand_name: 'Academy',
          community_application_type: 'academy',
          app_brand_img_url: '/assets/images/app_meta/academy.jpg',
          app_brand_short_desc:
            'A platform for learning and self-improvement, offering a variety of courses and educational resources.',
        };
        break;

      case 'storefinder':
      case 'store_finder':
        app_info = {
          visibility: true,
          icon: 'storefront',
          app_url: '/businesses-stores',
          app_brand_name: 'Store Finder',
          community_application_type: 'store_finder',
          app_brand_img_url: '/assets/images/app_meta/store_finder.jpg',
          app_brand_short_desc:
            'A convenient platform to locate and explore businesses and stores in your area or across regions.',
        };
        break;

      case 'chat':
        app_info = {
          visibility: true,
          icon: 'chat',
          app_url: '/communication',
          app_brand_name: 'Chat',
          community_application_type: 'chat',
          app_brand_img_url: '/assets/images/app_meta/chat.jpg',
          app_brand_short_desc:
            'An easy-to-use communication tool to stay connected with friends, family, and professional networks.',
        };
        break;
    }

    return app_info;
  }

  getSearchHelper(data: any) {
    switch (data) {
      case 'featured': {
        this.community_application_type__in = [
          'garden',
          'language_exchange',
          'business_networking',
          'pets',
          'be_green',
          'second_chance',
          'bucket_list',
          'travel_buddies',
        ];
        break;
      }
      case 'communities': {
        this.community_application_type__in = [
          'garden',
          'language_exchange',
          'business_networking',
          'pets',
          'be_green',
          'second_chance',
          'bucket_list',
          'travel_buddies',
          'collectors',
          'cooking',
          'fitness',
          'volunteer',
          'agriculture_livestock',
          'kids_space',
          'dating',
          'sport',
          'mindset',
          'diy_handmade',
          'tech',
          'crypto_blockchain',
          'b_to_b',
          'digital_nomads',
          'gaming',
          'erotic',
          'roommate',
          'aviation',
        ];
        break;
      }
      case 'marketplaces': {
        this.community_application_type__in = [
          'doctor_finder',
          'wellness_beauty',
          'skill_link',
          'events',
          'renting',
          'booking',
          'job',
          'digitalproducts',
          'taskly',
          'market_space',
          'real_estate',
          'boat',
          'cars',
          'coworking_space',
          'grocery',
          'fashion',
          'home_design',
          'media_channels',
          'taxi',
        ];
        break;
      }
      case 'productivity': {
        this.community_application_type__in = [
          'goal_manager',
          'calendar',
          'activity_stream',
          'task_manager',
          'notes',
          'contacts',
          'email_manager',
          'drive',
          'academy',
          'store_finder',
          'chat',
        ];
        break;
      }
    }

    return this.community_application_type__in;
  }

  getAppCatalogRedirect(catalog_type: any) {
    let app_data: any = null;

    switch (catalog_type) {
      case 'plant': {
        app_data = {
          app_url: this.getAppConfig('garden')?.app_url,
        };
        break;
      }
      case 'animal': {
        app_data = {
          app_url: this.getAppConfig('pets')?.app_url,
        };
        break;
      }
      case 'food': {
        app_data = {
          app_url: this.getAppConfig('food')?.app_url,
        };
        break;
      }
      case 'physical_product': {
        app_data = {
          app_url: this.getAppConfig('market_space')?.app_url,
        };
        break;
      }
      case 'bucket_list': {
        app_data = {
          app_url: this.getAppConfig('bucket_list')?.app_url,
        };
        break;
      }
    }
    return app_data;
  }

  getAppData(app_link: any) {
    let app_info: any = null;

    switch (app_link) {
      case 'botanix':
      case 'garden': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          profile_type: 'garden',
          catalog_type: 'plant',
          category_type: 'plant',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Plant catalog',
          catalog_details_name: 'Plant identifier',
          catalog_wishlist_name: 'Plant wishlist',
          catalog_collection_name: 'My plants',
          catalog_create_title: 'Add a new plant to our database',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Member finder',
          member_profile_settings_name: 'My profile settings',
          settings_native_visible: true,
          settings_spoken_visible: true,
          header_backgroundImage: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Plants and garden',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Profile visitors',
          share_available: true,
          store_locator: {
            products: {
              name: 'Gardening stores near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'garden_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
          },
          business_store: {
            products: {
              name: 'Garden related businesses',
              meta: 'Interact and discover businesses.',
              search_reference: 'garden_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
          },
          swap_module: true,
          swap_name: 'Plant swap',
          donation_module: true,
          donation_name: 'Free plants',
          map_area: true,
          post_type: 'donate',
          talk_experts: {
            module: true,
            name: 'Talk to experts',
            meta_desc:
              'Access a wide range of expert tips and how-to guides to nurture your green space.',
            ads_type: ['service_offered'],
            url: '/talk-experts',
            browse_category_type: 'service_offered_template',
          },
          //todo delete test data
          physical_product_template: {
            module: true,
            name: 'teeeeeest',
            meta_desc:
              'Access a wide range of expert tips and how-to guides to nurture your green space.',
            ads_type: ['service_offered'],
            url: '/talk-experts',
            browse_category_type: 'service_offered_template',
          },
          workshops_courses: {
            module: true,
            name: 'Workshops / Courses',
            meta_desc: 'Workshops / Courses',
            ads_type: ['digital_media', 'digital_product', 'booking'],
            url: '/workshops-courses',
            browse_category_type: [
              'digital_media_template',
              'digital_product_template',
              'booking_template',
            ],
          },
          garden_videos: {
            module: true,
            name: 'Garden TV',
            meta_desc: 'Garden TV',
            ads_type: ['digital_media'],
            url: '/garden-videos',
            browse_category_type: 'digital_media_template',
          },
          rent: {
            module: true,
            name: 'Rental of tools and machinery',
            meta_desc: 'Rental of tools and machinery',
            ads_type: ['rent'],
            url: '/rent',
            browse_category_type: 'rent_template',
          },
          live_plants: {
            module: true,
            name: 'Plant purchase',
            meta_desc: 'Plant purchase',
            ads_type: ['physical_product'],
            url: '/live-plants',
            browse_category_type: 'physical_product_template',
          },
          garden_products: {
            module: true,
            name: 'Garden products',
            meta_desc: 'Garden products',
            ads_type: ['physical_product'],
            url: '/garden-products',
            browse_category_type: 'physical_product_template',
          },
          hire_expert: {
            module: true,
            name: 'Hire an expert',
            meta_desc: 'Hire an expert',
            ads_type: ['service_offered'],
            url: '/hire-expert',
            browse_category_type: 'service_offered_template',
          },
          digital: {
            module: true,
            name: 'Downloadable materials',
            meta_desc: 'Downloadable materials',
            ads_type: ['digital_product'],
            url: '/digital',
            browse_category_type: 'digital_product_template',
          },
          diy_videos: {
            module: true,
            name: 'DIY TV',
            meta_desc: 'DIY TV',
            ads_type: ['digital_media'],
            url: '/diy-videos',
            browse_category_type: 'digital_media_template',
          },
          jobs: {
            module: true,
            name: 'Jobs',
            meta_desc: 'Jobs',
            ads_type: ['job'],
            url: '/jobs',
            browse_category_type: 'job_template',
          },
          events: {
            module: true,
            name: 'Events',
            meta_desc: 'Events',
            ads_type: ['booking'],
            url: '/events',
            browse_category_type: 'booking_template',
          },
        };
        break;
      }
      case 'language_exchange':
      case 'speakup': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'language_exchange',
          catalog_type: 'physical_product',
          category_type: 'physical_product_template',
          community_application_type: 'language_exchange',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          catalog_module: false,
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Member finder',
          member_profile_settings_name: 'My profile settings',
          member_finder_language_search: true,
          member_finder_rating_search: true,
          settings_native_visible: true,
          settings_spoken_visible: true,
          settings_learn_visible: true,
          header_backgroundImage: '/assets/images/app_meta/language-exchange.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Social way to learn languages!',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: true,
          ratings_name: 'Ratings',
          profile_visitors_module: true,
          profile_visitors_name: 'Profile visitors',
          dashboard_name: 'Dashboard',
          store_locator: {
            products: {
              name: 'Language schools near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'language_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
          },
          business_store: {
            products: {
              name: 'Language learning related businesses',
              meta: 'Interact and discover businesses.',
              search_reference: 'language_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
          },
          swap_module: true,
          swap_name: 'Swap',
          donation_module: true,
          donation_name: 'Free stuff',
          post_type: 'donate',
          talk_experts: {
            module: true,
            name: 'Language teachers',
            meta_desc: 'Language teachers',
            ads_type: ['service_offered'],
            url: '/talk-experts',
          },
          workshops_courses: {
            module: true,
            name: 'Workshops / Courses',
            meta_desc: 'Workshops / Courses',
            ads_type: ['ad_digital_media', 'ad_digital_product', 'ad_booking'],
            url: '/workshops-courses',
          },
          language_videos: {
            module: true,
            name: 'Fluently TV',
            meta_desc: 'Fluently TV',
            ads_type: ['ad_digital_media'],
            url: '/language-videos',
          },
          products: {
            module: true,
            name: 'Products',
            meta_desc: 'Products',
            ads_type: ['ad_physical_product'],
            url: '/products',
          },
          digital: {
            module: true,
            name: 'Downloadable materials',
            meta_desc: 'Downloadable materials',
            ads_type: ['ad_digital_product'],
            url: '/digital',
          },
          jobs: {
            module: true,
            name: 'Jobs',
            meta_desc: 'Jobs',
            ads_type: ['job'],
            url: '/jobs',
          },
          events: {
            module: true,
            name: 'Events',
            meta_desc: 'Events',
            ads_type: ['ad_booking'],
            url: '/events',
          },
        };
        break;
      }

      case 'business_networking':
      case 'bizconnect': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'business_networking',
          community_application_type: 'business_networking',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          catalog_module: false,
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Member finder',
          member_profile_settings_name: 'My profile settings',
          member_finder_rating_search: true,
          settings_native_visible: true,
          settings_spoken_visible: true,
          header_backgroundImage: '/assets/images/app_meta/business_network.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Business networking',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          force_type: true,
          force_album: true,
          force_colored: true,
          force_image_background: true,
          force_poll: true,
          force_link: true,
          ratings_module: true,
          ratings_name: 'Ratings',
          profile_visitors_module: true,
          profile_visitors_name: 'Profile visitors',
          dashboard_name: 'Dashboard',
          store_locator: {
            products: {
              name: 'Office rent',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'business_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
          },
          business_store: {
            products: {
              name: 'Office service providers',
              meta: 'Interact and discover businesses.',
              search_reference: 'office_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
          },
          swap_module: false,
          donation_module: false,
          talk_experts: {
            module: true,
            name: 'Business consultants',
            meta_desc: 'Business consultants',
            ads_type: ['service_offered'],
            url: '/talk-experts',
          },
          workshops_courses: {
            module: true,
            name: 'Workshops / Courses',
            meta_desc: 'Workshops / Courses',
            ads_type: ['ad_digital_media', 'ad_digital_product', 'ad_booking'],
            url: '/workshops-courses',
          },
          business_videos: {
            module: true,
            name: 'Business TV',
            meta_desc: 'Business TV',
            ads_type: ['ad_digital_media'],
            url: '/business-videos',
          },
          products: {
            module: true,
            name: 'Products',
            meta_desc: 'Products',
            ads_type: ['ad_physical_product'],
            url: '/products',
          },
          digital: {
            module: true,
            name: 'Downloadable materials',
            meta_desc: 'Downloadable materials',
            ads_type: ['ad_digital_product'],
            url: '/digital',
          },
          jobs: {
            module: true,
            name: 'Jobs',
            meta_desc: 'Jobs',
            ads_type: ['ad_job'],
            url: '/jobs',
          },
          events: {
            module: true,
            name: 'Events',
            meta_desc: 'Events',
            ads_type: ['ad_booking'],
            url: '/events',
          },
          rent: {
            module: true,
            name: 'Business equipment rental',
            meta_desc: 'Business equipment rental',
            ads_type: ['ad_rent'],
            url: '/rent',
          },
        };
        break;
      }

      case 'doctor_finder':
      case 'medspot': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'doctor_finder',
          community_application_type: 'doctor_finder',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          catalog_module: false,
          members_module: false,
          show_on_profile: false,
          members_finder_name: 'Member finder',
          member_profile_settings_name: 'My profile settings',
          member_finder_language_search: true,
          member_finder_rating_search: false,
          settings_native_visible: true,
          settings_spoken_visible: true,
          settings_learn_visible: false,
          header_backgroundImage: '/assets/images/doctor_finder/doctor_finder_cover.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Doctor Finder',
          landing_load_loadframe: true,
          ratings_module: false,
          ratings_name: 'Ratings',
          profile_visitors_module: false,
          profile_visitors_name: 'Profile visitors',
          dashboard_name: 'Dashboard',
          talk_experts: {
            module: true,
            name: 'Doctors',
            meta_desc: 'Doctors',
            ads_type: ['service_offered'],
            url: '/talk-experts',
          },
        };
        break;
      }

      case 'beautywave':
      case 'wellness_beauty': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          profile_type: 'wellness_beauty',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          catalog_module: false,
          members_module: false,
          show_on_profile: false,
          members_finder_name: 'Member finder',
          member_profile_settings_name: 'My profile settings',
          member_finder_language_search: true,
          member_finder_rating_search: false,
          settings_native_visible: false,
          settings_spoken_visible: false,
          settings_learn_visible: false,
          header_backgroundImage: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: this.getAppConfig(app_link)?.app_brand_name,
          landing_load_loadframe: true,
          community_stream_module: false,
          share_available: true,
          ratings_module: false,
          profile_visitors_module: false,
          dashboard_name: 'Dashboard',
          talk_experts: {
            module: true,
            name: 'Talk to experts',
            meta_desc: 'Talk to experts',
            ads_type: ['service_offered'],
            url: '/talk-experts',
          },
          store_locator: {
            products: {
              name: 'Beauty prodcuts',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'beautry_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
          },
          business_store: {
            hairdresser: {
              name: 'Hairdresser finder',
              meta: 'Hairdresser finder',
              search_reference: 'hairdresser_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            beautician: {
              name: 'Beautician finder',
              meta: 'Beautician finder',
              search_reference: 'beautician_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            pedicurist: {
              name: 'Pedicurist finder',
              meta: 'Pedicurist finder',
              search_reference: 'pedicurist_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            manicurist: {
              name: 'Manicurist finder',
              meta: 'Manicurist finder',
              search_reference: 'manicurist_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            masseur: {
              name: 'Masseur finder',
              meta: 'Masseur finder',
              search_reference: 'masseur_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            lash_stylist: {
              name: 'Lash_stylist finder',
              meta: 'Lash_stylist finder',
              search_reference: 'lash_stylist_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
          },
          workshops_courses: {
            module: true,
            name: 'Workshops / Courses',
            meta_desc: 'Workshops / Courses',
            ads_type: ['ad_digital_media', 'ad_digital_product', 'ad_booking'],
            url: '/workshops-courses',
            browse_category_type: [
              'digital_media_template',
              'digital_product_template',
              'booking_template',
            ],
          },
          events: {
            module: true,
            name: 'Events',
            meta_desc: 'Events',
            ads_type: ['ad_booking'],
            url: '/events',
            browse_category_type: 'booking_template',
          },
          digital: {
            module: true,
            name: 'Guide/handy book',
            meta_desc: 'Guide/handy book',
            ads_type: ['ad_digital_product'],
            url: '/digital',
            browse_category_type: 'digital_product_template',
          },
        };
        break;
      }

      case 'pets':
      case 'petnest': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'pets',
          community_application_type: 'pets',
          catalog_type: 'animal',
          category_type: 'animal',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Animal catalog',
          catalog_details_name: 'Animal identifier',
          catalog_wishlist_name: 'Favorites',
          catalog_collection_name: 'My pets',
          catalog_create_title: 'Add a new animal to our database',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_finder_name: 'Member finder',
          members_module: true,
          show_on_profile: true,
          member_profile_settings_name: 'My profile settings',
          settings_native_visible: true,
          settings_spoken_visible: true,
          header_backgroundImage: '/assets/images/app_meta/pets.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          landing_header_title: 'For animal lovers',
          landing_load_loadframe: true,
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Profile visitors',
          dashboard_name: 'Dashboard',
          store_locator: {
            products: {
              name: 'Animal stores near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'pets_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            clinics: {
              name: 'Veterinarians near me',
              meta: 'Veterinary clinics',
              search_reference: 'pets_clinics_reference',
              url: '/veterinary-clinics',
              map_button_link: '/veterinary-hospitals',
            },
          },
          business_store: {
            products: {
              name: 'Animal related businesses',
              meta: 'Interact and discover businesses.',
              search_reference: 'pets_store_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            clinics: {
              name: 'Veterinarians',
              meta: 'Veterinary clinics',
              search_reference: 'pets_clinics_reference',
              url: '/veterinary-hospitals',
              create_link: '/veterinary-clinics',
            },
          },
          swap_module: true,
          swap_name: 'Swap',
          donation_module: true,
          donation_name: 'Animal adoption',
          post_type: 'adoption',
          talk_experts: {
            module: true,
            name: 'Talk to experts',
            meta_desc:
              'Access a wide range of expert tips and how-to guides to nurture your green space.',
            ads_type: ['service_offered'],
            url: '/talk-experts',
          },
          workshops_courses: {
            module: true,
            name: 'Workshops / Courses',
            meta_desc: 'Workshops / Courses',
            ads_type: ['ad_digital_media', 'ad_digital_product', 'ad_booking'],
            url: '/workshops-courses',
          },
          animal_videos: {
            module: true,
            name: 'Animal TV',
            meta_desc: 'Animal TV',
            ads_type: ['ad_digital_media'],
            url: '/animal-videos',
          },
          rent: {
            module: true,
            name: 'Rental of tools and machinery',
            meta_desc: 'Rental of tools and machinery',
            ads_type: ['ad_rent'],
            url: '/rent',
          },
          live_animals: {
            module: true,
            name: 'Animal purchase',
            meta_desc: 'Animal purchase',
            ads_type: ['ad_physical_product'],
            url: '/live-animals',
          },
          products: {
            module: true,
            name: 'Products',
            meta_desc: 'Products',
            ads_type: ['ad_physical_product'],
            url: '/products',
          },
          hire_expert: {
            module: true,
            name: 'Hire an expert',
            meta_desc: 'Hire an expert',
            ads_type: ['service_offered'],
            url: '/hire-expert',
          },
          digital: {
            module: true,
            name: 'Downloadable materials',
            meta_desc: 'Downloadable materials',
            ads_type: ['ad_digital_product'],
            url: '/digital',
          },
          diy_videos: {
            module: true,
            name: 'DIY TV',
            meta_desc: 'DIY TV',
            ads_type: ['ad_digital_media'],
            url: '/diy-videos',
          },
          jobs: {
            module: true,
            name: 'Jobs',
            meta_desc: 'Jobs',
            ads_type: ['ad_job'],
            url: '/jobs',
          },
          events: {
            module: true,
            name: 'Events',
            meta_desc: 'Events',
            ads_type: ['ad_booking'],
            url: '/events',
          },
          doctors: {
            module: true,
            name: 'Veterinarians',
            meta_desc: 'Veterinarians',
            ads_type: ['service_offered'],
            url: '/doctors',
          },
          pet_sitter: {
            module: true,
            name: 'Pet Sitter',
            meta_desc: 'Pet Sitter',
            ads_type: ['service_offered'],
            url: '/pet-sitter',
          },
        };
        break;
      }

      case 'cooking': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'food',
          community_application_type: 'cooking',
          catalog_type: 'food',
          category_type: 'food',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Recipes',
          catalog_details_name: 'Recipe',
          catalog_wishlist_name: 'Favorites',
          catalog_collection_name: 'My cookbook',
          catalog_create_title: 'Add a new recipe to our database',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Member finder',
          member_profile_settings_name: 'My profile settings',
          header_backgroundImage: '/assets/images/app_meta/cooking.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: '-',
          landing_load_loadframe: true,
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Profile visitors',
          dashboard_name: 'Dashboard',
          store_locator: {
            near_me_restaurants: {
              name: 'Restaurants near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'garden_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            near_me_supermarkets: {
              name: 'Supermarkets stores near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'garden_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            near_me_groceries: {
              name: 'Groceries stores near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'garden_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            near_me_greengrocer: {
              name: 'Greengrocers stores near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'garden_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            near_me_homegrown: {
              name: 'Home-grown stores near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'garden_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
          },
        };
        break;
      }

      case 'market-space':
      case 'market_space':
      case 'physical_product': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'market_space',
          members_module: true,
          show_on_profile: false,
          members_finder_name: 'Member finder',
          member_profile_settings_name: 'My profile',
          community_application_type: 'market_space',
          catalog_type: 'physical_product',
          category_type: 'physical_product_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Product catalog',
          catalog_wishlist_name: 'Favorites',
          catalog_collection_name: 'My stuff',
          catalog_details_name: 'Product identifier',
          catalog_create_title: 'Add a new product to our database',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          profile_visitors_module: true,
          profile_visitors_name: 'Profile visitors',
          community_stream_module: true,
          community_stream_name: 'Community stream',
          dashboard_name: 'Dashboard',
          header_backgroundImage: '/assets/images/app_meta/market_space.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: '-',
          landing_load_loadframe: true,
          ratings_module: false,
          swap_module: true,
          swap_name: 'Product swap',
          donation_module: true,
          donation_name: 'Free products',
          map_area: true,
          post_type: 'donate',
          products: {
            module: true,
            name: 'Product ads',
            meta_desc: 'Product ads',
            ads_type: ['physical_product'],
            url: '/products',
            browse_category_type: 'physical_product_template',
          },
          store_locator: {
            products: {
              name: 'Stores near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
          },
          business_store: {
            products: {
              name: 'Businesses',
              meta: 'Interact and discover businesses.',
              search_reference: 'business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
          },
          review_videos: {
            module: true,
            name: 'Product reviews TV',
            meta_desc: 'Product reviews TV',
            ads_type: ['digital_media'],
            url: '/review-videos',
            browse_category_type: 'digital_media_template',
          },
          events: {
            module: true,
            name: 'Events',
            meta_desc: 'Events',
            ads_type: ['booking'],
            url: '/events',
            browse_category_type: 'booking_template',
          },
          rent: {
            module: true,
            name: 'Rental of tools and machinery',
            meta_desc: 'Rental of tools and machinery',
            ads_type: ['rent'],
            url: '/rent',
            browse_category_type: 'rent_template',
          },
          workshops_courses: {
            module: true,
            name: 'Workshops / Courses',
            meta_desc: 'Workshops / Courses',
            ads_type: ['digital_media', 'digital_product', 'booking'],
            url: '/workshops-courses',
            browse_category_type: [
              'digital_media_template',
              'digital_product_template',
              'booking_template',
            ],
          },
          hire_expert: {
            module: true,
            name: 'Hire an expert',
            meta_desc: 'Hire an expert',
            ads_type: ['service_offered'],
            url: '/hire-expert',
            browse_category_type: 'service_offered_template',
          },
          digital: {
            module: true,
            name: 'Downloadable materials',
            meta_desc: 'Downloadable materials',
            ads_type: ['digital_product'],
            url: '/digital',
            browse_category_type: 'digital_product_template',
          },
          diy_videos: {
            module: true,
            name: 'DIY TV',
            meta_desc: 'DIY TV',
            ads_type: ['digital_media'],
            url: '/diy-videos',
            browse_category_type: 'digital_media_template',
          },
        };
        break;
      }

      case 'bucket-list':
      case 'bucket_list': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'bucket_list',
          community_application_type: 'bucket_list',
          catalog_type: 'bucket_list',
          category_type: 'bucket_list',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_details_members: true,
          catalog_details_name: 'Goal',
          catalog_wishlist_name: 'Favorites',
          catalog_collection_name: 'My bucket list',
          catalog_name: 'Bucket list ideas',
          catalog_create_title: 'Add a new idea to our database',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Member finder',
          member_profile_settings_name: 'My profile settings',
          settings_native_visible: true,
          settings_spoken_visible: true,
          header_backgroundImage: '/assets/images/app_meta/bucket_list.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Before I die...',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Profile visitors',
          dashboard_name: 'Dashboard',
          store_locator: {
            products: {
              name: 'Stores near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
          },
          business_store: {
            products: {
              name: 'Businesses',
              meta: 'Interact and discover businesses.',
              search_reference: 'business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
          },
          swap_module: false,
          donation_module: false,
          talk_experts: {
            module: true,
            name: 'Talk to experts',
            meta_desc: 'Talk to experts.',
            ads_type: ['service_offered'],
            url: '/talk-experts',
          },
          workshops_courses: {
            module: true,
            name: 'Workshops / Courses',
            meta_desc: 'Workshops / Courses',
            ads_type: ['ad_digital_media', 'ad_digital_product', 'ad_booking'],
            url: '/workshops-courses',
          },
          videos: {
            module: true,
            name: 'Inspirational videos',
            meta_desc: 'Inspirational videos',
            ads_type: ['ad_digital_media'],
            url: '/videos',
          },
          rent: {
            module: true,
            name: 'Rental of tools and machinery',
            meta_desc: 'Rental of tools and machinery',
            ads_type: ['ad_rent'],
            url: '/rent',
          },
          products: {
            module: true,
            name: 'Products',
            meta_desc: 'Products',
            ads_type: ['ad_physical_product'],
            url: '/products',
          },
          hire_expert: {
            module: true,
            name: 'Hire an expert',
            meta_desc: 'Hire an expert',
            ads_type: ['service_offered'],
            url: '/hire-expert',
          },
          digital: {
            module: true,
            name: 'Downloadable materials',
            meta_desc: 'Downloadable materials',
            ads_type: ['ad_digital_product'],
            url: '/digital',
          },
          events: {
            module: true,
            name: 'Events',
            meta_desc: 'Events',
            ads_type: ['ad_booking'],
            url: '/events',
          },
        };
        break;
      }

      case 'skill_link':
      case 'skill-link': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'skill_link',
          community_application_type: 'skill_link',
          catalog_type: 'service',
          category_type: 'skill_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Skill catalog',
          catalog_wishlist_name: 'Saved skills',
          catalog_collection_name: 'My skills',
          catalog_details_name: 'Skill details',
          catalog_create_title: 'Add a new skill to our database',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Skill finder',
          member_profile_settings_name: 'My skill profile',
          header_backgroundImage: '/assets/images/app_meta/skill_link.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Skill Link',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Profile visitors',
          dashboard_name: 'Skill Dashboard',
          share_available: true,
          talk_experts: {
            module: true,
            name: 'Talk to experts',
            meta_desc:
              'Access a wide range of expert tips and how-to guides to nurture your green space.',
            ads_type: ['service_offered'],
            url: '/talk-experts',
            browse_category_type: 'service_offered_template',
          },
          events: {
            module: true,
            name: 'Events',
            meta_desc: 'Events',
            ads_type: ['ad_booking'],
            url: '/events',
            browse_category_type: 'booking_template',
          },
          workshops_courses: {
            module: true,
            name: 'Workshops / Courses',
            meta_desc: 'Workshops / Courses',
            ads_type: ['ad_digital_media', 'ad_digital_product', 'ad_booking'],
            url: '/workshops-courses',
            browse_category_type: [
              'digital_media_template',
              'digital_product_template',
              'booking_template',
            ],
          },
          jobs: {
            module: true,
            name: 'Jobs',
            meta_desc: 'Jobs',
            ads_type: ['ad_job'],
            url: '/jobs',
            browse_category_type: 'job_template',
          },
        };
        break;
      }

      case 'be_green':
      case 'be-green': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'be_green',
          community_application_type: 'be_green',
          catalog_type: 'physical_product',
          category_type: 'physical_product_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Catalog',
          catalog_wishlist_name: 'Favorites',
          catalog_collection_name: 'My collection',
          catalog_details_name: 'Product details',
          catalog_create_title: 'Add a new product',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Eco finder',
          member_profile_settings_name: 'My eco profile',
          header_backgroundImage: '/assets/images/app_meta/be_green.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Be Green',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Eco visitors',
          dashboard_name: 'Eco Dashboard',
          share_available: true,
          talk_experts: {
            module: true,
            name: 'Talk to experts',
            meta_desc:
              'Access a wide range of expert tips and how-to guides to nurture your green space.',
            ads_type: ['service_offered'],
            url: '/talk-experts',
            browse_category_type: 'service_offered_template',
          },
          workshops_courses: {
            module: true,
            name: 'Workshops / Courses',
            meta_desc: 'Workshops / Courses',
            ads_type: ['ad_digital_media', 'ad_digital_product', 'ad_booking'],
            url: '/workshops-courses',
            browse_category_type: [
              'digital_media_template',
              'digital_product_template',
              'booking_template',
            ],
          },
          eco_videos: {
            module: true,
            name: 'Green TV',
            meta_desc: 'Green TV',
            ads_type: ['ad_digital_media'],
            url: '/eco-videos',
            browse_category_type: 'digital_media_template',
          },
          diy_videos: {
            module: true,
            name: 'DIY TV',
            meta_desc: 'DIY TV',
            ads_type: ['ad_digital_media'],
            url: '/diy-videos',
            browse_category_type: 'digital_media_template',
          },
          events: {
            module: true,
            name: 'Events',
            meta_desc: 'Events',
            ads_type: ['ad_booking'],
            url: '/events',
            browse_category_type: 'booking_template',
          },
          digital: {
            module: true,
            name: 'Downloadable materials',
            meta_desc: 'Downloadable materials',
            ads_type: ['ad_digital_product'],
            url: '/digital',
            browse_category_type: 'digital_product_template',
          },
          jobs: {
            module: true,
            name: 'Jobs',
            meta_desc: 'Jobs',
            ads_type: ['ad_job'],
            url: '/jobs',
            browse_category_type: 'job_template',
          },
        };
        break;
      }

      case 'second-chance':
      case 'second_chance': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'second_chance',
          community_application_type: 'second_chance',
          catalog_type: 'used_product',
          category_type: 'used_product_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Second-hand catalog',
          catalog_wishlist_name: 'Saved items',
          catalog_collection_name: 'My second-hand stuff',
          catalog_details_name: 'Product details',
          catalog_create_title: 'Add a second-hand product',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Second-hand finder',
          member_profile_settings_name: 'My second-hand profile',
          header_backgroundImage: '/assets/images/app_meta/second_chance.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Second Chance',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Second-hand visitors',
          dashboard_name: 'Second-hand Dashboard',
          share_available: true,
          swap_module: true,
          swap_name: 'Swap',
          donation_module: true,
          donation_name: 'Free stuff',
          post_type: 'donate',
          events: {
            module: true,
            name: 'Events',
            meta_desc: 'Events',
            ads_type: ['ad_booking'],
            url: '/events',
            browse_category_type: 'booking_template',
          },
          business_store: {
            animal_shelter: {
              name: 'Animal shelter',
              meta: 'Animal shelter',
              search_reference: 'animal_shelter_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            foundation: {
              name: 'Foundation finder',
              meta: 'Foundation finder',
              search_reference: 'foundation_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            animal_rescuer: {
              name: 'Animal rescuer',
              meta: 'Animal rescuer',
              search_reference: 'animal_rescuer_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
          },
          store_locator: {
            animal_food: {
              name: 'Animal food',
              meta: 'Animal food',
              search_reference: 'animal_food_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
          },
        };
        break;
      }

      case 'eventix':
      case 'events': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'events',
          community_application_type: 'eventix',
          catalog_type: 'event',
          category_type: 'event_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Event catalog',
          catalog_wishlist_name: 'Saved events',
          catalog_collection_name: 'My events',
          catalog_details_name: 'Event details',
          catalog_create_title: 'Add a new event',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Event finder',
          member_profile_settings_name: 'My event profile',
          header_backgroundImage: '/assets/images/app_meta/eventix.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Eventix',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Event visitors',
          dashboard_name: 'Event Dashboard',
          share_available: true,
          business_store: {
            theatre: {
              name: 'Theatre',
              meta: 'Theatre',
              search_reference: 'theatre_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            music: {
              name: 'Music',
              meta: 'Music',
              search_reference: 'music_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            conference: {
              name: 'Conference',
              meta: 'Conference',
              search_reference: 'conference_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            festivals: {
              name: 'Festivals',
              meta: 'Festivals',
              search_reference: 'festivals_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            sports: {
              name: 'Sports',
              meta: 'Sports',
              search_reference: 'sports_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            exhibition: {
              name: 'Exhibition',
              meta: 'Exhibition',
              search_reference: 'exhibition_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            cinema: {
              name: 'Cinema',
              meta: 'Cinema',
              search_reference: 'cinema_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            circus: {
              name: 'Circus',
              meta: 'Circus',
              search_reference: 'circus_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            comedy: {
              name: 'Comedy Performance',
              meta: 'Comedy Performance',
              search_reference: 'comedy_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            charity: {
              name: 'Charity',
              meta: 'Charity',
              search_reference: 'charity_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
          },
          events: {
            module: true,
            name: 'Seasonal Events',
            meta_desc: 'Events',
            ads_type: ['ad_booking'],
            url: '/events',
            browse_category_type: 'booking_template',
          },
        };
        break;
      }

      case 'rentspace':
      case 'renting': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'renting',
          community_application_type: 'rentspace',
          catalog_type: 'rental_space',
          category_type: 'rental_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Rental catalog',
          catalog_wishlist_name: 'Saved rentals',
          catalog_collection_name: 'My rentals',
          catalog_details_name: 'Rental details',
          catalog_create_title: 'Add a new rental space',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Rental finder',
          member_profile_settings_name: 'My rental profile',
          header_backgroundImage: '/assets/images/app_meta/rentspace.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Rentspace',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Rental visitors',
          dashboard_name: 'Rental Dashboard',
          share_available: true,
          store_locator: {
            household_products: {
              name: 'Household prodcuts',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'household_products_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            health_products: {
              name: 'Health prodcuts',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'health_products_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            travel_products: {
              name: 'Travel prodcuts',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'travel_products_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            hobby_products: {
              name: 'Hobby prodcuts',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'hobby_products_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            construction_tools: {
              name: 'Construction tools',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'construction_tools_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            garden_tools: {
              name: 'Garden Tools',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'garden_tools_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            vehicles_agriculture: {
              name: 'Agriculture',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'vehicles_agriculture_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            vehicles_construction: {
              name: 'Construction',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'vehicles_construction_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            vehicles_cars: {
              name: 'Cars',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'vehicles_cars_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            vehicles_boats: {
              name: 'Boats',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'vehicles_boats_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            vehicles_aircrafts: {
              name: 'Aircrafts',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'vehicles_aircrafts_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            vehicles_bicycle: {
              name: 'Bycicle',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'vehicles_bicycle_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            vehicles_roller: {
              name: 'Roller',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'vehicles_roller_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            vehicles_motorcycle: {
              name: 'Motorcycle',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'vehicles_motorcycle_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            vehicles_trailer: {
              name: 'Trailer',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'vehicles_trailer_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            real_estate_flats: {
              name: 'Flats',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'real_estate_flats_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            real_estate_houses: {
              name: 'Houses',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'real_estate_houses_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            real_estate_garages: {
              name: 'Garages',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'real_estate_garages_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            real_estate_storages: {
              name: 'Storages',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'real_estate_storages_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            vacation_rooms: {
              name: 'Rooms',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'real_estate_storages_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            vacation_flats: {
              name: 'Flats',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'real_estate_storages_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            vacation_houses: {
              name: 'Houses',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'real_estate_storages_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
          },
        };
        break;
      }

      case 'scheduly':
      case 'booking': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'booking',
          community_application_type: 'scheduly',
          catalog_type: 'booking',
          category_type: 'booking_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Booking catalog',
          catalog_wishlist_name: 'Saved bookings',
          catalog_collection_name: 'My bookings',
          catalog_details_name: 'Booking details',
          catalog_create_title: 'Add a new booking',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Booking finder',
          member_profile_settings_name: 'My booking profile',
          header_backgroundImage: '/assets/images/app_meta/scheduly.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Scheduly',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Booking visitors',
          dashboard_name: 'Booking Dashboard',
          share_available: true,
          booking: {
            travel_accomodation: {
              name: 'Travel accomodation',
              meta: 'Interact and discover businesses.',
              search_reference: 'travel_accomodation_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            hotels: {
              name: 'Hotels',
              meta: 'Interact and discover businesses.',
              search_reference: 'hotels_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            room_rental: {
              name: 'Room rental',
              meta: 'Interact and discover businesses.',
              search_reference: 'room_rental_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            doctor_appointment: {
              name: 'Doctor appointment',
              meta: 'Interact and discover businesses.',
              search_reference: 'doctor_appointment_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            car_service: {
              name: 'Car service',
              meta: 'Interact and discover businesses.',
              search_reference: 'car_service_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            restaurants: {
              name: 'Restaurants',
              meta: 'Interact and discover businesses.',
              search_reference: 'restaurants_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            entertainment: {
              name: 'Entertainment',
              meta: 'Interact and discover businesses.',
              search_reference: 'entertainment_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
            private_tutor: {
              name: 'Private tutor',
              meta: 'Interact and discover businesses.',
              search_reference: 'private_tutor_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
          },
        };
        break;
      }

      case 'careerpath':
      case 'job': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'job',
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: false,
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: false,
          members_finder_name: 'Member finder',
          member_profile_settings_name: 'My profile',
          header_backgroundImage: '/assets/images/app_meta/careerpath.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Careerpath',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: true,
          profile_visitors_module: true,
          profile_visitors_name: 'Profile visitors',
          dashboard_name: 'Job Dashboard',
          share_available: true,
          jobs: {
            module: true,
            name: 'Jobs',
            meta_desc: 'Jobs',
            ads_type: ['job'],
            url: '/jobs',
            browse_category_type: 'job_template',
          },
          talk_experts: {
            module: true,
            name: 'Career advisers',
            meta_desc: 'Career advisers',
            ads_type: ['service_offered'],
            url: '/talk-experts',
          },
          workshops_courses: {
            module: true,
            name: 'Workshops / Courses',
            meta_desc: 'Workshops / Courses',
            ads_type: ['digital_media', 'digital_product', 'booking'],
            url: '/workshops-courses',
            browse_category_type: [
              'digital_media_template',
              'digital_product_template',
              'booking_template',
            ],
          },
          job_videos: {
            module: true,
            name: 'Career TV',
            meta_desc: 'Career TV',
            ads_type: ['digital_media'],
            url: '/career-videos',
            browse_category_type: 'digital_media_template',
          },
          events: {
            module: true,
            name: 'Events',
            meta_desc: 'Events',
            ads_type: ['booking'],
            url: '/events',
            browse_category_type: 'booking_template',
          },
          store_locator: {
            jobs: {
              name: 'Stores near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'hunters_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
          },
          business_store: {
            jobs: {
              name: 'Businesses',
              meta: 'Interact and discover businesses.',
              search_reference: 'hunters_business_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
          },
        };
        break;
      }

      case 'pixelmarket':
      case 'digitalproducts':
      case 'digital_downloads':
      case 'digital_product': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'digital_downloads',
          community_application_type: 'digital_downloads',
          catalog_type: 'digital_product',
          category_type: 'digital_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: false,
          show_on_profile: false,
          header_backgroundImage: '/assets/images/app_meta/pixelmarket.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Pixelmarket',
          landing_load_loadframe: true,
          community_stream_module: false,
          ratings_module: false,
          profile_visitors_module: false,
          share_available: true,
          downloads: {
            module: true,
            name: 'Downloadable materials',
            meta_desc: 'Downloadable materials',
            ads_type: ['digital_product'],
            url: '/downloads',
            browse_category_type: 'digital_product_template',
          },

          business_store: {
            products: {
              name: 'Digital businesses',
              meta: 'Interact and discover businesses.',
              search_reference: 'digitaln_business_reference',
              url: '/businesses-stores',
            },
          },
        };
        break;
      }

      case 'taskly': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'taskly',
          community_application_type: 'taskly',
          catalog_type: 'task',
          category_type: 'task_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Task catalog',
          catalog_wishlist_name: 'Saved tasks',
          catalog_collection_name: 'My tasks',
          catalog_details_name: 'Task details',
          catalog_create_title: 'Add a new task',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Task finder',
          member_profile_settings_name: 'My task profile',
          header_backgroundImage: '/assets/images/app_meta/taskly.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Taskly',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Task visitors',
          dashboard_name: 'Task Dashboard',
          share_available: true,
          business_store: {
            services_catalog: {
              name: 'Service Catalog',
              meta: 'Interact and discover businesses.',
              search_reference: 'services_catalog_reference',
              url: '/businesses-stores',
              create_link: '/store-locator',
            },
          },
        };
        break;
      }

      case 'travel-buddies':
      case 'travel_buddies': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'travel_buddies',
          community_application_type: 'travel_buddies',
          catalog_type: 'travel',
          category_type: 'travel_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Travel catalog',
          catalog_wishlist_name: 'Saved trips',
          catalog_collection_name: 'My trips',
          catalog_details_name: 'Trip details',
          catalog_create_title: 'Add a new trip',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Travel finder',
          member_profile_settings_name: 'My travel profile',
          header_backgroundImage: '/assets/images/app_meta/travel_buddies.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Travel Buddies',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Travel visitors',
          dashboard_name: 'Travel Dashboard',
          share_available: true,
          events: {
            module: true,
            name: 'Events',
            meta_desc: 'Events',
            ads_type: ['booking'],
            url: '/events',
            browse_category_type: 'booking_template',
          },
        };
        break;
      }

      case 'collectors': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'collectors',
          community_application_type: 'collectors',
          catalog_type: 'collectible',
          category_type: 'collectible_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Collectible catalog',
          catalog_wishlist_name: 'Saved collectibles',
          catalog_collection_name: 'My collectibles',
          catalog_details_name: 'Collectible details',
          catalog_create_title: 'Add a new collectible',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Collectible finder',
          member_profile_settings_name: 'My collectible profile',
          header_backgroundImage: '/assets/images/app_meta/collectors.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Collectors',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Collectible visitors',
          dashboard_name: 'Collectible Dashboard',
          share_available: true,
          store_locator: {
            hobby_shops: {
              name: 'Hobby shops near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'hobby_shops_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            antiquarian_bookshop: {
              name: 'Antiquarian bookshop near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'antiquarian_bookshop_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            antique_furniture_store: {
              name: 'Antique furniture store',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'antique_furniture_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            retro_toyshop: {
              name: 'Retro toy shop near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'retro_toyshop_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            comic_bookstore: {
              name: 'Comic bookstore near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'comic_bookstore_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
          },
        };
        break;
      }

      case 'real-estate':
      case 'real_estate': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'real_estate',
          community_application_type: 'real_estate',
          catalog_type: 'property',
          category_type: 'property_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Property catalog',
          catalog_wishlist_name: 'Saved properties',
          catalog_collection_name: 'My properties',
          catalog_details_name: 'Property details',
          catalog_create_title: 'Add a new property',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Property finder',
          member_profile_settings_name: 'My property profile',
          header_backgroundImage: '/assets/images/app_meta/real_estate.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Real Estate',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Property visitors',
          dashboard_name: 'Property Dashboard',
          share_available: true,
        };
        break;
      }

      case 'boat':
      case 'boats-yachts': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'boat',
          community_application_type: 'boat',
          catalog_type: 'boat',
          category_type: 'boat_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Boat catalog',
          catalog_wishlist_name: 'Saved boats',
          catalog_collection_name: 'My boats',
          catalog_details_name: 'Boat details',
          catalog_create_title: 'Add a new boat',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Boat finder',
          member_profile_settings_name: 'My boat profile',
          header_backgroundImage: '/assets/images/app_meta/boats_yachts.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Boats & Yachts',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Boat visitors',
          dashboard_name: 'Boat Dashboard',
          share_available: true,
          store_locator: {
            boats_new: {
              name: 'New Boats',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'boats_new_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            boats_for_sale: {
              name: 'Boats for sale',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'boats_for_sale_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            boats_for_rent: {
              name: 'Boats for rent',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'boats_for_rent_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            boat_shops: {
              name: 'Boat shops',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'boat_shops_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            sailing_apparel_stores: {
              name: 'Sailing apparel stores',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'sailing_apparel_stores_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            boat_trips: {
              name: 'Boat trips',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'boat_trips_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },

            sailing_courses: {
              name: 'Sailing courses',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'sailing_courses_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            sailing_races: {
              name: 'Sailing races',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'sailing_races_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
          },
          diy_videos: {
            module: true,
            name: 'DIY TV',
            meta_desc: 'DIY TV',
            ads_type: ['ad_digital_media'],
            url: '/diy-videos',
            browse_category_type: 'digital_media_template',
          },
          events: {
            module: true,
            name: 'Events',
            meta_desc: 'Events',
            ads_type: ['ad_booking'],
            url: '/events',
            browse_category_type: 'booking_template',
          },
          digital: {
            module: true,
            name: 'Downloadable materials',
            meta_desc: 'Downloadable materials',
            ads_type: ['ad_digital_product'],
            url: '/digital',
            browse_category_type: 'digital_product_template',
          },
        };
        break;
      }

      case 'cars-wheels':
      case 'cars': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'cars',
          community_application_type: 'cars',
          catalog_type: 'car',
          category_type: 'car_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Car catalog',
          catalog_wishlist_name: 'Saved cars',
          catalog_collection_name: 'My cars',
          catalog_details_name: 'Car details',
          catalog_create_title: 'Add a new car',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Car finder',
          member_profile_settings_name: 'My car profile',
          header_backgroundImage: '/assets/images/app_meta/cars.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Cars & Wheels',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Car visitors',
          dashboard_name: 'Car Dashboard',
          share_available: true,
          store_locator: {
            car_new_shop: {
              name: 'Buy cars',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'garden_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            car_rent: {
              name: 'Cars for rent',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'garden_store_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
          },
        };
        break;
      }

      case 'fitness': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'fitness',
          community_application_type: 'fitness',
          catalog_type: 'workout',
          category_type: 'workout_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Workout catalog',
          catalog_wishlist_name: 'Saved workouts',
          catalog_collection_name: 'My workouts',
          catalog_details_name: 'Workout details',
          catalog_create_title: 'Add a new workout',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Fitness finder',
          member_profile_settings_name: 'My fitness profile',
          header_backgroundImage: '/assets/images/app_meta/fitness.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Fitness',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Fitness visitors',
          dashboard_name: 'Fitness Dashboard',
          share_available: true,
          talk_experts: {
            module: true,
            name: 'Talk to experts',
            meta_desc:
              'Access a wide range of expert tips and how-to guides to nurture your green space.',
            ads_type: ['service_offered'],
            url: '/talk-experts',
            browse_category_type: 'service_offered_template',
          },
          workshops_courses: {
            module: true,
            name: 'Workshops / Courses',
            meta_desc: 'Workshops / Courses',
            ads_type: ['ad_digital_media', 'ad_digital_product', 'ad_booking'],
            url: '/workshops-courses',
            browse_category_type: [
              'digital_media_template',
              'digital_product_template',
              'booking_template',
            ],
          },
          fitness_videos: {
            module: true,
            name: 'Garden TV',
            meta_desc: 'Garden TV',
            ads_type: ['ad_digital_media'],
            url: '/garden-videos',
            browse_category_type: 'digital_media_template',
          },
          business_store: {
            sport_equipment: {
              name: 'Sport equipment',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'sport_equipment_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            sport_apparel: {
              name: 'Sport apparel',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'sport_apparel_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            nutritional_supplements: {
              name: 'Nutritional supplements',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'nutritional_supplements_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            zero_waste: {
              name: 'Zero waste',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'zero_waste_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
          },
          store_locator: {
            near_me_gym: {
              name: 'Gym near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'near_me_gym_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            near_me_fitness_rooms: {
              name: 'Fitness rooms near me',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'near_me_fitness_rooms_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            near_me_personal_trainer: {
              name: 'Personal trainer finder',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'near_me_personal_trainer_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
            near_me_group_trainings: {
              name: 'Group trainings',
              meta: 'Find businesses and stores near your location.',
              search_reference: 'near_me_group_trainings_reference',
              url: '/store-locator',
              map_button_link: '/businesses-stores',
            },
          },
          jobs: {
            module: true,
            name: 'Jobs',
            meta_desc: 'Jobs',
            ads_type: ['ad_job'],
            url: '/jobs',
            browse_category_type: 'job_template',
          },
          events: {
            module: true,
            name: 'Events',
            meta_desc: 'Events',
            ads_type: ['ad_booking'],
            url: '/events',
            browse_category_type: 'booking_template',
          },
        };
        break;
      }

      case 'volunteer': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'volunteer',
          community_application_type: 'volunteer',
          catalog_type: 'volunteer_opportunity',
          category_type: 'volunteer_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Volunteer catalog',
          catalog_wishlist_name: 'Saved opportunities',
          catalog_collection_name: 'My opportunities',
          catalog_details_name: 'Opportunity details',
          catalog_create_title: 'Add a new opportunity',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Volunteer finder',
          member_profile_settings_name: 'My volunteer profile',
          header_backgroundImage: '/assets/images/app_meta/volunteer.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Volunteer',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Volunteer visitors',
          dashboard_name: 'Volunteer Dashboard',
          share_available: true,
        };
        break;
      }

      case 'agriculture-livestock':
      case 'agriculture_livestock': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'agriculture_livestock',
          community_application_type: 'agriculture_livestock',
          catalog_type: 'agriculture_product',
          category_type: 'agriculture_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Agriculture catalog',
          catalog_wishlist_name: 'Saved products',
          catalog_collection_name: 'My products',
          catalog_details_name: 'Product details',
          catalog_create_title: 'Add a new product',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Agriculture finder',
          member_profile_settings_name: 'My agriculture profile',
          header_backgroundImage: '/assets/images/app_meta/agriculture_livestock.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Agriculture & Livestock',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Agriculture visitors',
          dashboard_name: 'Agriculture Dashboard',
          share_available: true,
        };
        break;
      }

      case 'office-meeting':
      case 'coworking_space': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'coworking_space',
          community_application_type: 'coworking_space',
          catalog_type: 'workspace',
          category_type: 'workspace_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Workspace catalog',
          catalog_wishlist_name: 'Saved workspaces',
          catalog_collection_name: 'My workspaces',
          catalog_details_name: 'Workspace details',
          catalog_create_title: 'Add a new workspace',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Workspace finder',
          member_profile_settings_name: 'My workspace profile',
          header_backgroundImage: '/assets/images/app_meta/coworking_space.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Coworking Space',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Workspace visitors',
          dashboard_name: 'Workspace Dashboard',
          share_available: true,
        };
        break;
      }

      case 'kids':
      case 'kids_space': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'kids_space',
          community_application_type: 'kids_space',
          catalog_type: 'kids_product',
          category_type: 'kids_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Kids catalog',
          catalog_wishlist_name: 'Saved kids products',
          catalog_collection_name: 'My kids products',
          catalog_details_name: 'Kids product details',
          catalog_create_title: 'Add a new kids product',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Kids finder',
          member_profile_settings_name: 'My kids profile',
          header_backgroundImage: '/assets/images/app_meta/kids_space.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Kids Space',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Kids visitors',
          dashboard_name: 'Kids Dashboard',
          share_available: true,
        };
        break;
      }

      case 'one-love':
      case 'dating': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'dating',
          community_application_type: 'dating',
          catalog_type: 'date',
          category_type: 'date_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Date catalog',
          catalog_wishlist_name: 'Saved dates',
          catalog_collection_name: 'My dates',
          catalog_details_name: 'Date details',
          catalog_create_title: 'Add a new date',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Date finder',
          member_profile_settings_name: 'My date profile',
          header_backgroundImage: '/assets/images/app_meta/dating.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Dating',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Date visitors',
          dashboard_name: 'Date Dashboard',
          share_available: true,
        };
        break;
      }

      case 'sport':
      case 'team-up': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'sport',
          community_application_type: 'sport',
          catalog_type: 'sport_event',
          category_type: 'sport_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Sport catalog',
          catalog_wishlist_name: 'Saved events',
          catalog_collection_name: 'My events',
          catalog_details_name: 'Event details',
          catalog_create_title: 'Add a new event',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Sport finder',
          member_profile_settings_name: 'My sport profile',
          header_backgroundImage: '/assets/images/app_meta/team_up.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Team Up',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Sport visitors',
          dashboard_name: 'Sport Dashboard',
          share_available: true,
        };
        break;
      }

      case 'mindset': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'mindset',
          community_application_type: 'mindset',
          catalog_type: 'mindset_session',
          category_type: 'mindset_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Mindset catalog',
          catalog_wishlist_name: 'Saved sessions',
          catalog_collection_name: 'My sessions',
          catalog_details_name: 'Session details',
          catalog_create_title: 'Add a new session',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Mindset finder',
          member_profile_settings_name: 'My mindset profile',
          header_backgroundImage: '/assets/images/app_meta/mindset.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Mindset',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Mindset visitors',
          dashboard_name: 'Mindset Dashboard',
          share_available: true,
        };
        break;
      }

      case 'grocery': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'grocery',
          community_application_type: 'grocery',
          catalog_type: 'grocery_product',
          category_type: 'grocery_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Grocery catalog',
          catalog_wishlist_name: 'Saved groceries',
          catalog_collection_name: 'My groceries',
          catalog_details_name: 'Grocery details',
          catalog_create_title: 'Add a new grocery',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Grocery finder',
          member_profile_settings_name: 'My grocery profile',
          header_backgroundImage: '/assets/images/app_meta/grocery.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Grocery',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Grocery visitors',
          dashboard_name: 'Grocery Dashboard',
          share_available: true,
        };
        break;
      }

      case 'diy-handmade':
      case 'diy_handmade': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'diy_handmade',
          community_application_type: 'diy_handmade',
          catalog_type: 'diy_product',
          category_type: 'diy_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'DIY catalog',
          catalog_wishlist_name: 'Saved DIYs',
          catalog_collection_name: 'My DIYs',
          catalog_details_name: 'DIY details',
          catalog_create_title: 'Add a new DIY',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'DIY finder',
          member_profile_settings_name: 'My DIY profile',
          header_backgroundImage: '/assets/images/app_meta/diy_handmade.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'DIY Handmade',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'DIY visitors',
          dashboard_name: 'DIY Dashboard',
          share_available: true,
        };
        break;
      }

      case 'fashion': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'fashion',
          community_application_type: 'fashion',
          catalog_type: 'fashion_product',
          category_type: 'fashion_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Fashion catalog',
          catalog_wishlist_name: 'Saved fashion items',
          catalog_collection_name: 'My fashion items',
          catalog_details_name: 'Fashion details',
          catalog_create_title: 'Add a new fashion item',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Fashion finder',
          member_profile_settings_name: 'My fashion profile',
          header_backgroundImage: '/assets/images/app_meta/fashion.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Fashion',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Fashion visitors',
          dashboard_name: 'Fashion Dashboard',
          share_available: true,
        };
        break;
      }

      case 'tech': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'tech',
          community_application_type: 'tech',
          catalog_type: 'tech_product',
          category_type: 'tech_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Tech catalog',
          catalog_wishlist_name: 'Saved tech items',
          catalog_collection_name: 'My tech items',
          catalog_details_name: 'Tech details',
          catalog_create_title: 'Add a new tech item',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Tech finder',
          member_profile_settings_name: 'My tech profile',
          header_backgroundImage: '/assets/images/app_meta/tech.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Tech',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Tech visitors',
          dashboard_name: 'Tech Dashboard',
          share_available: true,
        };
        break;
      }

      case 'crypto-blockchain':
      case 'crypto_blockchain': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'crypto_blockchain',
          community_application_type: 'crypto_blockchain',
          catalog_type: 'crypto_product',
          category_type: 'crypto_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Crypto catalog',
          catalog_wishlist_name: 'Saved crypto items',
          catalog_collection_name: 'My crypto items',
          catalog_details_name: 'Crypto details',
          catalog_create_title: 'Add a new crypto item',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Crypto finder',
          member_profile_settings_name: 'My crypto profile',
          header_backgroundImage: '/assets/images/app_meta/crypto_blockchain.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Crypto & Blockchain',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Crypto visitors',
          dashboard_name: 'Crypto Dashboard',
          share_available: true,
        };
        break;
      }

      case 'b-to-b':
      case 'b_to_b': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'b_to_b',
          community_application_type: 'b_to_b',
          catalog_type: 'business_product',
          category_type: 'business_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Business catalog',
          catalog_wishlist_name: 'Saved business items',
          catalog_collection_name: 'My business items',
          catalog_details_name: 'Business details',
          catalog_create_title: 'Add a new business item',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Business finder',
          member_profile_settings_name: 'My business profile',
          header_backgroundImage: '/assets/images/app_meta/b_to_b.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'B2B',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Business visitors',
          dashboard_name: 'Business Dashboard',
          share_available: true,
        };
        break;
      }

      case 'home-design':
      case 'home_design': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'home_design',
          community_application_type: 'home_design',
          catalog_type: 'home_product',
          category_type: 'home_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Home catalog',
          catalog_wishlist_name: 'Saved home items',
          catalog_collection_name: 'My home items',
          catalog_details_name: 'Home details',
          catalog_create_title: 'Add a new home item',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Home finder',
          member_profile_settings_name: 'My home profile',
          header_backgroundImage: '/assets/images/app_meta/home_design.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Home Design',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Home visitors',
          dashboard_name: 'Home Dashboard',
          share_available: true,
        };
        break;
      }

      case 'media-channels':
      case 'media_channels': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'media_channels',
          community_application_type: 'media_channels',
          catalog_type: 'media_content',
          category_type: 'media_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Media catalog',
          catalog_wishlist_name: 'Saved media',
          catalog_collection_name: 'My media',
          catalog_details_name: 'Media details',
          catalog_create_title: 'Add new media',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Media finder',
          member_profile_settings_name: 'My media profile',
          header_backgroundImage: '/assets/images/app_meta/media_channels.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Media Channels',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Media visitors',
          dashboard_name: 'Media Dashboard',
          share_available: true,
        };
        break;
      }

      case 'digital-nomads':
      case 'digital_nomads': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'digital_nomads',
          community_application_type: 'digital_nomads',
          catalog_type: 'nomad_product',
          category_type: 'nomad_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Nomad catalog',
          catalog_wishlist_name: 'Saved nomad items',
          catalog_collection_name: 'My nomad items',
          catalog_details_name: 'Nomad details',
          catalog_create_title: 'Add a new nomad item',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Nomad finder',
          member_profile_settings_name: 'My nomad profile',
          header_backgroundImage: '/assets/images/app_meta/digital_nomads.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Digital Nomads',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Nomad visitors',
          dashboard_name: 'Nomad Dashboard',
          share_available: true,
        };
        break;
      }

      case 'gaming': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'gaming',
          community_application_type: 'gaming',
          catalog_type: 'game',
          category_type: 'game_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Game catalog',
          catalog_wishlist_name: 'Saved games',
          catalog_collection_name: 'My games',
          catalog_details_name: 'Game details',
          catalog_create_title: 'Add a new game',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Game finder',
          member_profile_settings_name: 'My game profile',
          header_backgroundImage: '/assets/images/app_meta/gaming.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Gaming',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Game visitors',
          dashboard_name: 'Game Dashboard',
          share_available: true,
        };
        break;
      }

      case 'erotic': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'erotic',
          community_application_type: 'erotic',
          catalog_type: 'erotic_content',
          category_type: 'erotic_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Erotic catalog',
          catalog_wishlist_name: 'Saved erotic content',
          catalog_collection_name: 'My erotic content',
          catalog_details_name: 'Erotic details',
          catalog_create_title: 'Add new erotic content',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Erotic finder',
          member_profile_settings_name: 'My erotic profile',
          header_backgroundImage: '/assets/images/app_meta/erotic.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Erotic',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Erotic visitors',
          dashboard_name: 'Erotic Dashboard',
          share_available: true,
        };
        break;
      }

      case 'roommate': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'roommate',
          community_application_type: 'roommate',
          catalog_type: 'roommate_listing',
          category_type: 'roommate_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Roommate catalog',
          catalog_wishlist_name: 'Saved listings',
          catalog_collection_name: 'My listings',
          catalog_details_name: 'Listing details',
          catalog_create_title: 'Add a new listing',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Roommate finder',
          member_profile_settings_name: 'My roommate profile',
          header_backgroundImage: '/assets/images/app_meta/roommate.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Roommate',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Roommate visitors',
          dashboard_name: 'Roommate Dashboard',
          share_available: true,
        };
        break;
      }

      case 'aviation': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'aviation',
          community_application_type: 'aviation',
          catalog_type: 'aviation_product',
          category_type: 'aviation_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Aviation catalog',
          catalog_wishlist_name: 'Saved aviation items',
          catalog_collection_name: 'My aviation items',
          catalog_details_name: 'Aviation details',
          catalog_create_title: 'Add a new aviation item',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Member finder',
          member_profile_settings_name: 'My aviation profile',
          header_backgroundImage: '/assets/images/app_meta/aviation.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Aviation',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Aviation visitors',
          dashboard_name: 'Aviation Dashboard',
          share_available: true,
        };
        break;
      }

      case 'taxi': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'taxi',
          community_application_type: 'taxi',
          catalog_type: 'taxi_service',
          category_type: 'taxi_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Taxi catalog',
          catalog_wishlist_name: 'Saved taxi services',
          catalog_collection_name: 'My taxi services',
          catalog_details_name: 'Taxi details',
          catalog_create_title: 'Add a new taxi service',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Member finder',
          member_profile_settings_name: 'My taxi profile',
          header_backgroundImage: '/assets/images/app_meta/taxi.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Taxi',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Taxi visitors',
          dashboard_name: 'Taxi Dashboard',
          share_available: true,
        };
        break;
      }

      case 'goal_manager': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          profile_type: 'goal_manager',
          community_application_type: 'goal_manager',
          catalog_type: 'goal',
          category_type: 'goal_template',
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          catalog_module: true,
          catalog_name: 'Goal catalog',
          catalog_wishlist_name: 'Saved goals',
          catalog_collection_name: 'My goals',
          catalog_details_name: 'Goal details',
          catalog_create_title: 'Add a new goal',
          offer_name: 'Offers / Discounts / Coupons',
          offer_meta: 'Exclusive offers for members only!',
          members_module: true,
          show_on_profile: true,
          members_finder_name: 'Member finder',
          member_profile_settings_name: 'My goal profile',
          header_backgroundImage: '/assets/images/app_meta/goal_manager.jpg',
          meta_img_url: this.getAppConfig(app_link)?.app_brand_img_url,
          meta_desc: this.getAppConfig(app_link)?.app_brand_short_desc,
          landing_header_title: 'Goal Manager',
          landing_load_loadframe: true,
          community_stream_module: true,
          community_stream_name: 'Community stream',
          ratings_module: false,
          profile_visitors_module: true,
          profile_visitors_name: 'Goal visitors',
          dashboard_name: 'Goal Dashboard',
          share_available: true,
        };
        break;
      }
    }

    return app_info;
  }

  getUserAppProfileData(profile_type: any) {
    return this.activatedRoute.params.pipe(
      filter((params) => params['username']),
      switchMap((params) => {
        return this.communityApplicationService.communityApplicationPublicProfileRetrieve({
          profile_type: profile_type,
          user_name: params['username'],
        });
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(error);
      }),
    );
  }

  getAppMarketingData(app_link: any) {
    let app_info: any = null;

    switch (app_link) {
      case 'botanix':
      case 'garden': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Plants and Garden',
            sectionone_subtitle:
              'A marketplace for plant lovers and gardening enthusiasts. Discover a wide range of plants, gardening tools, seeds, and expert tips to transform your green space.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Browse Plants',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Explore Community',
              sectionone_button_two_icon: 'pi pi-sync',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/members',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/plant_garden/plants_and_garden1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Wide Plant Selection',
                sub_title:
                  'Choose from an extensive collection of indoor and outdoor plants, from common favorites to exotic species.',
                url: this.getAppConfig(app_link)?.app_url + '/catalog',
                last_box: false,
              },
              {
                title: 'Expert Gardening Tips',
                sub_title:
                  'Access a wide range of expert tips and how-to guides to nurture your green space.',
                url: this.getAppConfig(app_link)?.app_url + '/guides',
                last_box: false,
              },
              {
                title: 'Gardening Tools and Accessories',
                sub_title:
                  'Find tools, pots, and equipment to care for your plants and design your garden.',
                url: this.getAppConfig(app_link)?.app_url + '/ads',
                last_box: false,
              },
              {
                title: 'Community and Inspiration',
                sub_title:
                  'Connect with other plant lovers and share tips, ideas, and inspiration.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Find a way to share your plant and garden enthusiasm!',
            section_three_subtitle:
              'A dedicated space for plant and garden lovers, with countless useful features!',
            section_three_button_one: {
              section_three_button_one_title: 'Start Exploring',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/plant_garden/plants_and_garden2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a professional seller?',
            section_four_subtitle:
              'There are no commissions or hidden sales fees. Completely free!',
            section_four_button_one: {
              section_four_button_one_title: 'Join for Free',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Find what you need from countless sellers!',
            boxes_one: [
              {
                title: 'Variety of Plants',
                sub_title:
                  'Browse through a vast marketplace of plants from trusted sellers and nurseries.',
                url: this.getAppConfig(app_link)?.app_url + '/catalog',
                icon: 'qr_code',
              },
              {
                title: 'Gardening Tools',
                sub_title: 'Discover tools to make gardening tasks easier, from pruners to gloves.',
                url: this.getAppConfig(app_link)?.app_url + '/ads',
                icon: 'co_present',
              },
              {
                title: 'Indoor and Outdoor Accessories',
                sub_title: 'Enhance your plant’s environment with stylish pots and planters.',
                url: this.getAppConfig(app_link)?.app_url + '/ads',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Seeds and Saplings',
                sub_title: 'Start your garden with carefully sourced seeds and saplings.',
                url: this.getAppConfig(app_link)?.app_url + '/catalog',
                icon: 'contact_phone',
              },
              {
                title: 'Plant Care Advice',
                sub_title:
                  'Access plant care tips and reminders to help your plants thrive year-round.',
                url: this.getAppConfig(app_link)?.app_url + '/guides',
                icon: 'task_alt',
              },
              {
                title: 'Garden Design Inspiration',
                sub_title:
                  'Find creative layouts and design ideas to bring your garden vision to life.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/plant_garden/plants_and_garden3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium subscription',
            section_six_subtitle: 'Gain access to exclusive features, premium content, and more!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'One application,',
            section_seven_sub_title_second: 'Many solutions',
            boxes: [
              {
                title: 'Is there a fee to sell plants and gardening products?',
                sub_title: 'No, OffConOn does not charge any sales fees.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I connect with other gardeners for advice?',
                sub_title: 'Yes! Join community forums to share tips and connect.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are the plants delivered to my home?',
                sub_title: 'Yes, sellers offer delivery options for convenience.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build a fantastic community.',
            section_eight_subtitle:
              'Millions of garden and plant lovers are looking for a new opportunity!',
          },
        };
        break;
      }

      case 'language_exchange':
      case 'speakup': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Language Exchange',
            sectionone_subtitle:
              'Connect with people from around the world to practice and learn new languages. Join a global community where language learners share knowledge, culture, and conversation.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Start Learning',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Find Partners',
              sectionone_button_two_icon: 'pi pi-sync',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/members',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/language_exchange/language_exchange_1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Find Language Partners',
                sub_title:
                  'Connect with native speakers to practice real-time conversations and improve fluency.',
                url: this.getAppConfig(app_link)?.app_url + '/members',
                last_box: false,
              },
              {
                title: 'Cultural Exchange Events',
                sub_title:
                  'Participate in online events and workshops that promote cultural exchange.',
                url: this.getAppConfig(app_link)?.app_url + '/events',
                last_box: false,
              },
              {
                title: 'Language Learning Resources',
                sub_title: 'Access a library of courses, lessons, and tutorials for all levels.',
                url: this.getAppConfig(app_link)?.app_url + '/courses',
                last_box: false,
              },
              {
                title: 'Global Community',
                sub_title:
                  'Join a worldwide community of language enthusiasts and share experiences.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready to break language barriers?',
            section_three_subtitle: 'Start your language learning journey today, completely free!',
            section_three_button_one: {
              section_three_button_one_title: 'Join Now',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/language_exchange/language_exchange_2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a professional language tutor?',
            section_four_subtitle:
              'Offer your services without commission or hidden fees. Join our global community of language professionals!',
            section_four_button_one: {
              section_four_button_one_title: 'Get Started',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Learn languages, explore cultures, connect globally!',
            boxes_one: [
              {
                title: 'Multilingual Chat Support',
                sub_title:
                  'Engage in real-time chats with native speakers to practice conversation skills.',
                url: this.getAppConfig(app_link)?.app_url + '/chat',
                icon: 'qr_code',
              },
              {
                title: 'Language Level Assessment',
                sub_title: 'Take a free language assessment test to track your proficiency.',
                url: this.getAppConfig(app_link)?.app_url + '/assessment',
                icon: 'co_present',
              },
              {
                title: 'Interactive Learning Tools',
                sub_title: 'Use flashcards, quizzes, and video tutorials to enhance your skills.',
                url: this.getAppConfig(app_link)?.app_url + '/tools',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Community Forums',
                sub_title: 'Join forums dedicated to specific languages and share knowledge.',
                url: this.getAppConfig(app_link)?.app_url + '/forums',
                icon: 'contact_phone',
              },
              {
                title: 'Language Exchange Matchmaking',
                sub_title: 'Match with partners based on your interests and goals.',
                url: this.getAppConfig(app_link)?.app_url + '/matchmaking',
                icon: 'task_alt',
              },
              {
                title: 'Expert-Led Workshops',
                sub_title: 'Attend workshops led by experts to accelerate your learning.',
                url: this.getAppConfig(app_link)?.app_url + '/workshops',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/language_exchange/language_exchange_3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium subscription',
            section_six_subtitle:
              'Unlock personalized language coaching and exclusive content with our premium subscription!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'One application,',
            section_seven_sub_title_second: 'Many solutions',
            boxes: [
              {
                title: 'Is it free to join the Language Exchange community?',
                sub_title:
                  'Yes, joining and participating in the Language Exchange community is free.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I find tutors on this platform?',
                sub_title: 'Absolutely! Connect with professional tutors for personalized lessons.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Do I need prior language knowledge to join?',
                sub_title: 'No, we have resources for learners of all levels.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build a global language learning community!',
            section_eight_subtitle:
              'Millions of language learners are waiting to connect with you!',
          },
        };
        break;
      }
      case 'business_networking':
      case 'bizconnect': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Business Network',
            sectionone_subtitle:
              'A platform for professionals to connect, collaborate, and grow their businesses. Discover networking events, business services, and valuable connections to elevate your career.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Get Started',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Explore Services',
              sectionone_button_two_icon: 'pi pi-sync',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/services',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/business_networking/business_networking1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Professional Networking',
                sub_title:
                  'Connect with industry leaders, mentors, and peers to expand your professional network.',
                url: this.getAppConfig(app_link)?.app_url + '/network',
                last_box: false,
              },
              {
                title: 'Business Services',
                sub_title: 'Access a range of services tailored to help your business grow.',
                url: this.getAppConfig(app_link)?.app_url + '/services',
                last_box: false,
              },
              {
                title: 'Exclusive Events',
                sub_title:
                  'Join workshops, conferences, and networking events to stay ahead in your industry.',
                url: this.getAppConfig(app_link)?.app_url + '/events',
                last_box: false,
              },
              {
                title: 'Mentorship Opportunities',
                sub_title:
                  'Find experienced mentors to guide you in your career and business ventures.',
                url: this.getAppConfig(app_link)?.app_url + '/mentorship',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready to elevate your career?',
            section_three_subtitle: 'Join the ultimate platform for business professionals today!',
            section_three_button_one: {
              section_three_button_one_title: 'Sign Up Now',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/business_networking/business_networking2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a business services provider?',
            section_four_subtitle:
              'Offer your services commission-free and connect with entrepreneurs looking for solutions.',
            section_four_button_one: {
              section_four_button_one_title: 'Join for Free',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Grow your business, expand your network!',
            boxes_one: [
              {
                title: 'Industry-Specific Networks',
                sub_title:
                  'Join networks tailored to your industry to make meaningful connections.',
                url: this.getAppConfig(app_link)?.app_url + '/networks',
                icon: 'qr_code',
              },
              {
                title: 'Job Opportunities',
                sub_title: 'Access job boards to find new opportunities or hire talent.',
                url: this.getAppConfig(app_link)?.app_url + '/jobs',
                icon: 'co_present',
              },
              {
                title: 'Business Collaboration Tools',
                sub_title: 'Collaborate on projects using shared workspaces and tools.',
                url: this.getAppConfig(app_link)?.app_url + '/collaboration',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Professional Profiles',
                sub_title: 'Create a profile to showcase your skills and achievements.',
                url: this.getAppConfig(app_link)?.app_url + '/profiles',
                icon: 'contact_phone',
              },
              {
                title: 'Event Booking and Management',
                sub_title: 'Easily book tickets to events and manage participation.',
                url: this.getAppConfig(app_link)?.app_url + '/events',
                icon: 'task_alt',
              },
              {
                title: 'Business Promotions',
                sub_title: 'Promote your business to a targeted audience within the platform.',
                url: this.getAppConfig(app_link)?.app_url + '/promotions',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/business_networking/business_networking3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium subscription',
            section_six_subtitle:
              'Get exclusive access to high-level business events and personalized networking opportunities!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'One application,',
            section_seven_sub_title_second: 'Many solutions',
            boxes: [
              {
                title: 'Is it free to join the Business Network?',
                sub_title: 'Yes, creating a profile and connecting with professionals is free.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I promote my business here?',
                sub_title:
                  'Yes, you can promote your business and services to a professional audience.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there fees for attending events?',
                sub_title: 'Some events are free, while others may require a ticket purchase.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build a professional community!',
            section_eight_subtitle:
              'Join thousands of business professionals who are growing their network today!',
          },
        };
        break;
      }
      case 'doctor_finder':
      case 'medspot': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Doctor Finder',
            sectionone_subtitle:
              'Find and book appointments with trusted healthcare professionals in your area. Browse by specialty, location, and patient reviews to get the best care possible.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Search Doctors',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'View Reviews',
              sectionone_button_two_icon: 'pi pi-star',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/reviews',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/doctor_finder/doctor_finder1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Search by Specialty',
                sub_title:
                  'Easily find healthcare professionals based on specialty, from dermatologists to cardiologists.',
                url: this.getAppConfig(app_link)?.app_url + '/specialties',
                last_box: false,
              },
              {
                title: 'Patient Reviews and Ratings',
                sub_title:
                  'Read verified reviews and ratings from other patients to help you choose the right doctor.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                last_box: false,
              },
              {
                title: 'Appointment Scheduling',
                sub_title:
                  'Book appointments online with just a few clicks for a hassle-free experience.',
                url: this.getAppConfig(app_link)?.app_url + '/appointments',
                last_box: false,
              },
              {
                title: 'Location-Based Search',
                sub_title:
                  'Find doctors and clinics in your area, ensuring quick access to care when you need it.',
                url: this.getAppConfig(app_link)?.app_url + '/locations',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Need medical care?',
            section_three_subtitle:
              'Book an appointment with a trusted healthcare professional today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/doctor_finder/doctor_finder2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a healthcare provider?',
            section_four_subtitle:
              'Join our platform to reach more patients and offer appointment bookings for free!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Find the care you need with ease!',
            boxes_one: [
              {
                title: 'Detailed Doctor Profiles',
                sub_title:
                  'View complete profiles with qualifications, specialties, and contact details.',
                url: this.getAppConfig(app_link)?.app_url + '/profiles',
                icon: 'qr_code',
              },
              {
                title: 'Telehealth Services',
                sub_title:
                  'Access virtual consultations with healthcare professionals for remote care.',
                url: this.getAppConfig(app_link)?.app_url + '/telehealth',
                icon: 'co_present',
              },
              {
                title: 'Insurance Compatibility',
                sub_title: 'Filter doctors by accepted insurance plans for easier decision-making.',
                url: this.getAppConfig(app_link)?.app_url + '/insurance',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Emergency Care Locator',
                sub_title: 'Quickly find urgent care centers and emergency rooms in your area.',
                url: this.getAppConfig(app_link)?.app_url + '/emergency',
                icon: 'contact_phone',
              },
              {
                title: 'Follow-Up Scheduling',
                sub_title:
                  'Set follow-up appointments directly through the platform for continuity of care.',
                url: this.getAppConfig(app_link)?.app_url + '/appointments',
                icon: 'task_alt',
              },
              {
                title: 'Prescription Management',
                sub_title:
                  'Request prescription refills or new prescriptions online for convenience.',
                url: this.getAppConfig(app_link)?.app_url + '/prescriptions',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/doctor_finder/doctor_finder3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium subscription',
            section_six_subtitle:
              'Get priority booking, 24/7 access to healthcare professionals, and enhanced patient care services with our premium subscription!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'One application,',
            section_seven_sub_title_second: 'Many solutions',
            boxes: [
              {
                title: 'Is it free to use Doctor Finder?',
                sub_title: 'Yes, searching for doctors and booking appointments is free.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I find specialists on this platform?',
                sub_title:
                  'Absolutely! You can filter by specialty and find the healthcare professional you need.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Is telehealth available?',
                sub_title:
                  'Yes, we offer telehealth consultations for those who prefer virtual appointments.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build a trusted healthcare community!',
            section_eight_subtitle:
              'Share your experience and help others find the best care possible!',
          },
        };
        break;
      }
      case 'beautywave':
      case 'wellness_beauty': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Wellness and Beauty',
            sectionone_subtitle:
              'Your one-stop shop for wellness, beauty products, and services. Explore spa treatments, skincare products, and wellness tips to look and feel your best.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Shop Now',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Explore Services',
              sectionone_button_two_icon: 'pi pi-sync',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/services',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/wellness_and_beauty/wellness_and_beauty1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Spa and Wellness Treatments',
                sub_title:
                  'Explore spa packages and wellness services to rejuvenate your body and mind.',
                url: this.getAppConfig(app_link)?.app_url + '/spa',
                last_box: false,
              },
              {
                title: 'Organic Skincare Products',
                sub_title:
                  'Shop for eco-friendly, organic skincare products to enhance your beauty routine.',
                url: this.getAppConfig(app_link)?.app_url + '/products',
                last_box: false,
              },
              {
                title: 'Holistic Fitness Programs',
                sub_title:
                  'Access a variety of fitness programs tailored to your goals, from yoga to strength training.',
                url: this.getAppConfig(app_link)?.app_url + '/fitness',
                last_box: false,
              },
              {
                title: 'Beauty and Wellness Experts',
                sub_title:
                  'Consult with beauty and wellness professionals for personalized advice and treatments.',
                url: this.getAppConfig(app_link)?.app_url + '/experts',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready for self-care?',
            section_three_subtitle:
              'Start your wellness journey with premium products and treatments today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/wellness_and_beauty/wellness_and_beauty2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a beauty or wellness provider?',
            section_four_subtitle:
              'Join our marketplace and offer your services without any commission fees!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle:
              'Look and feel your best with our curated wellness and beauty products!',
            boxes_one: [
              {
                title: 'Beauty Product Reviews',
                sub_title:
                  'Read reviews from verified buyers to find the best beauty products for your needs.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'qr_code',
              },
              {
                title: 'Wellness Workshops',
                sub_title:
                  'Attend workshops focused on holistic health and wellness, including meditation and mindfulness practices.',
                url: this.getAppConfig(app_link)?.app_url + '/workshops',
                icon: 'co_present',
              },
              {
                title: 'Personalized Skincare Regimens',
                sub_title:
                  'Get personalized skincare recommendations based on your skin type and concerns.',
                url: this.getAppConfig(app_link)?.app_url + '/skincare',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Spa and Salon Booking',
                sub_title:
                  'Book spa treatments and salon appointments through our easy-to-use platform.',
                url: this.getAppConfig(app_link)?.app_url + '/spa',
                icon: 'contact_phone',
              },
              {
                title: 'Natural and Organic Products',
                sub_title:
                  'Discover eco-friendly and cruelty-free beauty products that are good for you and the planet.',
                url: this.getAppConfig(app_link)?.app_url + '/organic',
                icon: 'task_alt',
              },
              {
                title: 'Fitness and Nutrition Plans',
                sub_title:
                  'Access fitness programs and nutrition plans tailored to your health goals.',
                url: this.getAppConfig(app_link)?.app_url + '/fitness',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/wellness_and_beauty/wellness_and_beauty3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium subscription',
            section_six_subtitle:
              'Unlock exclusive wellness and beauty products, early access to workshops, and priority booking with our premium subscription!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'One application,',
            section_seven_sub_title_second: 'Many solutions',
            boxes: [
              {
                title: 'Is there a fee for using the Wellness and Beauty marketplace?',
                sub_title: 'No, browsing and purchasing products is free.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I book spa treatments online?',
                sub_title:
                  'Yes, you can book treatments and appointments directly through our platform.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are the products eco-friendly?',
                sub_title:
                  'Yes, we offer a wide range of organic and eco-friendly beauty products.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build a community of wellness and beauty enthusiasts!',
            section_eight_subtitle:
              'Join thousands of users who are looking and feeling their best with our platform!',
          },
        };
        break;
      }
      case 'pets':
      case 'petnest': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Pets',
            sectionone_subtitle:
              'A dedicated marketplace for pet lovers. Find pet supplies, accessories, grooming services, and even adoption opportunities to give your furry friends the best life.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Search',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Start Browsing',
              sectionone_button_two_icon: 'pi pi-sync',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/members',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/pets/pets1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Pet Adoption',
                sub_title:
                  'Find pets in need of loving homes and start the adoption process online.',
                url: this.getAppConfig(app_link)?.app_url + '/members',
                last_box: false,
              },
              {
                title: 'Pet Care Services',
                sub_title:
                  'Book grooming, veterinary, and pet sitting services through trusted providers.',
                url: this.getAppConfig(app_link)?.app_url + '/ads',
                last_box: false,
              },
              {
                title: 'High-Quality Pet Supplies',
                sub_title:
                  'Shop for premium food, toys, and accessories to keep your pets happy and healthy.',
                url: this.getAppConfig(app_link)?.app_url + '/ads',
                last_box: false,
              },
              {
                title: 'Pet Training and Behavior Advice',
                sub_title:
                  'Access expert advice and services to help train and care for your pets.',
                url: this.getAppConfig(app_link)?.app_url + '/catalog-collection',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready to give your pet the best?',
            section_three_subtitle: 'Shop for premium pet products and services today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/pets/pets2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a pet service provider?',
            section_four_subtitle:
              'Offer your pet care services to a wider audience for free – no hidden fees!',
            section_four_button_one: {
              section_four_button_one_title: 'Get started',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Everything you need for your pets is just a click away!',
            boxes_one: [
              {
                title: 'Adoption Listings',
                sub_title:
                  'Browse profiles of pets available for adoption and start the process online.',
                url: this.getAppConfig(app_link)?.app_url + '/members',
                icon: 'qr_code',
              },
              {
                title: 'Pet Care Subscriptions',
                sub_title:
                  'Set up recurring deliveries of food, treats, and other supplies, so you never run out.',
                url: this.getAppConfig(app_link)?.app_url + '/ads',
                icon: 'co_present',
              },
              {
                title: 'Veterinary Services',
                sub_title:
                  'Find and book appointments with local veterinarians to ensure your pet’s health.',
                url: this.getAppConfig(app_link)?.app_url + '/ads',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Grooming Services',
                sub_title:
                  'Schedule grooming appointments for your pets with trusted professionals.',
                url: this.getAppConfig(app_link)?.app_url + '/members',
                icon: 'contact_phone',
              },
              {
                title: 'Pet Health Products',
                sub_title:
                  'Shop for vitamins, supplements, and health care products to keep your pet in top condition.',
                url: this.getAppConfig(app_link)?.app_url + '/ads',
                icon: 'task_alt',
              },
              {
                title: 'Pet Training Courses',
                sub_title:
                  'Access training programs that help with behavioral issues and obedience training.',
                url: this.getAppConfig(app_link)?.app_url + '/ads',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/pets/pets3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium subscription',
            section_six_subtitle:
              'Get access to exclusive pet care products, early booking for services, and more with our premium subscription!',
            section_six_button_one: {
              section_six_button_one_title: 'Get started instantly',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'One application,',
            section_seven_sub_title_second: 'Many solutions',
            boxes: [
              {
                title: 'Is it free to browse pet adoption listings?',
                sub_title: 'Yes, browsing and starting the adoption process is free.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I schedule pet services through the platform?',
                sub_title:
                  'Yes, you can book grooming, training, and veterinary services directly through the app.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are the pet supplies high quality?',
                sub_title:
                  'Yes, we partner with trusted suppliers to provide premium products for your pets.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build a pet-loving community!',
            section_eight_subtitle:
              'Join millions of pet owners and give your pets the best life possible!',
          },
        };
        break;
      }
      case 'skilllink':
      case 'skill_link':
      case 'skill-link': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Skill Link',
            sectionone_subtitle:
              'A hub for learning and sharing skills. Access courses, workshops, and consulting services across various fields to enhance your knowledge and expertise.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Courses',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/courses',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join Workshops',
              sectionone_button_two_icon: 'pi pi-sync',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/workshops',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/education/skill_link1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Expert-Led Courses',
                sub_title:
                  'Access high-quality courses taught by industry experts across a wide range of fields.',
                url: this.getAppConfig(app_link)?.app_url + '/courses',
                last_box: false,
              },
              {
                title: 'Workshops and Webinars',
                sub_title:
                  'Participate in live workshops and webinars for hands-on learning and real-time interaction.',
                url: this.getAppConfig(app_link)?.app_url + '/webinars',
                last_box: false,
              },
              {
                title: 'Consulting Services',
                sub_title:
                  'Connect with professionals for personalized consulting in your field of interest.',
                url: this.getAppConfig(app_link)?.app_url + '/consulting',
                last_box: false,
              },
              {
                title: 'Peer Learning and Collaboration',
                sub_title:
                  'Join study groups, collaborate with peers, and grow your network while you learn.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready to enhance your skills?',
            section_three_subtitle:
              'Join the community and start learning today with free and premium courses!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/education/skill_link2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you an expert looking to share your knowledge?',
            section_four_subtitle:
              'Create and sell your own courses, workshops, and services with no commissions!',
            section_four_button_one: {
              section_four_button_one_title: 'Join as an Instructor',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle:
              'Learn, grow, and share your skills with a community of learners!',
            boxes_one: [
              {
                title: 'Wide Course Selection',
                sub_title:
                  'Explore a variety of courses in fields like technology, business, arts, and personal development.',
                url: this.getAppConfig(app_link)?.app_url + '/courses',
                icon: 'qr_code',
              },
              {
                title: 'Certification Programs',
                sub_title: 'Complete courses and receive certificates to showcase your new skills.',
                url: this.getAppConfig(app_link)?.app_url + '/certifications',
                icon: 'co_present',
              },
              {
                title: 'Live Interaction with Instructors',
                sub_title:
                  'Ask questions, get feedback, and engage in live discussions with instructors.',
                url: this.getAppConfig(app_link)?.app_url + '/live-sessions',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Custom Learning Paths',
                sub_title:
                  'Create personalized learning plans that suit your career goals and interests.',
                url: this.getAppConfig(app_link)?.app_url + '/learning-paths',
                icon: 'contact_phone',
              },
              {
                title: 'Course Reviews and Ratings',
                sub_title: 'Read reviews from other learners to help you choose the best course.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'task_alt',
              },
              {
                title: 'Collaborative Projects',
                sub_title:
                  'Work on projects with fellow learners to apply your skills in real-world scenarios.',
                url: this.getAppConfig(app_link)?.app_url + '/projects',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/education/skill_link3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium subscription',
            section_six_subtitle:
              'Access exclusive courses, advanced workshops, and personalized consulting services with our premium plan!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'One application,',
            section_seven_sub_title_second: 'Many solutions',
            boxes: [
              {
                title: 'Are the courses free?',
                sub_title:
                  'Many courses are free, but some advanced courses and certifications may require payment.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I earn certifications?',
                sub_title: 'Yes, we offer certification programs to verify your skills.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I create my own courses?',
                sub_title:
                  'Absolutely! You can become an instructor and create courses for other learners, commission-free.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build a thriving learning community!',
            section_eight_subtitle: 'Share your knowledge, take a course, and help others grow!',
          },
        };
        break;
      }
      case 'begreen':
      case 'be_green':
      case 'be-green': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Be Green',
            sectionone_subtitle:
              'Discover eco-friendly products and services that promote a sustainable lifestyle. From zero-waste items to green solutions for your home, Be Green is your guide to a better planet.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Shop Now',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Explore Green Solutions',
              sectionone_button_two_icon: 'pi pi-sync',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/services',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/be_green/be_green1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Eco-Friendly Products',
                sub_title:
                  'Shop for zero-waste essentials, reusable items, and sustainably sourced products.',
                url: this.getAppConfig(app_link)?.app_url + '/products',
                last_box: false,
              },
              {
                title: 'Green Technology',
                sub_title:
                  'Discover innovative green tech solutions for energy efficiency and sustainable living.',
                url: this.getAppConfig(app_link)?.app_url + '/green-tech',
                last_box: false,
              },
              {
                title: 'Sustainable Fashion',
                sub_title:
                  'Explore clothing and accessories made from eco-friendly materials that are ethically produced.',
                url: this.getAppConfig(app_link)?.app_url + '/fashion',
                last_box: false,
              },
              {
                title: 'Community and Impact',
                sub_title:
                  'Connect with a community of like-minded individuals who are committed to creating a positive environmental impact.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready to live a greener life?',
            section_three_subtitle:
              'Find eco-friendly products and services to support your sustainable lifestyle today!',
            section_three_button_one: {
              section_three_button_one_title: 'Start Shopping',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/be_green/be_green2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you an eco-conscious business?',
            section_four_subtitle:
              'Join our platform and sell your eco-friendly products without any commission fees!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Sustainable products and services for a better planet!',
            boxes_one: [
              {
                title: 'Zero-Waste Essentials',
                sub_title:
                  'Shop for reusable bags, bottles, and other zero-waste essentials to reduce your environmental footprint.',
                url: this.getAppConfig(app_link)?.app_url + '/zero-waste',
                icon: 'qr_code',
              },
              {
                title: 'Sustainable Home Goods',
                sub_title:
                  'Discover eco-friendly home decor, kitchenware, and cleaning products to create a greener home.',
                url: this.getAppConfig(app_link)?.app_url + '/home',
                icon: 'co_present',
              },
              {
                title: 'Renewable Energy Solutions',
                sub_title:
                  'Find solar panels, energy-efficient appliances, and other renewable energy products for your home.',
                url: this.getAppConfig(app_link)?.app_url + '/energy',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Sustainable Fashion',
                sub_title:
                  'Browse ethical clothing made from organic and recycled materials, supporting slow fashion.',
                url: this.getAppConfig(app_link)?.app_url + '/fashion',
                icon: 'contact_phone',
              },
              {
                title: 'Eco-Friendly Beauty',
                sub_title:
                  'Shop natural, cruelty-free, and vegan beauty products that are gentle on your skin and the environment.',
                url: this.getAppConfig(app_link)?.app_url + '/beauty',
                icon: 'task_alt',
              },
              {
                title: 'Green Living Tips',
                sub_title:
                  'Get tips and guides on living a more sustainable life, from reducing waste to conserving energy.',
                url: this.getAppConfig(app_link)?.app_url + '/tips',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/be_green/be_green3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium subscription',
            section_six_subtitle:
              'Access exclusive discounts on eco-friendly products, early access to green innovations, and more with our premium subscription!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'One application,',
            section_seven_sub_title_second: 'Many solutions',
            boxes: [
              {
                title: 'Are all products eco-friendly?',
                sub_title:
                  'Yes, all products on Be Green are carefully curated to meet sustainable and eco-friendly standards.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I return items?',
                sub_title:
                  'Yes, we have a flexible return policy, but we encourage reducing waste by choosing carefully before buying.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there green living tips on the platform?',
                sub_title:
                  'Yes, we provide guides and tips to help you live a more sustainable lifestyle.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build a greener community!',
            section_eight_subtitle: 'Join the movement for a better planet today!',
          },
        };
        break;
      }
      case 'secondchance':
      case 'second-chance':
      case 'second_chance': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Second Chance',
            sectionone_subtitle:
              'A charity marketplace dedicated to saving animals in need. Support animal rescue, rehabilitation, and adoption efforts by contributing to this compassionate cause. Every purchase helps give animals a new lease on life.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Shop Now',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Make a Donation',
              sectionone_button_two_icon: 'pi pi-heart',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/donate',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/second_chance/second_chance1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Support Animal Welfare',
                sub_title:
                  'Every purchase directly supports animal rescue and rehabilitation efforts.',
                url: this.getAppConfig(app_link)?.app_url + '/catalog',
                last_box: false,
              },
              {
                title: 'Adopt a Rescue Pet',
                sub_title:
                  'Explore adoption opportunities and give a loving home to animals in need.',
                url: this.getAppConfig(app_link)?.app_url + '/adoption',
                last_box: false,
              },
              {
                title: 'Donate to Animal Charities',
                sub_title:
                  'Easily make donations to trusted animal welfare organizations through our platform.',
                url: this.getAppConfig(app_link)?.app_url + '/donate',
                last_box: false,
              },
              {
                title: 'Unique Charity Products',
                sub_title: 'Shop for unique products that contribute to animal causes.',
                url: this.getAppConfig(app_link)?.app_url + '/products',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready to make a difference?',
            section_three_subtitle: 'Shop or donate today to give animals a second chance at life!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/second_chance/second_chance2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you an animal charity?',
            section_four_subtitle:
              'Join our marketplace and raise funds for your rescue efforts without any commission fees!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Support, adopt, and help animals in need with Second Chance!',
            boxes_one: [
              {
                title: 'Adoption Listings',
                sub_title:
                  'Browse profiles of rescue animals ready for adoption and start the process online.',
                url: this.getAppConfig(app_link)?.app_url + '/adoption',
                icon: 'qr_code',
              },
              {
                title: 'Donation Opportunities',
                sub_title:
                  'Donate directly to animal shelters and rescues, knowing your contribution will make a difference.',
                url: this.getAppConfig(app_link)?.app_url + '/donate',
                icon: 'co_present',
              },
              {
                title: 'Charity Events',
                sub_title:
                  'Participate in charity events, auctions, and fundraisers to support animal welfare.',
                url: this.getAppConfig(app_link)?.app_url + '/events',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Rescue Pet Products',
                sub_title:
                  'Buy pet products, with proceeds going towards animal rescue and rehabilitation efforts.',
                url: this.getAppConfig(app_link)?.app_url + '/products',
                icon: 'contact_phone',
              },
              {
                title: 'Volunteer Programs',
                sub_title:
                  'Sign up for volunteer opportunities at animal shelters and rescues near you.',
                url: this.getAppConfig(app_link)?.app_url + '/volunteer',
                icon: 'task_alt',
              },
              {
                title: 'Pet Health Support',
                sub_title:
                  'Support rehabilitation programs that provide medical care for injured or abandoned animals.',
                url: this.getAppConfig(app_link)?.app_url + '/health',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/second_chance/second_chance3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium subscription',
            section_six_subtitle:
              'Get exclusive updates on rescue efforts, early access to charity events, and more with our premium subscription!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'One application,',
            section_seven_sub_title_second: 'Many solutions',
            boxes: [
              {
                title: 'Do all purchases support animal rescue?',
                sub_title:
                  'Yes, every purchase on Second Chance contributes to animal rescue and welfare efforts.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I adopt pets through the platform?',
                sub_title:
                  'Yes, we offer a platform where you can browse rescue pets and start the adoption process.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I make direct donations?',
                sub_title:
                  'Absolutely! You can donate directly to animal charities and shelters through our platform.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build a compassionate community for animals!',
            section_eight_subtitle: 'Together, we can give every animal a second chance!',
          },
        };
        break;
      }
      case 'events':
      case 'eventix': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Eventix',
            sectionone_subtitle:
              'Find and book tickets for events, concerts, and activities happening near you. Create and promote your own events to connect with a larger audience.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Find Events',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Promote Your Event',
              sectionone_button_two_icon: 'pi pi-bullhorn',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/create',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/events/events1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Event Ticket Booking',
                sub_title:
                  'Easily find and book tickets for concerts, festivals, conferences, and more.',
                url: this.getAppConfig(app_link)?.app_url + '/tickets',
                last_box: false,
              },
              {
                title: 'Venue Booking',
                sub_title:
                  'Need a space for your event? Book venues of all sizes to suit your event needs.',
                url: this.getAppConfig(app_link)?.app_url + '/venues',
                last_box: false,
              },
              {
                title: 'Promote Your Events',
                sub_title:
                  'Host and promote your own events, reaching a wider audience through our platform.',
                url: this.getAppConfig(app_link)?.app_url + '/create',
                last_box: false,
              },
              {
                title: 'Discover Local and Global Events',
                sub_title:
                  'Explore events happening near you or worldwide, and connect with a community of event-goers.',
                url: this.getAppConfig(app_link)?.app_url + '/explore',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Looking for something to do?',
            section_three_subtitle:
              'Find and book tickets to exciting events happening near you today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/events/events2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Hosting an event?',
            section_four_subtitle:
              'Promote your event and reach a larger audience for free on our platform!',
            section_four_button_one: {
              section_four_button_one_title: 'Start Promoting',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Discover, book, and promote events with ease!',
            boxes_one: [
              {
                title: 'Event Categories',
                sub_title:
                  'Browse events by category – music, art, business, sports, and more – to find what interests you.',
                url: this.getAppConfig(app_link)?.app_url + '/categories',
                icon: 'qr_code',
              },
              {
                title: 'Early Bird Discounts',
                sub_title:
                  'Get early bird discounts on tickets when you book events ahead of time.',
                url: this.getAppConfig(app_link)?.app_url + '/discounts',
                icon: 'co_present',
              },
              {
                title: 'VIP Event Packages',
                sub_title: 'Access VIP packages for premium experiences at concerts and festivals.',
                url: this.getAppConfig(app_link)?.app_url + '/vip',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Custom Event Pages',
                sub_title:
                  'Create a custom page for your event with all the details, including ticket sales and venue information.',
                url: this.getAppConfig(app_link)?.app_url + '/event-pages',
                icon: 'contact_phone',
              },
              {
                title: 'Event Calendar',
                sub_title:
                  'Stay up to date with an interactive calendar that shows upcoming events.',
                url: this.getAppConfig(app_link)?.app_url + '/calendar',
                icon: 'task_alt',
              },
              {
                title: 'Group Booking',
                sub_title:
                  'Book tickets for friends and family with group booking options, and enjoy group discounts.',
                url: this.getAppConfig(app_link)?.app_url + '/group-booking',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/events/events3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium subscription',
            section_six_subtitle:
              'Get early access to event tickets, VIP packages, and exclusive invitations to top events with our premium subscription!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'One application,',
            section_seven_sub_title_second: 'Many solutions',
            boxes: [
              {
                title: 'Can I book tickets for events worldwide?',
                sub_title: 'Yes, you can explore and book tickets for events globally.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there any fees for promoting my events?',
                sub_title: 'No, promoting your event on our platform is completely free.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Do I get a refund if the event is canceled?',
                sub_title:
                  "Yes, in the case of a cancellation, refunds are processed based on the event organizer's policy.",
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us create an unforgettable event community!',
            section_eight_subtitle: 'Join millions of event-goers and hosts on our platform today!',
          },
        };
        break;
      }
      case 'renting':
      case 'rentspace': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'RentSpace',
            sectionone_subtitle:
              'The ultimate marketplace for renting everything you need. From equipment to real estate, find rental options that suit your budget and needs.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Browse Rentals',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'List Your Rental',
              sectionone_button_two_icon: 'pi pi-upload',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/create',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/renting/renting1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Real Estate Rentals',
                sub_title:
                  'Find rental properties, including vacation homes, apartments, and office spaces for short or long-term needs.',
                url: this.getAppConfig(app_link)?.app_url + '/real-estate',
                last_box: false,
              },
              {
                title: 'Equipment and Tools',
                sub_title:
                  'Rent equipment for DIY projects, construction, or business operations without the hefty purchase cost.',
                url: this.getAppConfig(app_link)?.app_url + '/equipment',
                last_box: false,
              },
              {
                title: 'Event Rentals',
                sub_title:
                  'Book venues, furniture, or event gear for weddings, parties, or corporate gatherings.',
                url: this.getAppConfig(app_link)?.app_url + '/events',
                last_box: false,
              },
              {
                title: 'Flexible Rental Terms',
                sub_title:
                  'Enjoy flexible rental terms to suit your timeline, whether you need something for a day, a week, or longer.',
                url: this.getAppConfig(app_link)?.app_url + '/flexible-terms',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Need something?',
            section_three_subtitle:
              'Start browsing our rental marketplace for items and properties today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/renting/renting2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Do you own items or properties to rent?',
            section_four_subtitle:
              'List your rentals on our platform without any fees and reach more customers!',
            section_four_button_one: {
              section_four_button_one_title: 'List Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Affordable and flexible rental options at your fingertips!',
            boxes_one: [
              {
                title: 'Daily, Weekly, and Monthly Rentals',
                sub_title:
                  'Find rental options with flexible durations, whether you need it for a day or an extended period.',
                url: this.getAppConfig(app_link)?.app_url + '/rental-durations',
                icon: 'qr_code',
              },
              {
                title: 'Property Listings',
                sub_title:
                  'Search for vacation homes, apartments, or office spaces available for rent in your area.',
                url: this.getAppConfig(app_link)?.app_url + '/properties',
                icon: 'co_present',
              },
              {
                title: 'Easy Booking Process',
                sub_title: 'Book rentals quickly and securely with just a few clicks.',
                url: this.getAppConfig(app_link)?.app_url + '/booking',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Equipment and Tool Rentals',
                sub_title:
                  'Rent construction tools, gardening equipment, or party supplies with convenient pick-up and drop-off options.',
                url: this.getAppConfig(app_link)?.app_url + '/tools',
                icon: 'contact_phone',
              },
              {
                title: 'Tenant and Renter Reviews',
                sub_title:
                  'Read reviews from previous renters to ensure you’re making the best choice.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'task_alt',
              },
              {
                title: 'Damage Protection Options',
                sub_title:
                  'Opt for rental insurance to protect against accidental damage during the rental period.',
                url: this.getAppConfig(app_link)?.app_url + '/damage-protection',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/renting/renting3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium subscription',
            section_six_subtitle:
              'Unlock premium rental listings, exclusive discounts, and priority bookings with our premium subscription!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'One application,',
            section_seven_sub_title_second: 'Many solutions',
            boxes: [
              {
                title: 'Is it free to list my rental property or item?',
                sub_title:
                  'Yes, listing your rental property or items on our platform is completely free.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I rent internationally?',
                sub_title: 'Yes, you can find rental options in multiple countries and regions.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'What happens if something is damaged during a rental?',
                sub_title:
                  'We offer optional damage protection to cover accidental damages during the rental period.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build a dynamic rental community!',
            section_eight_subtitle: 'Join millions of renters and owners on our platform today!',
          },
        };
        break;
      }
      case 'booking':
      case 'scheduly': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Scheduly',
            sectionone_subtitle:
              'A seamless platform for booking appointments, services, and reservations. From restaurants to travel accommodations, plan your activities effortlessly.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Start Booking',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'List Your Services',
              sectionone_button_two_icon: 'pi pi-upload',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/create',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/booking/booking1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Restaurant and Dining Reservations',
                sub_title:
                  'Book tables at top restaurants and discover dining experiences in your area.',
                url: this.getAppConfig(app_link)?.app_url + '/restaurants',
                last_box: false,
              },
              {
                title: 'Travel and Accommodations',
                sub_title:
                  'Find and reserve hotels, vacation homes, and travel packages for your next trip.',
                url: this.getAppConfig(app_link)?.app_url + '/travel',
                last_box: false,
              },
              {
                title: 'Service Appointments',
                sub_title:
                  'Schedule beauty, wellness, or healthcare services with trusted providers near you.',
                url: this.getAppConfig(app_link)?.app_url + '/services',
                last_box: false,
              },
              {
                title: 'Event and Activity Booking',
                sub_title:
                  'Book tickets and reservations for concerts, shows, and activities, all from one platform.',
                url: this.getAppConfig(app_link)?.app_url + '/events',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready to book your next experience?',
            section_three_subtitle:
              'Discover and reserve restaurants, services, and accommodations with ease!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/booking/booking2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a service provider or venue owner?',
            section_four_subtitle: 'List your services and accept bookings without any extra fees!',
            section_four_button_one: {
              section_four_button_one_title: 'List Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Easily book restaurants, services, and activities today!',
            boxes_one: [
              {
                title: 'Restaurant Discovery',
                sub_title:
                  'Find and book top-rated restaurants and dining experiences in your city.',
                url: this.getAppConfig(app_link)?.app_url + '/restaurants',
                icon: 'qr_code',
              },
              {
                title: 'Travel Booking',
                sub_title:
                  'Reserve accommodations, flights, and transportation for your next vacation or business trip.',
                url: this.getAppConfig(app_link)?.app_url + '/travel',
                icon: 'co_present',
              },
              {
                title: 'Service Scheduling',
                sub_title:
                  'Book salon, spa, or medical appointments with trusted providers through our platform.',
                url: this.getAppConfig(app_link)?.app_url + '/services',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Real-Time Availability',
                sub_title:
                  'See up-to-date availability for services, tables, and accommodations before booking.',
                url: this.getAppConfig(app_link)?.app_url + '/availability',
                icon: 'contact_phone',
              },
              {
                title: 'Personalized Recommendations',
                sub_title:
                  'Get tailored suggestions for dining, travel, and services based on your preferences.',
                url: this.getAppConfig(app_link)?.app_url + '/recommendations',
                icon: 'task_alt',
              },
              {
                title: 'Instant Booking Confirmations',
                sub_title: 'Receive instant confirmations via email or SMS for all your bookings.',
                url: this.getAppConfig(app_link)?.app_url + '/confirmations',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/booking/booking3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium subscription',
            section_six_subtitle:
              'Enjoy exclusive booking deals, early access to reservations, and more with our premium subscription!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'One application,',
            section_seven_sub_title_second: 'Many solutions',
            boxes: [
              {
                title: 'Is there a fee to book through the platform?',
                sub_title:
                  'No, booking services, restaurants, and accommodations are free of additional fees.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I cancel my bookings?',
                sub_title:
                  'Yes, cancellation policies vary based on the provider, but you can manage cancellations directly through the platform.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I find last-minute bookings?',
                sub_title:
                  'Yes, we provide real-time availability, so you can book last-minute services and reservations.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us create a seamless booking experience!',
            section_eight_subtitle:
              'Join millions of users in simplifying the way you plan your activities today!',
          },
        };
        break;
      }

      case 'job':
      case 'careerpath':
      case 'career-path': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'CareerPath',
            sectionone_subtitle:
              'A dynamic job marketplace connecting employers and job seekers. Browse job listings, upload resumes, and find the perfect career opportunity tailored to your skills.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Find Jobs',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/jobs',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Post a Job',
              sectionone_button_two_icon: 'pi pi-upload',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: '/cognitive-erp/create',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/jobs/jobs1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Job Search by Industry',
                sub_title:
                  'Filter job listings by industry to find opportunities that match your skills and interests.',
                url: this.getAppConfig(app_link)?.app_url + '/industries',
                last_box: false,
              },
              {
                title: 'Resume Upload and Profile Creation',
                sub_title:
                  'Create a professional profile and upload your resume for employers to find.',
                url: this.getAppConfig(app_link)?.app_url + '/resume',
                last_box: false,
              },
              {
                title: 'Personalized Job Alerts',
                sub_title:
                  'Set up alerts for new job postings that match your career goals and preferences.',
                url: this.getAppConfig(app_link)?.app_url + '/alerts',
                last_box: false,
              },
              {
                title: 'Employer Connections',
                sub_title:
                  'Connect directly with hiring managers and employers for interviews and networking opportunities.',
                url: this.getAppConfig(app_link)?.app_url + '/connections',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready to find your dream job?',
            section_three_subtitle: 'Start browsing thousands of job opportunities today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/jobs/jobs2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you hiring?',
            section_four_subtitle:
              'Post job listings for free and connect with top talent through our platform!',
            section_four_button_one: {
              section_four_button_one_title: 'Post a Job',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Find your next job or hire top talent easily!',
            boxes_one: [
              {
                title: 'Industry-Specific Job Boards',
                sub_title:
                  'Search job opportunities by specific industries, such as tech, healthcare, finance, and more.',
                url: this.getAppConfig(app_link)?.app_url + '/industries',
                icon: 'qr_code',
              },
              {
                title: 'Resume Builder',
                sub_title:
                  'Use our resume builder tool to create a professional resume that stands out to employers.',
                url: this.getAppConfig(app_link)?.app_url + '/resume-builder',
                icon: 'co_present',
              },
              {
                title: 'Application Tracking',
                sub_title:
                  'Keep track of all your job applications in one place, from submission to interview scheduling.',
                url: this.getAppConfig(app_link)?.app_url + '/applications',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Company Reviews and Ratings',
                sub_title:
                  'Read reviews from current and former employees to learn more about the companies you’re interested in.',
                url: this.getAppConfig(app_link)?.app_url + '/company-reviews',
                icon: 'contact_phone',
              },
              {
                title: 'Networking Opportunities',
                sub_title:
                  'Connect with professionals and employers to expand your job search and networking opportunities.',
                url: this.getAppConfig(app_link)?.app_url + '/networking',
                icon: 'task_alt',
              },
              {
                title: 'Salary Insights',
                sub_title:
                  'Access salary information for different job roles, helping you make informed decisions during the application process.',
                url: this.getAppConfig(app_link)?.app_url + '/salary-insights',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/jobs/jobs3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium subscription',
            section_six_subtitle:
              'Unlock exclusive job postings, priority application reviews, and personalized career coaching with our premium subscription!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'One application,',
            section_seven_sub_title_second: 'Many solutions',
            boxes: [
              {
                title: 'Is it free to apply for jobs on this platform?',
                sub_title:
                  'Yes, applying for jobs and uploading your resume is free for all job seekers.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I set up job alerts?',
                sub_title:
                  'Yes, you can create personalized job alerts to receive notifications for relevant job listings.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I contact employers directly?',
                sub_title:
                  'Yes, our platform allows you to connect with employers and hiring managers directly.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build a dynamic job marketplace!',
            section_eight_subtitle:
              'Join thousands of job seekers and employers in finding the perfect match today!',
          },
        };
        break;
      }
      case 'digital_products':
      case 'pixelmarket':
      case 'digital_downloads': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'PixelMarket',
            sectionone_subtitle:
              'Buy, sell, and explore digital goods like software, eBooks, templates, and more. A marketplace for creators to showcase their digital products and for buyers to find what they need.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Browse Products',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/downloads',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Sell Your Work',
              sectionone_button_two_icon: 'pi pi-upload',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: '/cognitive-erp/create',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/digital_products/digital_products1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Instant Digital Downloads',
                sub_title:
                  'Purchase and download digital products instantly, from software to eBooks, with secure transactions.',
                url: this.getAppConfig(app_link)?.app_url + '/downloads',
                last_box: false,
              },
              {
                title: 'Creative Design Templates',
                sub_title:
                  'Explore a library of templates for graphic design, presentations, websites, and more.',
                url: this.getAppConfig(app_link)?.app_url + '/templates',
                last_box: false,
              },
              {
                title: 'Online Courses and Tutorials',
                sub_title:
                  'Access a variety of digital learning materials, including video tutorials and courses.',
                url: this.getAppConfig(app_link)?.app_url + '/courses',
                last_box: false,
              },
              {
                title: 'Digital Art and Assets',
                sub_title:
                  'Discover unique digital art, stock images, and other creative assets from artists and creators.',
                url: this.getAppConfig(app_link)?.app_url + '/assets',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Looking for digital products?',
            section_three_subtitle:
              'Explore and buy the latest software, templates, and digital content today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/digital_products/digital_products2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a digital creator?',
            section_four_subtitle:
              'Showcase your work and sell your digital products with no commission fees!',
            section_four_button_one: {
              section_four_button_one_title: 'Start Selling',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Browse, buy, and sell digital goods with ease!',
            boxes_one: [
              {
                title: 'Secure Transactions',
                sub_title:
                  'All purchases are secured with encrypted payment options, ensuring safe transactions.',
                url: this.getAppConfig(app_link)?.app_url + '/secure-payments',
                icon: 'qr_code',
              },
              {
                title: 'Instant Access to Products',
                sub_title:
                  'Get immediate access to your purchases with instant download options for all digital goods.',
                url: this.getAppConfig(app_link)?.app_url + '/instant-downloads',
                icon: 'co_present',
              },
              {
                title: 'Customizable Design Templates',
                sub_title:
                  'Browse templates for various industries and customize them to meet your needs.',
                url: this.getAppConfig(app_link)?.app_url + '/templates',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Digital Product Reviews',
                sub_title:
                  'Read reviews from other buyers to ensure the product meets your expectations.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'contact_phone',
              },
              {
                title: 'E-Book Marketplace',
                sub_title:
                  'Access thousands of eBooks across genres, from fiction to non-fiction, for instant download.',
                url: this.getAppConfig(app_link)?.app_url + '/ebooks',
                icon: 'task_alt',
              },
              {
                title: 'Royalty-Free Stock Images',
                sub_title:
                  'Find and purchase royalty-free images for use in marketing, presentations, or creative projects.',
                url: this.getAppConfig(app_link)?.app_url + '/stock-images',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/digital_products/digital_products3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium subscription',
            section_six_subtitle:
              'Unlock exclusive digital products, early access to new content, and more with our premium subscription!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'One application,',
            section_seven_sub_title_second: 'Many solutions',
            boxes: [
              {
                title: 'Is there a fee to sell digital products?',
                sub_title:
                  'No, selling digital products on our platform is completely free with no commissions.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'How do I receive my digital purchases?',
                sub_title:
                  'Once you complete the purchase, you can instantly download your digital product from your account.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I sell multiple types of digital products?',
                sub_title:
                  'Yes, you can sell various digital products, including software, eBooks, templates, and art.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build a thriving digital product marketplace!',
            section_eight_subtitle:
              'Join digital creators and buyers in exploring and sharing innovative digital content today!',
          },
        };
        break;
      }
      case 'bucket-list':
      case 'bucket_list': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Bucketlist',
            sectionone_subtitle:
              'Discover, create, and achieve your dreams. Connect with others, find inspiration, and share your aspirations on a platform designed to make goals a reality.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Goals',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Create your goal',
              sectionone_button_two_icon: 'pi pi-plus',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/create-catalog',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/bucket_list/bucket_list1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Goal-Setting Tools',
                sub_title:
                  'Create and track your personal bucket list with intuitive goal-setting features.',
                url: this.getAppConfig(app_link)?.app_url + '/goal-tools',
                last_box: false,
              },
              {
                title: 'Inspiration Library',
                sub_title:
                  'Discover new ideas from curated lists and success stories shared by the community.',
                url: this.getAppConfig(app_link)?.app_url + '/inspiration',
                last_box: false,
              },
              {
                title: 'Collaborative Planning',
                sub_title:
                  'Connect with others who share similar goals and plan your adventures together.',
                url: this.getAppConfig(app_link)?.app_url + '/collaboration',
                last_box: false,
              },
              {
                title: 'Achievement Sharing',
                sub_title:
                  'Celebrate your milestones by sharing stories, photos, and videos with others.',
                url: this.getAppConfig(app_link)?.app_url + '/achievements',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Looking for inspiration?',
            section_three_subtitle: 'Explore new goals and start building your bucket list today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/bucket_list/bucket_list2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Have a bucket list dream?',
            section_four_subtitle: 'Join the community and share your journey!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Achieve your goals, one step at a time!',
            boxes_one: [
              {
                title: 'Goal Progress Tracking',
                sub_title: 'Monitor your progress with milestones, deadlines, and checklists.',
                url: this.getAppConfig(app_link)?.app_url + '/tracking',
                icon: 'qr_code',
              },
              {
                title: 'Community Engagement',
                sub_title: 'Share your achievements and motivate others to reach their goals.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                icon: 'co_present',
              },
              {
                title: 'Expert Resources',
                sub_title: 'Access guides, tips, and resources to help you accomplish your dreams.',
                url: this.getAppConfig(app_link)?.app_url + '/resources',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Customizable Lists',
                sub_title: 'Personalize your bucket list to reflect your unique aspirations.',
                url: this.getAppConfig(app_link)?.app_url + '/custom-lists',
                icon: 'contact_phone',
              },
              {
                title: 'Event Planning',
                sub_title: 'Coordinate events with others to achieve shared goals or milestones.',
                url: this.getAppConfig(app_link)?.app_url + '/events',
                icon: 'task_alt',
              },
              {
                title: 'Recognition Badges',
                sub_title: 'Earn badges and rewards for completing goals and inspiring others.',
                url: this.getAppConfig(app_link)?.app_url + '/rewards',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/bucket_list/bucket_list3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Gain access to exclusive goal-planning tools, personalized advice, and a supportive network of like-minded individuals!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your goals, made achievable',
            boxes: [
              {
                title: 'Can I connect with others to achieve shared goals?',
                sub_title:
                  'Yes, you can find and collaborate with others who have similar aspirations.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there tools to track my progress?',
                sub_title:
                  'Absolutely! Use our goal-setting features to track progress and celebrate achievements.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I share my accomplishments?',
                sub_title: 'Yes, share your stories, photos, and videos with the community.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate Bucketlist platform!',
            section_eight_subtitle:
              'Join dreamers and achievers in creating and fulfilling meaningful goals today!',
          },
        };
        break;
      }
      case 'travelbuddies':
      case 'travel-buddies':
      case 'travel_buddies': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Travel Buddies',
            sectionone_subtitle:
              'Find your perfect travel companion. Connect with like-minded explorers and embark on unforgettable adventures together.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Find a Buddy',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/explore',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join the Community',
              sectionone_button_two_icon: 'pi pi-users',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/join',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/travel_buddies/travel_buddies1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Companion Matching',
                sub_title:
                  'Find travel partners who share your interests, destination goals, and travel style.',
                url: this.getAppConfig(app_link)?.app_url + '/matching',
                last_box: false,
              },
              {
                title: 'Trip Planning Tools',
                sub_title:
                  'Collaboratively plan itineraries, budgets, and activities with your travel buddy.',
                url: this.getAppConfig(app_link)?.app_url + '/planning',
                last_box: false,
              },
              {
                title: 'Travel Community Forums',
                sub_title: 'Join discussions, share tips, and connect with travelers worldwide.',
                url: this.getAppConfig(app_link)?.app_url + '/forums',
                last_box: false,
              },
              {
                title: 'Experience Sharing',
                sub_title: 'Post and view photos, stories, and recommendations from past trips.',
                url: this.getAppConfig(app_link)?.app_url + '/experiences',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready for your next trip?',
            section_three_subtitle: 'Find a travel buddy and start planning today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/travel_buddies/travel_buddies2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Looking for a fellow adventurer?',
            section_four_subtitle: 'Join now and connect with travelers like you!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Travel together, better!',
            boxes_one: [
              {
                title: 'Profile Matching',
                sub_title:
                  'Connect with travelers based on shared interests, destinations, and budgets.',
                url: this.getAppConfig(app_link)?.app_url + '/matching',
                icon: 'qr_code',
              },
              {
                title: 'Shared Itineraries',
                sub_title: 'Create, edit, and share trip plans with your travel companions.',
                url: this.getAppConfig(app_link)?.app_url + '/itineraries',
                icon: 'co_present',
              },
              {
                title: 'Destination Insights',
                sub_title: 'Access reviews, tips, and recommendations for travel destinations.',
                url: this.getAppConfig(app_link)?.app_url + '/destinations',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Split Expenses',
                sub_title: 'Simplify cost-sharing for accommodations, transport, and activities.',
                url: this.getAppConfig(app_link)?.app_url + '/expenses',
                icon: 'contact_phone',
              },
              {
                title: 'Group Travel Options',
                sub_title: 'Join group trips or plan adventures with multiple companions.',
                url: this.getAppConfig(app_link)?.app_url + '/group-travel',
                icon: 'task_alt',
              },
              {
                title: 'Safety Features',
                sub_title: 'Verified profiles and secure messaging to ensure safe connections.',
                url: this.getAppConfig(app_link)?.app_url + '/safety',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/travel_buddies/travel_buddies3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Access exclusive travel deals, advanced matching options, and priority community support!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Travel smarter, together',
            boxes: [
              {
                title: 'Is there a fee to find a travel buddy?',
                sub_title: 'No, connecting with travel companions is completely free.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I plan group trips?',
                sub_title:
                  'Yes, you can organize trips with multiple companions using our planning tools.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'How can I ensure safety when meeting someone?',
                sub_title:
                  'All profiles are verified, and secure messaging is provided for safe communication.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate travel community!',
            section_eight_subtitle: 'Find your travel buddy and explore the world together today!',
          },
        };
        break;
      }
      case 'collectors': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Collectors',
            sectionone_subtitle:
              'Buy, sell, and showcase unique collectibles. A dedicated platform for enthusiasts to connect, trade, and grow their collections.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Discover Collectibles',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Sell Your Treasures',
              sectionone_button_two_icon: 'pi pi-upload',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/create',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/collectors/collectors1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Marketplace for Collectibles',
                sub_title:
                  'Buy and sell rare, vintage, or unique items directly with other collectors.',
                url: this.getAppConfig(app_link)?.app_url + '/marketplace',
                last_box: false,
              },
              {
                title: 'Community Discussions',
                sub_title: 'Engage with fellow enthusiasts to share knowledge and discuss trends.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                last_box: false,
              },
              {
                title: 'Collection Management Tools',
                sub_title: 'Organize, track, and display your collection digitally.',
                url: this.getAppConfig(app_link)?.app_url + '/tools',
                last_box: false,
              },
              {
                title: 'Authentication Services',
                sub_title: 'Verify the authenticity of high-value collectibles before purchasing.',
                url: this.getAppConfig(app_link)?.app_url + '/authentication',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Looking for rare finds?',
            section_three_subtitle: 'Discover unique collectibles from passionate sellers today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/collectors/collectors2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Have collectibles to sell?',
            section_four_subtitle: 'List your treasures and connect with collectors worldwide!',
            section_four_button_one: {
              section_four_button_one_title: 'List Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Turn your passion into a thriving collection!',
            boxes_one: [
              {
                title: 'Secure Trading',
                sub_title:
                  'All transactions are protected, ensuring trust between buyers and sellers.',
                url: this.getAppConfig(app_link)?.app_url + '/secure-trading',
                icon: 'qr_code',
              },
              {
                title: 'Detailed Listings',
                sub_title: 'High-quality images and descriptions for every collectible.',
                url: this.getAppConfig(app_link)?.app_url + '/listings',
                icon: 'co_present',
              },
              {
                title: 'Collector Profiles',
                sub_title: 'Showcase your collection and connect with like-minded enthusiasts.',
                url: this.getAppConfig(app_link)?.app_url + '/profiles',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Auction Features',
                sub_title: 'Bid on rare items in live auctions.',
                url: this.getAppConfig(app_link)?.app_url + '/auctions',
                icon: 'contact_phone',
              },
              {
                title: 'Valuation Tools',
                sub_title: 'Access pricing trends to accurately value your collectibles.',
                url: this.getAppConfig(app_link)?.app_url + '/valuation',
                icon: 'task_alt',
              },
              {
                title: 'Collector Groups',
                sub_title: 'Join niche communities for specific types of collectibles.',
                url: this.getAppConfig(app_link)?.app_url + '/groups',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/collectors/collectors3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Unlock exclusive auctions, premium community features, and advanced collection tools!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your collection, our community',
            boxes: [
              {
                title: 'Can I list items for free?',
                sub_title: 'Yes, listing your collectibles is free with no commissions.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there tools to manage my collection?',
                sub_title:
                  'Absolutely! Use our digital tools to organize and display your collection.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'How do I know items are authentic?',
                sub_title: 'Utilize our authentication services to verify high-value collectibles.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate Collectors platform!',
            section_eight_subtitle:
              'Join a vibrant community of enthusiasts and grow your collection today!',
          },
        };
        break;
      }

      case 'realestate':
      case 'real-estate':
      case 'real_estate': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Real Estate',
            sectionone_subtitle:
              'Find your dream home or sell your property with ease. A comprehensive platform for buying, selling, and renting real estate.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Properties',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'List Your Property',
              sectionone_button_two_icon: 'pi pi-upload',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/create',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/real_estate/real_estate1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Advanced Search Tools',
                sub_title:
                  'Filter properties by location, price, size, and features to find exactly what you need.',
                url: this.getAppConfig(app_link)?.app_url + '/search',
                last_box: false,
              },
              {
                title: 'AI-Powered Recommendations',
                sub_title: 'Get personalized property suggestions based on your preferences.',
                url: this.getAppConfig(app_link)?.app_url + '/recommendations',
                last_box: false,
              },
              {
                title: 'Virtual Tours',
                sub_title: 'Explore properties online with immersive 360° virtual tours.',
                url: this.getAppConfig(app_link)?.app_url + '/virtual-tours',
                last_box: false,
              },
              {
                title: 'Agent Connections',
                sub_title:
                  'Directly connect with trusted real estate agents for smooth transactions.',
                url: this.getAppConfig(app_link)?.app_url + '/agents',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Searching for your dream home?',
            section_three_subtitle: 'Explore the best properties in your area today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/real_estate/real_estate2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Have a property to sell?',
            section_four_subtitle: 'List it now and reach thousands of potential buyers!',
            section_four_button_one: {
              section_four_button_one_title: 'List Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your property search, simplified',
            boxes_one: [
              {
                title: 'Interactive Maps',
                sub_title: 'Locate properties with detailed map views and nearby amenities.',
                url: this.getAppConfig(app_link)?.app_url + '/maps',
                icon: 'qr_code',
              },
              {
                title: 'Secure Transactions',
                sub_title: 'Ensure safe and verified dealings with our trusted platform.',
                url: this.getAppConfig(app_link)?.app_url + '/secure-transactions',
                icon: 'co_present',
              },
              {
                title: 'Property Valuation Tools',
                sub_title: 'Access real-time market insights to price your property accurately.',
                url: this.getAppConfig(app_link)?.app_url + '/valuation',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Favorites and Alerts',
                sub_title: 'Save favorite listings and receive alerts on price changes.',
                url: this.getAppConfig(app_link)?.app_url + '/favorites',
                icon: 'contact_phone',
              },
              {
                title: 'Flexible Rental Options',
                sub_title: 'Discover long-term, short-term, or vacation rentals.',
                url: this.getAppConfig(app_link)?.app_url + '/rentals',
                icon: 'task_alt',
              },
              {
                title: 'Home Financing Resources',
                sub_title: 'Get advice and tools for mortgages and property investments.',
                url: this.getAppConfig(app_link)?.app_url + '/financing',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/real_estate/real_estate3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Access exclusive listings, priority support, and advanced tools to make your property search effortless!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Real estate, reimagined',
            boxes: [
              {
                title: 'Is listing a property free?',
                sub_title:
                  'Yes, property listings are free, with premium options available for increased visibility.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I contact agents directly?',
                sub_title: 'Yes, connect with verified agents directly through the platform.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there tools for renters?',
                sub_title:
                  'Absolutely! Find flexible rental options and manage your lease agreements easily.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate Real Estate platform!',
            section_eight_subtitle: 'Find or sell your perfect property with ease today!',
          },
        };
        break;
      }

      case 'boat':
      case 'boats-yachts': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Boat',
            sectionone_subtitle:
              'Buy, sell, or rent boats with ease. A dedicated platform for boating enthusiasts to connect and explore their passion.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Boats',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'List Your Boat',
              sectionone_button_two_icon: 'pi pi-upload',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/create',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/boat/boat1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Boat Listings',
                sub_title:
                  'Browse a wide range of boats, from yachts to sailboats, all in one platform.',
                url: this.getAppConfig(app_link)?.app_url + '/listings',
                last_box: false,
              },
              {
                title: 'Rental Options',
                sub_title:
                  'Find short-term or long-term boat rental opportunities for your next trip.',
                url: this.getAppConfig(app_link)?.app_url + '/rentals',
                last_box: false,
              },
              {
                title: 'Seller Verification',
                sub_title: 'Trust verified sellers to ensure safe and reliable transactions.',
                url: this.getAppConfig(app_link)?.app_url + '/verification',
                last_box: false,
              },
              {
                title: 'Marine Accessories',
                sub_title:
                  'Explore and purchase accessories for your boat, including navigation tools and safety gear.',
                url: this.getAppConfig(app_link)?.app_url + '/accessories',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Looking for the perfect boat?',
            section_three_subtitle: 'Explore our listings and set sail on your next adventure!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/boat/boat2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Have a boat to sell or rent?',
            section_four_subtitle: 'List it today and connect with boating enthusiasts worldwide!',
            section_four_button_one: {
              section_four_button_one_title: 'List Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Boating made easy',
            boxes_one: [
              {
                title: 'Detailed Listings',
                sub_title: 'Comprehensive descriptions, images, and specifications for every boat.',
                url: this.getAppConfig(app_link)?.app_url + '/detailed-listings',
                icon: 'qr_code',
              },
              {
                title: 'Secure Transactions',
                sub_title: 'Safe and reliable payment methods for buyers and sellers.',
                url: this.getAppConfig(app_link)?.app_url + '/secure-transactions',
                icon: 'co_present',
              },
              {
                title: 'Boat Rentals',
                sub_title: 'Flexible rental options for leisure or business purposes.',
                url: this.getAppConfig(app_link)?.app_url + '/boat-rentals',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Watercraft Financing',
                sub_title: 'Access tools and advice for boat loans and insurance.',
                url: this.getAppConfig(app_link)?.app_url + '/financing',
                icon: 'contact_phone',
              },
              {
                title: 'Boat Maintenance Services',
                sub_title: 'Connect with experts for repair and maintenance.',
                url: this.getAppConfig(app_link)?.app_url + '/maintenance',
                icon: 'task_alt',
              },
              {
                title: 'Community Reviews',
                sub_title: 'Read reviews from other buyers and renters to make informed decisions.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/boat/boat3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Unlock exclusive listings, advanced search tools, and priority support for all your boating needs!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your boating journey starts here',
            boxes: [
              {
                title: 'Can I rent a boat directly through the platform?',
                sub_title:
                  'Yes, you can find and rent boats easily with our verified rental listings.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there financing options available?',
                sub_title: 'Yes, explore financing resources for purchasing your dream boat.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Is it free to list a boat?',
                sub_title:
                  'Yes, listing your boat is free, with premium options available for greater visibility.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate Boat platform!',
            section_eight_subtitle:
              'Connect with enthusiasts and discover the perfect watercraft today!',
          },
        };
        break;
      }
      case 'cars-wheels':
      case 'cars': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Car',
            sectionone_subtitle:
              'Buy, sell, or rent cars effortlessly. A trusted platform to explore vehicles for every need and budget.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Cars',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'List Your Car',
              sectionone_button_two_icon: 'pi pi-upload',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/create',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/car/car1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Comprehensive Car Listings',
                sub_title: 'Explore vehicles by brand, model, price, and location.',
                url: this.getAppConfig(app_link)?.app_url + '/listings',
                last_box: false,
              },
              {
                title: 'Car Rentals',
                sub_title: 'Find flexible rental options for short-term or long-term use.',
                url: this.getAppConfig(app_link)?.app_url + '/rentals',
                last_box: false,
              },
              {
                title: 'Verified Sellers',
                sub_title: 'Buy with confidence from verified sellers and dealerships.',
                url: this.getAppConfig(app_link)?.app_url + '/verified-sellers',
                last_box: false,
              },
              {
                title: 'Vehicle History Reports',
                sub_title: 'Access detailed history reports to make informed buying decisions.',
                url: this.getAppConfig(app_link)?.app_url + '/history-reports',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Looking for your next car?',
            section_three_subtitle: 'Find the perfect vehicle for your needs today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/car/car2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Have a car to sell?',
            section_four_subtitle: 'List it now and reach thousands of buyers!',
            section_four_button_one: {
              section_four_button_one_title: 'List Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your car journey, simplified',
            boxes_one: [
              {
                title: 'Advanced Search Filters',
                sub_title: 'Narrow down your search by price, make, model, and more.',
                url: this.getAppConfig(app_link)?.app_url + '/search',
                icon: 'qr_code',
              },
              {
                title: 'Safe Transactions',
                sub_title: 'Secure payment methods for worry-free car buying or selling.',
                url: this.getAppConfig(app_link)?.app_url + '/secure-transactions',
                icon: 'co_present',
              },
              {
                title: 'Rental Services',
                sub_title: 'Access flexible car rental options for personal or business use.',
                url: this.getAppConfig(app_link)?.app_url + '/rentals',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Trade-In Offers',
                sub_title: 'Explore trade-in options to upgrade your vehicle.',
                url: this.getAppConfig(app_link)?.app_url + '/trade-ins',
                icon: 'contact_phone',
              },
              {
                title: 'Dealer Connections',
                sub_title: 'Connect directly with trusted dealerships in your area.',
                url: this.getAppConfig(app_link)?.app_url + '/dealers',
                icon: 'task_alt',
              },
              {
                title: 'Inspection Services',
                sub_title: 'Book vehicle inspections to ensure quality before purchase.',
                url: this.getAppConfig(app_link)?.app_url + '/inspections',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/car/car3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Access exclusive car listings, premium rental options, and advanced support for all your vehicle needs!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your next car, just a click away',
            boxes: [
              {
                title: 'Can I rent cars through the platform?',
                sub_title: 'Yes, browse and book rental cars directly from trusted providers.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there trade-in options available?',
                sub_title: 'Yes, explore trade-in deals to upgrade your car conveniently.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Is it free to list my car for sale?',
                sub_title:
                  'Yes, listing your car is free, with premium options available for added visibility.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate Car platform!',
            section_eight_subtitle: 'Discover, buy, or rent your perfect vehicle today!',
          },
        };
        break;
      }
      case 'cooking': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Cooking',
            sectionone_subtitle:
              'Buy, sell, and share your love for cooking. A platform for foodies to explore recipes, cooking tools, and culinary experiences.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Recipes',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Share Your Recipe',
              sectionone_button_two_icon: 'pi pi-upload',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/create',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/cooking/cooking1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Recipe Marketplace',
                sub_title: 'Buy and sell unique recipes from top chefs and home cooks.',
                url: this.getAppConfig(app_link)?.app_url + '/recipes',
                last_box: false,
              },
              {
                title: 'Cooking Tools',
                sub_title: 'Explore a variety of kitchen gadgets, cookware, and appliances.',
                url: this.getAppConfig(app_link)?.app_url + '/tools',
                last_box: false,
              },
              {
                title: 'Culinary Classes',
                sub_title: 'Join live or on-demand cooking classes to learn new skills.',
                url: this.getAppConfig(app_link)?.app_url + '/classes',
                last_box: false,
              },
              {
                title: 'Food Community',
                sub_title: 'Share tips, ask questions, and connect with fellow foodies.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Looking for cooking inspiration?',
            section_three_subtitle: 'Explore recipes, tools, and experiences today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/cooking/cooking2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Have a recipe to share?',
            section_four_subtitle: 'Sell your creations and connect with food lovers worldwide!',
            section_four_button_one: {
              section_four_button_one_title: 'List Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Cooking made simple and fun',
            boxes_one: [
              {
                title: 'Recipe Library',
                sub_title: 'Browse thousands of recipes for every cuisine and occasion.',
                url: this.getAppConfig(app_link)?.app_url + '/library',
                icon: 'qr_code',
              },
              {
                title: 'Kitchen Tools Store',
                sub_title: 'Buy or sell high-quality cooking equipment and gadgets.',
                url: this.getAppConfig(app_link)?.app_url + '/store',
                icon: 'co_present',
              },
              {
                title: 'Live Cooking Classes',
                sub_title: 'Join real-time classes taught by professional chefs.',
                url: this.getAppConfig(app_link)?.app_url + '/live-classes',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Meal Planning Tools',
                sub_title: 'Organize weekly meal plans with recipes and shopping lists.',
                url: this.getAppConfig(app_link)?.app_url + '/meal-planning',
                icon: 'contact_phone',
              },
              {
                title: 'Food Photography',
                sub_title: 'Share and view stunning food photography from the community.',
                url: this.getAppConfig(app_link)?.app_url + '/photography',
                icon: 'task_alt',
              },
              {
                title: 'Dietary Preferences',
                sub_title:
                  'Find recipes tailored to your dietary needs, including vegan, keto, and gluten-free options.',
                url: this.getAppConfig(app_link)?.app_url + '/dietary-preferences',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/cooking/cooking3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Access exclusive recipes, advanced meal planning tools, and priority access to cooking classes!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your culinary journey starts here',
            boxes: [
              {
                title: 'Can I sell my own recipes?',
                sub_title:
                  'Yes, list your recipes for sale and connect with food lovers worldwide.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there live cooking classes available?',
                sub_title: 'Absolutely! Join live sessions or access recorded classes anytime.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I buy cooking tools directly?',
                sub_title: 'Yes, explore a wide range of kitchen gadgets and tools for purchase.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate Cooking platform!',
            section_eight_subtitle:
              'Share your passion for food and explore the world of cooking today!',
          },
        };
        break;
      }
      case 'market-space':
      case 'market_space': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'MarketSpace',
            sectionone_subtitle:
              'Buy and sell physical products with ease. From unique handmade items to everyday essentials, MarketSpace connects buyers and sellers in one convenient platform.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Products',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'List Your Products',
              sectionone_button_two_icon: 'pi pi-upload',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/create',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/marketspace/marketspace1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Product Listings',
                sub_title:
                  'Browse an extensive range of physical goods, from unique handmade crafts to branded items.',
                url: this.getAppConfig(app_link)?.app_url + '/listings',
                last_box: false,
              },
              {
                title: 'Secure Transactions',
                sub_title: 'Buy and sell with confidence using our secure payment system.',
                url: this.getAppConfig(app_link)?.app_url + '/secure-transactions',
                last_box: false,
              },
              {
                title: 'Verified Sellers',
                sub_title: 'Shop from trusted sellers to ensure quality and reliability.',
                url: this.getAppConfig(app_link)?.app_url + '/verified-sellers',
                last_box: false,
              },
              {
                title: 'Shipping Options',
                sub_title: 'Choose from multiple shipping options tailored to your convenience.',
                url: this.getAppConfig(app_link)?.app_url + '/shipping',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Looking for the perfect product?',
            section_three_subtitle: 'Explore thousands of listings on MarketSpace today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/marketspace/marketspace2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Have something to sell?',
            section_four_subtitle: 'List your products and reach buyers around the world!',
            section_four_button_one: {
              section_four_button_one_title: 'List Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Simplify your buying and selling journey',
            boxes_one: [
              {
                title: 'Customizable Listings',
                sub_title:
                  'Create detailed product listings with photos, descriptions, and pricing.',
                url: this.getAppConfig(app_link)?.app_url + '/customizable-listings',
                icon: 'qr_code',
              },
              {
                title: 'Buyer Reviews',
                sub_title: 'Read reviews from other buyers to ensure product quality.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'co_present',
              },
              {
                title: 'Category Filters',
                sub_title:
                  'Easily find items with advanced filtering options by category, price, and more.',
                url: this.getAppConfig(app_link)?.app_url + '/filters',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Shipping Tracking',
                sub_title: 'Track your order status from dispatch to delivery.',
                url: this.getAppConfig(app_link)?.app_url + '/tracking',
                icon: 'contact_phone',
              },
              {
                title: 'Bulk Order Discounts',
                sub_title: 'Access discounts on bulk purchases for select items.',
                url: this.getAppConfig(app_link)?.app_url + '/bulk-discounts',
                icon: 'task_alt',
              },
              {
                title: 'Return and Refund Policies',
                sub_title: 'Shop with confidence with clear return and refund options.',
                url: this.getAppConfig(app_link)?.app_url + '/returns',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/marketspace/marketspace3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Unlock exclusive seller tools, advanced analytics, and priority support to grow your business!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your marketplace, simplified',
            boxes: [
              {
                title: 'Is it free to list products on MarketSpace?',
                sub_title:
                  'Yes, listing products is free, with optional upgrades for increased visibility.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'How are transactions secured?',
                sub_title: 'All transactions are protected with encrypted payment processing.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I track my orders?',
                sub_title: 'Yes, you can track shipping progress directly through the platform.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate MarketSpace!',
            section_eight_subtitle: 'Join our growing community of buyers and sellers today!',
          },
        };
        break;
      }
      case 'taskly': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Taskly',
            sectionone_subtitle:
              'Find skilled professionals for every task. From home repairs to digital design, Taskly connects you with the right expert for the job.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Find Professionals',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join Taskly',
              sectionone_button_two_icon: 'pi pi-users',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/create',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/taskly/taskly1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Expert Profiles',
                sub_title:
                  'Browse detailed profiles of professionals, including skills, experience, and ratings.',
                url: this.getAppConfig(app_link)?.app_url + '/profiles',
                last_box: false,
              },
              {
                title: 'Task Bidding',
                sub_title: 'Post your task and receive competitive bids from qualified experts.',
                url: this.getAppConfig(app_link)?.app_url + '/bidding',
                last_box: false,
              },
              {
                title: 'Secure Payments',
                sub_title: 'Pay safely through the platform with guaranteed task completion.',
                url: this.getAppConfig(app_link)?.app_url + '/secure-payments',
                last_box: false,
              },
              {
                title: 'Task Tracking',
                sub_title: 'Monitor progress and communicate directly with your professional.',
                url: this.getAppConfig(app_link)?.app_url + '/tracking',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Need a task done?',
            section_three_subtitle: 'Find and hire skilled professionals on Taskly today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/taskly/taskly2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a professional?',
            section_four_subtitle: 'Join Taskly and start connecting with clients for free!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Hire experts for every need',
            boxes_one: [
              {
                title: 'Verified Professionals',
                sub_title: 'Work with trusted experts verified by our team.',
                url: this.getAppConfig(app_link)?.app_url + '/verified-professionals',
                icon: 'qr_code',
              },
              {
                title: 'Transparent Pricing',
                sub_title: 'Compare quotes and choose the best fit for your budget.',
                url: this.getAppConfig(app_link)?.app_url + '/pricing',
                icon: 'co_present',
              },
              {
                title: 'Review System',
                sub_title: 'Read and leave reviews to ensure quality and reliability.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'On-Demand Services',
                sub_title: 'Find professionals for urgent tasks with immediate availability.',
                url: this.getAppConfig(app_link)?.app_url + '/on-demand',
                icon: 'contact_phone',
              },
              {
                title: 'Customizable Tasks',
                sub_title: 'Post specific task details to find the most qualified professionals.',
                url: this.getAppConfig(app_link)?.app_url + '/custom-tasks',
                icon: 'task_alt',
              },
              {
                title: 'Dedicated Support',
                sub_title: 'Get help from our support team at every step of the process.',
                url: this.getAppConfig(app_link)?.app_url + '/support',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/taskly/taskly3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Access exclusive features, priority support, and premium listings to grow your freelance business or hire faster!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your tasks, handled',
            boxes: [
              {
                title: 'How do I hire a professional?',
                sub_title:
                  'Simply post your task, review bids, and hire the expert of your choice.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Is payment secure?',
                sub_title: 'Yes, all payments are processed securely through the platform.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I leave a review for completed tasks?',
                sub_title:
                  'Absolutely! Share your experience to help others make informed decisions.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate Taskly platform!',
            section_eight_subtitle:
              'Connect with skilled professionals and complete your tasks effortlessly today!',
          },
        };
        break;
      }
      case 'fitness': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Fitness',
            sectionone_subtitle:
              'Find fitness programs, personal trainers, and wellness solutions tailored to your needs. A platform for fitness enthusiasts to achieve their goals with expert guidance.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Programs',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join as a Trainer',
              sectionone_button_two_icon: 'pi pi-users',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/create',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/fitness/fitness1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Personalized Training Programs',
                sub_title: 'Access custom workout plans designed to meet your specific goals.',
                url: this.getAppConfig(app_link)?.app_url + '/training-programs',
                last_box: false,
              },
              {
                title: 'Certified Trainers',
                sub_title:
                  'Connect with verified personal trainers and fitness coaches for expert guidance.',
                url: this.getAppConfig(app_link)?.app_url + '/trainers',
                last_box: false,
              },
              {
                title: 'Fitness Gear Marketplace',
                sub_title: 'Buy and sell fitness equipment, apparel, and accessories.',
                url: this.getAppConfig(app_link)?.app_url + '/marketplace',
                last_box: false,
              },
              {
                title: 'Nutritional Plans',
                sub_title:
                  'Explore meal plans and diet recommendations tailored to your fitness objectives.',
                url: this.getAppConfig(app_link)?.app_url + '/nutrition',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready to reach your fitness goals?',
            section_three_subtitle:
              'Find programs, trainers, and tools to start your journey today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/fitness/fitness2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a fitness professional?',
            section_four_subtitle:
              'Join the platform and connect with clients to grow your business!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Transform your fitness journey',
            boxes_one: [
              {
                title: 'Custom Training Plans',
                sub_title: 'Tailored workout routines for every fitness level and goal.',
                url: this.getAppConfig(app_link)?.app_url + '/custom-plans',
                icon: 'qr_code',
              },
              {
                title: 'Virtual and In-Person Coaching',
                sub_title: 'Choose from online sessions or meet trainers near you.',
                url: this.getAppConfig(app_link)?.app_url + '/coaching',
                icon: 'co_present',
              },
              {
                title: 'Progress Tracking',
                sub_title: 'Monitor your fitness achievements with built-in progress trackers.',
                url: this.getAppConfig(app_link)?.app_url + '/tracking',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Verified Reviews',
                sub_title: 'Read reviews of trainers and programs from other users.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'contact_phone',
              },
              {
                title: 'Group Classes',
                sub_title: 'Join group fitness sessions for added motivation and camaraderie.',
                url: this.getAppConfig(app_link)?.app_url + '/group-classes',
                icon: 'task_alt',
              },
              {
                title: 'Fitness Gear Store',
                sub_title:
                  'Browse a wide selection of equipment and gear to enhance your workouts.',
                url: this.getAppConfig(app_link)?.app_url + '/gear-store',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/fitness/fitness3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Unlock exclusive programs, priority access to top trainers, and advanced progress tracking features!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your fitness, made achievable',
            boxes: [
              {
                title: 'Can I find both virtual and in-person trainers?',
                sub_title:
                  'Yes, you can connect with trainers offering online sessions or those in your area.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there fitness plans for beginners?',
                sub_title: 'Absolutely! We offer plans and trainers for all experience levels.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I sell my fitness gear on the platform?',
                sub_title: 'Yes, list your fitness equipment and reach buyers easily.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate Fitness platform!',
            section_eight_subtitle:
              'Join a thriving community of fitness enthusiasts and professionals today!',
          },
        };
        break;
      }
      case 'office-meeting':
      case 'coworking_space':
      case 'meetingpoint': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'MeetingPoint',
            sectionone_subtitle:
              'A professional space booking platform for meetings, conferences, and co-working spaces.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Find Spaces',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/spaces',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'List Your Space',
              sectionone_button_two_icon: 'pi pi-upload',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/list',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/meetingpoint/meetingpoint1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Meeting Spaces',
                sub_title: 'Find and book professional meeting rooms for your team or clients.',
                url: this.getAppConfig(app_link)?.app_url + '/meeting-spaces',
                last_box: false,
              },
              {
                title: 'Conference Rooms',
                sub_title: 'Book large conference rooms for events, seminars, or workshops.',
                url: this.getAppConfig(app_link)?.app_url + '/conference-rooms',
                last_box: false,
              },
              {
                title: 'Co-Working Spaces',
                sub_title: 'Discover flexible co-working spaces for individuals or teams.',
                url: this.getAppConfig(app_link)?.app_url + '/coworking-spaces',
                last_box: false,
              },
              {
                title: 'Event Hosting',
                sub_title: 'Host your events in professionally managed spaces with all amenities.',
                url: this.getAppConfig(app_link)?.app_url + '/event-hosting',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Need a professional space?',
            section_three_subtitle:
              'Find and book the perfect space for your next meeting or event!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/meetingpoint/meetingpoint2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Have a space to list?',
            section_four_subtitle:
              'List your space and connect with professionals looking for venues!',
            section_four_button_one: {
              section_four_button_one_title: 'List Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your professional space booking, simplified',
            boxes_one: [
              {
                title: 'Advanced Search Filters',
                sub_title: 'Filter spaces by location, capacity, amenities, and more.',
                url: this.getAppConfig(app_link)?.app_url + '/search',
                icon: 'qr_code',
              },
              {
                title: 'Real-Time Availability',
                sub_title: 'Check real-time availability and book instantly.',
                url: this.getAppConfig(app_link)?.app_url + '/availability',
                icon: 'co_present',
              },
              {
                title: 'Secure Payments',
                sub_title: 'Book with confidence using our secure payment system.',
                url: this.getAppConfig(app_link)?.app_url + '/payments',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Space Reviews',
                sub_title: 'Read reviews from other users to choose the best space.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'contact_phone',
              },
              {
                title: 'Customizable Bookings',
                sub_title: 'Tailor your booking to include catering, AV equipment, and more.',
                url: this.getAppConfig(app_link)?.app_url + '/custom-bookings',
                icon: 'task_alt',
              },
              {
                title: 'Dedicated Support',
                sub_title: 'Get assistance from our team for all your booking needs.',
                url: this.getAppConfig(app_link)?.app_url + '/support',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/meetingpoint/meetingpoint3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Unlock exclusive spaces, priority bookings, and advanced booking management tools!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your space, your success',
            boxes: [
              {
                title: 'Is it free to list my space?',
                sub_title: 'Yes, listing your space is free, with premium options available.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I book spaces for recurring meetings?',
                sub_title: 'Yes, you can book spaces for one-time or recurring events.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there tools for managing bookings?',
                sub_title: 'Yes, we provide tools to manage and track your bookings easily.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate space booking platform!',
            section_eight_subtitle: 'Find or list professional spaces and host your events today!',
          },
        };
        break;
      }
      case 'grocery':
      case 'grocery_to_home': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Grocery to Home',
            sectionone_subtitle:
              'A convenient online marketplace for fresh produce, daily essentials, and household goods.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Shop Now',
              sectionone_button_one_icon: 'pi pi-shopping-cart',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/shop',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Explore Deals',
              sectionone_button_two_icon: 'pi pi-tags',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/deals',
            },
          },
          section_two: {
            section_two_img_url: '/assets/images/grocery_to_home/grocery1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Fresh Produce',
                sub_title:
                  'Shop for fresh fruits, vegetables, and organic products delivered to your door.',
                url: this.getAppConfig(app_link)?.app_url + '/produce',
                last_box: false,
              },
              {
                title: 'Daily Essentials',
                sub_title:
                  'Find everything you need for your household, from groceries to cleaning supplies.',
                url: this.getAppConfig(app_link)?.app_url + '/essentials',
                last_box: false,
              },
              {
                title: 'Household Goods',
                sub_title: 'Discover a wide range of home products to make your life easier.',
                url: this.getAppConfig(app_link)?.app_url + '/home-goods',
                last_box: false,
              },
              {
                title: 'Fast Delivery',
                sub_title: 'Enjoy quick and reliable delivery services for all your orders.',
                url: this.getAppConfig(app_link)?.app_url + '/delivery',
                last_box: true,
              },
            ],
          },
          section_three: {
            section_three_title: 'Need groceries delivered?',
            section_three_subtitle: 'Shop fresh produce and essentials online today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },
          section_four: {
            section_four_img_url: '/assets/images/grocery_to_home/grocery2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a supplier?',
            section_four_subtitle: 'Join our platform and reach thousands of customers!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },
          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Fresh groceries and essentials at your fingertips!',
            boxes_one: [
              {
                title: 'Fresh Produce',
                sub_title: 'Shop for farm-fresh fruits and vegetables.',
                url: this.getAppConfig(app_link)?.app_url + '/produce',
                icon: 'pi pi-apple',
              },
              {
                title: 'Daily Essentials',
                sub_title: 'Find all your household needs in one place.',
                url: this.getAppConfig(app_link)?.app_url + '/essentials',
                icon: 'pi pi-home',
              },
              {
                title: 'Fast Delivery',
                sub_title: 'Get your orders delivered quickly and reliably.',
                url: this.getAppConfig(app_link)?.app_url + '/delivery',
                icon: 'pi pi-truck',
              },
            ],
            boxes_two: [
              {
                title: 'Exclusive Deals',
                sub_title: 'Save big with our daily discounts and offers.',
                url: this.getAppConfig(app_link)?.app_url + '/deals',
                icon: 'pi pi-tags',
              },
              {
                title: 'Subscription Plans',
                sub_title: 'Subscribe for regular deliveries of your favorite items.',
                url: this.getAppConfig(app_link)?.app_url + '/subscriptions',
                icon: 'pi pi-calendar',
              },
              {
                title: 'Customer Reviews',
                sub_title: 'Read reviews from other shoppers to make informed choices.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'pi pi-star',
              },
            ],
          },
          section_six: {
            section_six_img_url: '/assets/images/grocery_to_home/grocery3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Get exclusive discounts, priority delivery, and more with our premium plan!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },
          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your grocery shopping, simplified',
            boxes: [
              {
                title: 'Is delivery free?',
                sub_title: 'Delivery fees vary based on your location and order size.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I schedule deliveries?',
                sub_title: 'Yes, you can choose a delivery time that works for you.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there discounts for bulk orders?',
                sub_title: 'Yes, we offer discounts for larger orders and subscriptions.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate grocery platform!',
            section_eight_subtitle: 'Join thousands of shoppers and suppliers today!',
          },
        };
        break;
      }
      case 'fashion_and_style':
      case 'fashion': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Fashion and Style',
            sectionone_subtitle:
              'An online hub for fashion trends, clothing, and accessories for every style.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Shop Now',
              sectionone_button_one_icon: 'pi pi-shopping-cart',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/shop',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Explore Trends',
              sectionone_button_two_icon: 'pi pi-tags',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/trends',
            },
          },
          section_two: {
            section_two_img_url: '/assets/images/fashion_and_style/fashion1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Trendy Clothing',
                sub_title: 'Discover the latest fashion trends for every season.',
                url: this.getAppConfig(app_link)?.app_url + '/clothing',
                last_box: false,
              },
              {
                title: 'Stylish Accessories',
                sub_title: 'Find the perfect accessories to complete your look.',
                url: this.getAppConfig(app_link)?.app_url + '/accessories',
                last_box: false,
              },
              {
                title: 'Exclusive Brands',
                sub_title: 'Shop from top fashion brands and designers.',
                url: this.getAppConfig(app_link)?.app_url + '/brands',
                last_box: false,
              },
              {
                title: 'Personalized Styling',
                sub_title: 'Get personalized fashion recommendations based on your style.',
                url: this.getAppConfig(app_link)?.app_url + '/styling',
                last_box: true,
              },
            ],
          },
          section_three: {
            section_three_title: 'Ready to upgrade your wardrobe?',
            section_three_subtitle: 'Explore the latest fashion trends today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },
          section_four: {
            section_four_img_url: '/assets/images/fashion_and_style/fashion2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a designer?',
            section_four_subtitle: 'Join our platform and showcase your creations to the world!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },
          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Fashion that fits your style!',
            boxes_one: [
              {
                title: 'Trendy Collections',
                sub_title: 'Shop the latest fashion trends for every occasion.',
                url: this.getAppConfig(app_link)?.app_url + '/collections',
                icon: 'pi pi-tags',
              },
              {
                title: 'Exclusive Brands',
                sub_title: 'Discover unique designs from top fashion brands.',
                url: this.getAppConfig(app_link)?.app_url + '/brands',
                icon: 'pi pi-star',
              },
              {
                title: 'Personalized Styling',
                sub_title: 'Get fashion advice tailored to your preferences.',
                url: this.getAppConfig(app_link)?.app_url + '/styling',
                icon: 'pi pi-user',
              },
            ],
            boxes_two: [
              {
                title: 'Accessories Galore',
                sub_title: 'Find the perfect accessories to elevate your look.',
                url: this.getAppConfig(app_link)?.app_url + '/accessories',
                icon: 'pi pi-briefcase',
              },
              {
                title: 'Customer Reviews',
                sub_title: 'Read reviews from other fashion enthusiasts.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'pi pi-star',
              },
              {
                title: 'Fast Shipping',
                sub_title: 'Get your orders delivered quickly and securely.',
                url: this.getAppConfig(app_link)?.app_url + '/shipping',
                icon: 'pi pi-truck',
              },
            ],
          },
          section_six: {
            section_six_img_url: '/assets/images/fashion_and_style/fashion3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Unlock exclusive fashion deals, early access to sales, and more!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },
          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your fashion journey starts here',
            boxes: [
              {
                title: 'Can I return items?',
                sub_title: 'Yes, we offer a hassle-free return policy.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there discounts for first-time buyers?',
                sub_title: 'Yes, new customers enjoy exclusive discounts.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I track my order?',
                sub_title: 'Yes, you can track your order status in real-time.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate fashion platform!',
            section_eight_subtitle: 'Join fashion lovers and designers worldwide!',
          },
        };
        break;
      }
      case 'home_and_style':
      case 'home-design':
      case 'home_design': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Home and Style',
            sectionone_subtitle:
              'A marketplace for home décor, furniture, and interior design inspiration.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Shop Now',
              sectionone_button_one_icon: 'pi pi-shopping-cart',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/shop',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Explore Ideas',
              sectionone_button_two_icon: 'pi pi-lightbulb',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/ideas',
            },
          },
          section_two: {
            section_two_img_url: '/assets/images/home_and_style/home1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Home Décor',
                sub_title: 'Discover stylish décor items to transform your living space.',
                url: this.getAppConfig(app_link)?.app_url + '/decor',
                last_box: false,
              },
              {
                title: 'Furniture',
                sub_title: 'Find high-quality furniture for every room in your home.',
                url: this.getAppConfig(app_link)?.app_url + '/furniture',
                last_box: false,
              },
              {
                title: 'Interior Design',
                sub_title: 'Get inspired by professional interior design ideas.',
                url: this.getAppConfig(app_link)?.app_url + '/design',
                last_box: false,
              },
              {
                title: 'DIY Projects',
                sub_title: 'Explore DIY guides to personalize your home.',
                url: this.getAppConfig(app_link)?.app_url + '/diy',
                last_box: true,
              },
            ],
          },
          section_three: {
            section_three_title: 'Ready to redesign your home?',
            section_three_subtitle: 'Shop for home décor and furniture today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },
          section_four: {
            section_four_img_url: '/assets/images/home_and_style/home2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a designer?',
            section_four_subtitle: 'Join our platform and showcase your work to a global audience!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },
          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Transform your home with style!',
            boxes_one: [
              {
                title: 'Home Décor',
                sub_title: 'Shop for unique décor items to elevate your space.',
                url: this.getAppConfig(app_link)?.app_url + '/decor',
                icon: 'pi pi-home',
              },
              {
                title: 'Furniture',
                sub_title: 'Find stylish and functional furniture for your home.',
                url: this.getAppConfig(app_link)?.app_url + '/furniture',
                icon: 'pi pi-table',
              },
              {
                title: 'Interior Design',
                sub_title: 'Get inspired by professional design ideas.',
                url: this.getAppConfig(app_link)?.app_url + '/design',
                icon: 'pi pi-palette',
              },
            ],
            boxes_two: [
              {
                title: 'DIY Projects',
                sub_title: 'Explore step-by-step guides for home improvement.',
                url: this.getAppConfig(app_link)?.app_url + '/diy',
                icon: 'pi pi-wrench',
              },
              {
                title: 'Customer Reviews',
                sub_title: 'Read reviews from other homeowners.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'pi pi-star',
              },
              {
                title: 'Fast Shipping',
                sub_title: 'Get your orders delivered quickly and securely.',
                url: this.getAppConfig(app_link)?.app_url + '/shipping',
                icon: 'pi pi-truck',
              },
            ],
          },
          section_six: {
            section_six_img_url: '/assets/images/home_and_style/home3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle: 'Unlock exclusive discounts, design consultations, and more!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },
          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your home, your style',
            boxes: [
              {
                title: 'Can I return items?',
                sub_title: 'Yes, we offer a hassle-free return policy.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there discounts for bulk orders?',
                sub_title: 'Yes, we offer discounts for larger orders.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I book a design consultation?',
                sub_title: 'Yes, premium members can book consultations with designers.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate home platform!',
            section_eight_subtitle: 'Join homeowners and designers worldwide!',
          },
        };
        break;
      }
      case 'media-channels':
      case 'media_channels': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Media Channels',
            sectionone_subtitle:
              'A platform to explore and manage media services, channels, and advertising opportunities.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Channels',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/channels',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Advertise Now',
              sectionone_button_two_icon: 'pi pi-bullhorn',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/advertise',
            },
          },
          section_two: {
            section_two_img_url: '/assets/images/media_channels/media1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Channel Discovery',
                sub_title: 'Explore a wide range of media channels and services.',
                url: this.getAppConfig(app_link)?.app_url + '/discovery',
                last_box: false,
              },
              {
                title: 'Advertising Opportunities',
                sub_title: 'Promote your brand through targeted media campaigns.',
                url: this.getAppConfig(app_link)?.app_url + '/advertising',
                last_box: false,
              },
              {
                title: 'Content Management',
                sub_title: 'Manage and distribute your media content seamlessly.',
                url: this.getAppConfig(app_link)?.app_url + '/content',
                last_box: false,
              },
              {
                title: 'Analytics and Insights',
                sub_title: 'Track performance and optimize your media strategy.',
                url: this.getAppConfig(app_link)?.app_url + '/analytics',
                last_box: true,
              },
            ],
          },
          section_three: {
            section_three_title: 'Ready to grow your media presence?',
            section_three_subtitle: 'Explore channels and advertising opportunities today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },
          section_four: {
            section_four_img_url: '/assets/images/media_channels/media2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a media provider?',
            section_four_subtitle: 'Join our platform and reach a global audience!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },
          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your media journey starts here!',
            boxes_one: [
              {
                title: 'Channel Discovery',
                sub_title: 'Find media channels tailored to your interests.',
                url: this.getAppConfig(app_link)?.app_url + '/discovery',
                icon: 'pi pi-search',
              },
              {
                title: 'Advertising Tools',
                sub_title: 'Promote your brand with targeted campaigns.',
                url: this.getAppConfig(app_link)?.app_url + '/advertising',
                icon: 'pi pi-bullhorn',
              },
              {
                title: 'Content Management',
                sub_title: 'Easily manage and distribute your media content.',
                url: this.getAppConfig(app_link)?.app_url + '/content',
                icon: 'pi pi-file',
              },
            ],
            boxes_two: [
              {
                title: 'Analytics',
                sub_title: 'Track performance and optimize your strategy.',
                url: this.getAppConfig(app_link)?.app_url + '/analytics',
                icon: 'pi pi-chart-bar',
              },
              {
                title: 'Community Forums',
                sub_title: 'Connect with media professionals and share insights.',
                url: this.getAppConfig(app_link)?.app_url + '/forums',
                icon: 'pi pi-users',
              },
              {
                title: 'Premium Support',
                sub_title: 'Get priority support for your media needs.',
                url: this.getAppConfig(app_link)?.app_url + '/support',
                icon: 'pi pi-headset',
              },
            ],
          },
          section_six: {
            section_six_img_url: '/assets/images/media_channels/media3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle: 'Unlock advanced analytics, exclusive channels, and more!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },
          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your media, your way',
            boxes: [
              {
                title: 'Can I advertise on multiple channels?',
                sub_title: 'Yes, you can run campaigns across multiple channels.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there tools for content creators?',
                sub_title: 'Yes, we offer tools for content management and distribution.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I track campaign performance?',
                sub_title: 'Yes, our analytics tools provide detailed insights.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate media platform!',
            section_eight_subtitle: 'Join media professionals and advertisers worldwide!',
          },
        };
        break;
      }
      case 'taxi': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Taxi',
            sectionone_subtitle:
              'A convenient platform to book taxis and rideshares for safe and reliable transportation.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Book Now',
              sectionone_button_one_icon: 'pi pi-car',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/book',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Become a Driver',
              sectionone_button_two_icon: 'pi pi-user-plus',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/driver',
            },
          },
          section_two: {
            section_two_img_url: '/assets/images/taxi/taxi1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Quick Booking',
                sub_title: 'Book a ride in seconds with just a few taps.',
                url: this.getAppConfig(app_link)?.app_url + '/book',
                last_box: false,
              },
              {
                title: 'Safe Rides',
                sub_title: 'Travel with verified drivers for a safe journey.',
                url: this.getAppConfig(app_link)?.app_url + '/safety',
                last_box: false,
              },
              {
                title: 'Affordable Rates',
                sub_title: 'Enjoy competitive pricing for every ride.',
                url: this.getAppConfig(app_link)?.app_url + '/pricing',
                last_box: false,
              },
              {
                title: 'Ride Tracking',
                sub_title: 'Track your ride in real-time for peace of mind.',
                url: this.getAppConfig(app_link)?.app_url + '/tracking',
                last_box: true,
              },
            ],
          },
          section_three: {
            section_three_title: 'Need a ride?',
            section_three_subtitle: 'Book a taxi or rideshare today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },
          section_four: {
            section_four_img_url: '/assets/images/taxi/taxi2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a driver?',
            section_four_subtitle: 'Join our platform and start earning today!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },
          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your ride, your way!',
            boxes_one: [
              {
                title: 'Quick Booking',
                sub_title: 'Book rides instantly with our easy-to-use app.',
                url: this.getAppConfig(app_link)?.app_url + '/book',
                icon: 'pi pi-car',
              },
              {
                title: 'Safe Rides',
                sub_title: 'Travel with confidence using our verified drivers.',
                url: this.getAppConfig(app_link)?.app_url + '/safety',
                icon: 'pi pi-shield',
              },
              {
                title: 'Affordable Rates',
                sub_title: 'Enjoy competitive pricing for every trip.',
                url: this.getAppConfig(app_link)?.app_url + '/pricing',
                icon: 'pi pi-dollar',
              },
            ],
            boxes_two: [
              {
                title: 'Ride Tracking',
                sub_title: 'Track your ride in real-time for added security.',
                url: this.getAppConfig(app_link)?.app_url + '/tracking',
                icon: 'pi pi-map-marker',
              },
              {
                title: 'Driver Ratings',
                sub_title: 'Rate your driver and share your experience.',
                url: this.getAppConfig(app_link)?.app_url + '/ratings',
                icon: 'pi pi-star',
              },
              {
                title: '24/7 Support',
                sub_title: 'Get help anytime with our dedicated support team.',
                url: this.getAppConfig(app_link)?.app_url + '/support',
                icon: 'pi pi-headset',
              },
            ],
          },
          section_six: {
            section_six_img_url: '/assets/images/taxi/taxi3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle: 'Unlock exclusive perks, priority booking, and more!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },
          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your ride, simplified',
            boxes: [
              {
                title: 'How do I book a ride?',
                sub_title: 'Simply open the app, enter your destination, and book!',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are drivers verified?',
                sub_title: 'Yes, all drivers undergo a thorough verification process.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I pay with cash?',
                sub_title: 'Yes, we offer both cash and digital payment options.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate taxi platform!',
            section_eight_subtitle: 'Join riders and drivers worldwide!',
          },
        };
        break;
      }
      case 'passionist':
      case 'diy-handmade':
      case 'diy_handmade': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Passionist',
            sectionone_subtitle:
              'A platform to showcase and sell unique handmade crafts and DIY projects.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Crafts',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/crafts',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Sell Your Work',
              sectionone_button_two_icon: 'pi pi-upload',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/sell',
            },
          },
          section_two: {
            section_two_img_url: '/assets/images/passionist/passionist1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Handmade Crafts',
                sub_title: 'Discover unique handmade items crafted with love.',
                url: this.getAppConfig(app_link)?.app_url + '/crafts',
                last_box: false,
              },
              {
                title: 'DIY Projects',
                sub_title: 'Explore creative DIY ideas and tutorials.',
                url: this.getAppConfig(app_link)?.app_url + '/diy',
                last_box: false,
              },
              {
                title: 'Artisan Community',
                sub_title: 'Connect with passionate creators worldwide.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                last_box: false,
              },
              {
                title: 'Secure Transactions',
                sub_title: 'Buy and sell with confidence using our secure platform.',
                url: this.getAppConfig(app_link)?.app_url + '/transactions',
                last_box: true,
              },
            ],
          },
          section_three: {
            section_three_title: 'Looking for unique crafts?',
            section_three_subtitle: 'Explore handmade items and DIY projects today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },
          section_four: {
            section_four_img_url: '/assets/images/passionist/passionist2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a creator?',
            section_four_subtitle: 'Join our platform and showcase your work to the world!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },
          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your creativity, your platform!',
            boxes_one: [
              {
                title: 'Handmade Crafts',
                sub_title: 'Discover unique items crafted by passionate creators.',
                url: this.getAppConfig(app_link)?.app_url + '/crafts',
                icon: 'pi pi-palette',
              },
              {
                title: 'DIY Projects',
                sub_title: 'Explore step-by-step guides for creative projects.',
                url: this.getAppConfig(app_link)?.app_url + '/diy',
                icon: 'pi pi-wrench',
              },
              {
                title: 'Artisan Community',
                sub_title: 'Connect with like-minded creators and share ideas.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                icon: 'pi pi-users',
              },
            ],
            boxes_two: [
              {
                title: 'Secure Transactions',
                sub_title: 'Buy and sell with confidence using our secure platform.',
                url: this.getAppConfig(app_link)?.app_url + '/transactions',
                icon: 'pi pi-lock',
              },
              {
                title: 'Customer Reviews',
                sub_title: 'Read reviews from other buyers and sellers.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'pi pi-star',
              },
              {
                title: 'Fast Shipping',
                sub_title: 'Get your orders delivered quickly and securely.',
                url: this.getAppConfig(app_link)?.app_url + '/shipping',
                icon: 'pi pi-truck',
              },
            ],
          },
          section_six: {
            section_six_img_url: '/assets/images/passionist/passionist3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle: 'Unlock exclusive features, advanced tools, and more!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },
          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your creativity, your platform',
            boxes: [
              {
                title: 'Can I sell my crafts?',
                sub_title: 'Yes, you can list and sell your handmade items.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there fees for selling?',
                sub_title: 'No, listing your crafts is free with no commissions.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I track my orders?',
                sub_title: 'Yes, you can track your orders in real-time.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate creative platform!',
            section_eight_subtitle: 'Join artisans and craft lovers worldwide!',
          },
        };
        break;
      }
      case 'volunteer': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Volunteer',
            sectionone_subtitle:
              'Connect with opportunities to make a difference by joining volunteer initiatives in your community or worldwide.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Find Opportunities',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/opportunities',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join the Community',
              sectionone_button_two_icon: 'pi pi-users',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/community',
            },
          },
          section_two: {
            section_two_img_url: '/assets/images/volunteer/volunteer1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Local Opportunities',
                sub_title: 'Find volunteer opportunities in your local community.',
                url: this.getAppConfig(app_link)?.app_url + '/local',
                last_box: false,
              },
              {
                title: 'Global Initiatives',
                sub_title: 'Join impactful projects and initiatives worldwide.',
                url: this.getAppConfig(app_link)?.app_url + '/global',
                last_box: false,
              },
              {
                title: 'Skill-Based Volunteering',
                sub_title: 'Contribute your skills to meaningful causes.',
                url: this.getAppConfig(app_link)?.app_url + '/skills',
                last_box: false,
              },
              {
                title: 'Community Impact',
                sub_title: 'Track your impact and share your experiences.',
                url: this.getAppConfig(app_link)?.app_url + '/impact',
                last_box: true,
              },
            ],
          },
          section_three: {
            section_three_title: 'Ready to make a difference?',
            section_three_subtitle: 'Find volunteer opportunities today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },
          section_four: {
            section_four_img_url: '/assets/images/volunteer/volunteer2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you an organization?',
            section_four_subtitle: 'Join our platform and connect with passionate volunteers!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },
          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your journey to making a difference starts here!',
            boxes_one: [
              {
                title: 'Local Opportunities',
                sub_title: 'Find volunteer opportunities near you.',
                url: this.getAppConfig(app_link)?.app_url + '/local',
                icon: 'pi pi-map-marker',
              },
              {
                title: 'Global Initiatives',
                sub_title: 'Join impactful projects around the world.',
                url: this.getAppConfig(app_link)?.app_url + '/global',
                icon: 'pi pi-globe',
              },
              {
                title: 'Skill-Based Volunteering',
                sub_title: 'Use your skills to contribute to meaningful causes.',
                url: this.getAppConfig(app_link)?.app_url + '/skills',
                icon: 'pi pi-wrench',
              },
            ],
            boxes_two: [
              {
                title: 'Community Impact',
                sub_title: 'Track and share the impact of your volunteer work.',
                url: this.getAppConfig(app_link)?.app_url + '/impact',
                icon: 'pi pi-chart-bar',
              },
              {
                title: 'Volunteer Reviews',
                sub_title: 'Read reviews from other volunteers and organizations.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'pi pi-star',
              },
              {
                title: 'Dedicated Support',
                sub_title: 'Get help from our support team at every step.',
                url: this.getAppConfig(app_link)?.app_url + '/support',
                icon: 'pi pi-headset',
              },
            ],
          },
          section_six: {
            section_six_img_url: '/assets/images/volunteer/volunteer3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle: 'Unlock exclusive features, advanced tools, and more!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },
          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your journey to making a difference',
            boxes: [
              {
                title: 'Can I volunteer remotely?',
                sub_title: 'Yes, we offer both in-person and remote opportunities.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there opportunities for students?',
                sub_title: 'Yes, we have opportunities tailored for students.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can organizations post opportunities?',
                sub_title: 'Yes, organizations can join and post volunteer opportunities.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate volunteer platform!',
            section_eight_subtitle: 'Join volunteers and organizations worldwide!',
          },
        };
        break;
      }
      case 'agriculture-livestock':
      case 'agriculture_livestock': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Agriculture / Livestock',
            sectionone_subtitle:
              'A marketplace connecting agricultural producers and livestock businesses for trading, resources, and collaboration.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Marketplace',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/marketplace',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join as a Producer',
              sectionone_button_two_icon: 'pi pi-user-plus',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/producer',
            },
          },
          section_two: {
            section_two_img_url: '/assets/images/agriculture_livestock/agriculture1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Agricultural Products',
                sub_title: 'Buy and sell crops, seeds, and farming equipment.',
                url: this.getAppConfig(app_link)?.app_url + '/products',
                last_box: false,
              },
              {
                title: 'Livestock Trading',
                sub_title: 'Connect with buyers and sellers of livestock.',
                url: this.getAppConfig(app_link)?.app_url + '/livestock',
                last_box: false,
              },
              {
                title: 'Resource Sharing',
                sub_title: 'Access farming resources, tools, and knowledge.',
                url: this.getAppConfig(app_link)?.app_url + '/resources',
                last_box: false,
              },
              {
                title: 'Collaborative Networks',
                sub_title: 'Join a community of agricultural professionals.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                last_box: true,
              },
            ],
          },
          section_three: {
            section_three_title: 'Ready to grow your agricultural business?',
            section_three_subtitle: 'Explore our marketplace and connect with producers today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },
          section_four: {
            section_four_img_url: '/assets/images/agriculture_livestock/agriculture2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a producer?',
            section_four_subtitle: 'Join our platform and reach a global audience!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },
          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your agricultural journey starts here!',
            boxes_one: [
              {
                title: 'Agricultural Products',
                sub_title: 'Buy and sell crops, seeds, and farming equipment.',
                url: this.getAppConfig(app_link)?.app_url + '/products',
                icon: 'pi pi-leaf',
              },
              {
                title: 'Livestock Trading',
                sub_title: 'Connect with buyers and sellers of livestock.',
                url: this.getAppConfig(app_link)?.app_url + '/livestock',
                icon: 'pi pi-paw',
              },
              {
                title: 'Resource Sharing',
                sub_title: 'Access farming resources, tools, and knowledge.',
                url: this.getAppConfig(app_link)?.app_url + '/resources',
                icon: 'pi pi-book',
              },
            ],
            boxes_two: [
              {
                title: 'Collaborative Networks',
                sub_title: 'Join a community of agricultural professionals.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                icon: 'pi pi-users',
              },
              {
                title: 'Market Insights',
                sub_title: 'Stay updated with the latest market trends and prices.',
                url: this.getAppConfig(app_link)?.app_url + '/insights',
                icon: 'pi pi-chart-line',
              },
              {
                title: 'Dedicated Support',
                sub_title: 'Get help from our support team at every step.',
                url: this.getAppConfig(app_link)?.app_url + '/support',
                icon: 'pi pi-headset',
              },
            ],
          },
          section_six: {
            section_six_img_url: '/assets/images/agriculture_livestock/agriculture3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle: 'Unlock exclusive features, advanced tools, and more!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },
          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your agricultural journey starts here',
            boxes: [
              {
                title: 'Can I sell my crops?',
                sub_title: 'Yes, you can list and sell your agricultural products.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there fees for selling?',
                sub_title: 'No, listing your products is free with no commissions.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I track my orders?',
                sub_title: 'Yes, you can track your orders in real-time.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate agricultural platform!',
            section_eight_subtitle: 'Join farmers and producers worldwide!',
          },
        };
        break;
      }
      case 'kids':
      case 'kids_space': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Kids',
            sectionone_subtitle:
              "A dedicated marketplace for kids' products, services, and activities to make childhood special.",
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Products',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/products',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join as a Seller',
              sectionone_button_two_icon: 'pi pi-user-plus',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/seller',
            },
          },
          section_two: {
            section_two_img_url: '/assets/images/kids/kids1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Toys and Games',
                sub_title: 'Discover fun and educational toys for kids of all ages.',
                url: this.getAppConfig(app_link)?.app_url + '/toys',
                last_box: false,
              },
              {
                title: 'Clothing and Accessories',
                sub_title: 'Find stylish and comfortable clothing for kids.',
                url: this.getAppConfig(app_link)?.app_url + '/clothing',
                last_box: false,
              },
              {
                title: 'Activities and Classes',
                sub_title: 'Explore fun activities and classes for kids.',
                url: this.getAppConfig(app_link)?.app_url + '/activities',
                last_box: false,
              },
              {
                title: 'Parenting Resources',
                sub_title: 'Access resources and tips for raising happy kids.',
                url: this.getAppConfig(app_link)?.app_url + '/resources',
                last_box: true,
              },
            ],
          },
          section_three: {
            section_three_title: 'Looking for the best for your kids?',
            section_three_subtitle: 'Explore our marketplace and find everything you need!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },
          section_four: {
            section_four_img_url: '/assets/images/kids/kids2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a seller?',
            section_four_subtitle: 'Join our platform and reach parents worldwide!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },
          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Everything for your kids in one place!',
            boxes_one: [
              {
                title: 'Toys and Games',
                sub_title: 'Find fun and educational toys for kids.',
                url: this.getAppConfig(app_link)?.app_url + '/toys',
                icon: 'pi pi-gamepad',
              },
              {
                title: 'Clothing and Accessories',
                sub_title: 'Shop stylish and comfortable clothing for kids.',
                url: this.getAppConfig(app_link)?.app_url + '/clothing',
                icon: 'pi pi-tags',
              },
              {
                title: 'Activities and Classes',
                sub_title: 'Explore fun activities and classes for kids.',
                url: this.getAppConfig(app_link)?.app_url + '/activities',
                icon: 'pi pi-calendar',
              },
            ],
            boxes_two: [
              {
                title: 'Parenting Resources',
                sub_title: 'Access resources and tips for raising happy kids.',
                url: this.getAppConfig(app_link)?.app_url + '/resources',
                icon: 'pi pi-book',
              },
              {
                title: 'Customer Reviews',
                sub_title: 'Read reviews from other parents and caregivers.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'pi pi-star',
              },
              {
                title: 'Fast Shipping',
                sub_title: 'Get your orders delivered quickly and securely.',
                url: this.getAppConfig(app_link)?.app_url + '/shipping',
                icon: 'pi pi-truck',
              },
            ],
          },
          section_six: {
            section_six_img_url: '/assets/images/kids/kids3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle: 'Unlock exclusive discounts, early access to sales, and more!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },
          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your kids, our priority',
            boxes: [
              {
                title: 'Can I return items?',
                sub_title: 'Yes, we offer a hassle-free return policy.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there discounts for bulk orders?',
                sub_title: 'Yes, we offer discounts for larger orders.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I track my order?',
                sub_title: 'Yes, you can track your order status in real-time.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate kids platform!',
            section_eight_subtitle: 'Join parents and sellers worldwide!',
          },
        };
        break;
      }

      case 'one-love':
      case 'dating': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Dating',
            sectionone_subtitle:
              'A platform to foster connections and celebrate inclusivity, diversity, and love.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Find Matches',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/matches',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join the Community',
              sectionone_button_two_icon: 'pi pi-users',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/community',
            },
          },
          section_two: {
            section_two_img_url: '/assets/images/dating/dating1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Matchmaking',
                sub_title: 'Find compatible matches based on your preferences.',
                url: this.getAppConfig(app_link)?.app_url + '/matchmaking',
                last_box: false,
              },
              {
                title: 'Inclusive Community',
                sub_title: 'Celebrate diversity and inclusivity in our community.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                last_box: false,
              },
              {
                title: 'Safe and Secure',
                sub_title: 'Enjoy a safe and secure dating experience.',
                url: this.getAppConfig(app_link)?.app_url + '/safety',
                last_box: false,
              },
              {
                title: 'Events and Meetups',
                sub_title: 'Join events and meetups to connect with others.',
                url: this.getAppConfig(app_link)?.app_url + '/events',
                last_box: true,
              },
            ],
          },
          section_three: {
            section_three_title: 'Ready to find your match?',
            section_three_subtitle: 'Join our platform and start connecting today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },
          section_four: {
            section_four_img_url: '/assets/images/dating/dating2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you looking for love?',
            section_four_subtitle: 'Join our platform and find your perfect match!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },
          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your journey to love starts here!',
            boxes_one: [
              {
                title: 'Matchmaking',
                sub_title: 'Find compatible matches based on your preferences.',
                url: this.getAppConfig(app_link)?.app_url + '/matchmaking',
                icon: 'pi pi-heart',
              },
              {
                title: 'Inclusive Community',
                sub_title: 'Celebrate diversity and inclusivity in our community.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                icon: 'pi pi-users',
              },
              {
                title: 'Safe and Secure',
                sub_title: 'Enjoy a safe and secure dating experience.',
                url: this.getAppConfig(app_link)?.app_url + '/safety',
                icon: 'pi pi-shield',
              },
            ],
            boxes_two: [
              {
                title: 'Events and Meetups',
                sub_title: 'Join events and meetups to connect with others.',
                url: this.getAppConfig(app_link)?.app_url + '/events',
                icon: 'pi pi-calendar',
              },
              {
                title: 'Profile Customization',
                sub_title: 'Create a profile that reflects your personality.',
                url: this.getAppConfig(app_link)?.app_url + '/profile',
                icon: 'pi pi-user',
              },
              {
                title: 'Dedicated Support',
                sub_title: 'Get help from our support team at every step.',
                url: this.getAppConfig(app_link)?.app_url + '/support',
                icon: 'pi pi-headset',
              },
            ],
          },
          section_six: {
            section_six_img_url: '/assets/images/dating/dating3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle: 'Unlock exclusive features, advanced matchmaking, and more!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },
          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your journey to love starts here',
            boxes: [
              {
                title: 'Is it free to join?',
                sub_title: 'Yes, creating a profile and browsing matches is free.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I customize my profile?',
                sub_title: 'Yes, you can create a profile that reflects your personality.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there events to meet others?',
                sub_title: 'Yes, we host events and meetups for our community.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate dating platform!',
            section_eight_subtitle: 'Join singles and couples worldwide!',
          },
        };
        break;
      }
      case 'team_up': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Team Up',
            sectionone_subtitle:
              'A collaborative platform for teams to connect, share ideas, and achieve goals together.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Join a Team',
              sectionone_button_one_icon: 'pi pi-users',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/teams',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Create a Team',
              sectionone_button_two_icon: 'pi pi-plus',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/create',
            },
          },
          section_two: {
            section_two_img_url: '/assets/images/team_up/team1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Team Collaboration',
                sub_title: 'Connect with your team and share ideas seamlessly.',
                url: this.getAppConfig(app_link)?.app_url + '/collaboration',
                last_box: false,
              },
              {
                title: 'Project Management',
                sub_title: 'Organize and manage projects with ease.',
                url: this.getAppConfig(app_link)?.app_url + '/projects',
                last_box: false,
              },
              {
                title: 'Goal Tracking',
                sub_title: 'Set and track goals to achieve success together.',
                url: this.getAppConfig(app_link)?.app_url + '/goals',
                last_box: false,
              },
              {
                title: 'Community Support',
                sub_title: 'Join a community of like-minded teams and professionals.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                last_box: true,
              },
            ],
          },
          section_three: {
            section_three_title: 'Ready to achieve more together?',
            section_three_subtitle: 'Join or create a team today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },
          section_four: {
            section_four_img_url: '/assets/images/team_up/team2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a team leader?',
            section_four_subtitle: 'Create a team and start collaborating today!',
            section_four_button_one: {
              section_four_button_one_title: 'Create Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },
          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your team, your success!',
            boxes_one: [
              {
                title: 'Team Collaboration',
                sub_title: 'Connect with your team and share ideas seamlessly.',
                url: this.getAppConfig(app_link)?.app_url + '/collaboration',
                icon: 'pi pi-users',
              },
              {
                title: 'Project Management',
                sub_title: 'Organize and manage projects with ease.',
                url: this.getAppConfig(app_link)?.app_url + '/projects',
                icon: 'pi pi-briefcase',
              },
              {
                title: 'Goal Tracking',
                sub_title: 'Set and track goals to achieve success together.',
                url: this.getAppConfig(app_link)?.app_url + '/goals',
                icon: 'pi pi-chart-bar',
              },
            ],
            boxes_two: [
              {
                title: 'Community Support',
                sub_title: 'Join a community of like-minded teams and professionals.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                icon: 'pi pi-comments',
              },
              {
                title: 'File Sharing',
                sub_title: 'Share files and documents with your team securely.',
                url: this.getAppConfig(app_link)?.app_url + '/files',
                icon: 'pi pi-file',
              },
              {
                title: 'Dedicated Support',
                sub_title: 'Get help from our support team at every step.',
                url: this.getAppConfig(app_link)?.app_url + '/support',
                icon: 'pi pi-headset',
              },
            ],
          },
          section_six: {
            section_six_img_url: '/assets/images/team_up/team3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle: 'Unlock exclusive features, advanced tools, and more!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },
          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your team, your success',
            boxes: [
              {
                title: 'Can I create multiple teams?',
                sub_title: 'Yes, you can create and manage multiple teams.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Is file sharing secure?',
                sub_title: 'Yes, all file sharing is encrypted and secure.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I track team progress?',
                sub_title: 'Yes, you can track progress with our goal-tracking tools.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate team platform!',
            section_eight_subtitle: 'Join teams and professionals worldwide!',
          },
        };
        break;
      }
      case 'mindset_loading':
      case 'mindset': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Mindset Loading',
            sectionone_subtitle:
              'A platform offering tools, resources, and guidance for personal development and positive thinking.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Resources',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/resources',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join the Community',
              sectionone_button_two_icon: 'pi pi-users',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/community',
            },
          },
          section_two: {
            section_two_img_url: '/assets/images/mindset_loading/mindset1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Personal Development',
                sub_title: 'Access tools and resources for self-improvement.',
                url: this.getAppConfig(app_link)?.app_url + '/development',
                last_box: false,
              },
              {
                title: 'Positive Thinking',
                sub_title: 'Learn techniques to cultivate a positive mindset.',
                url: this.getAppConfig(app_link)?.app_url + '/positive',
                last_box: false,
              },
              {
                title: 'Guided Meditations',
                sub_title: 'Practice mindfulness with guided meditations.',
                url: this.getAppConfig(app_link)?.app_url + '/meditations',
                last_box: false,
              },
              {
                title: 'Community Support',
                sub_title: 'Join a community of like-minded individuals.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                last_box: true,
              },
            ],
          },
          section_three: {
            section_three_title: 'Ready to transform your mindset?',
            section_three_subtitle: 'Explore our resources and start your journey today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },
          section_four: {
            section_four_img_url: '/assets/images/mindset_loading/mindset2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a coach or mentor?',
            section_four_subtitle: 'Join our platform and share your expertise!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },
          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your journey to a better mindset starts here!',
            boxes_one: [
              {
                title: 'Personal Development',
                sub_title: 'Access tools and resources for self-improvement.',
                url: this.getAppConfig(app_link)?.app_url + '/development',
                icon: 'pi pi-user',
              },
              {
                title: 'Positive Thinking',
                sub_title: 'Learn techniques to cultivate a positive mindset.',
                url: this.getAppConfig(app_link)?.app_url + '/positive',
                icon: 'pi pi-smile',
              },
              {
                title: 'Guided Meditations',
                sub_title: 'Practice mindfulness with guided meditations.',
                url: this.getAppConfig(app_link)?.app_url + '/meditations',
                icon: 'pi pi-heart',
              },
            ],
            boxes_two: [
              {
                title: 'Community Support',
                sub_title: 'Join a community of like-minded individuals.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                icon: 'pi pi-users',
              },
              {
                title: 'Expert Guidance',
                sub_title: 'Get advice from coaches and mentors.',
                url: this.getAppConfig(app_link)?.app_url + '/guidance',
                icon: 'pi pi-star',
              },
              {
                title: 'Dedicated Support',
                sub_title: 'Get help from our support team at every step.',
                url: this.getAppConfig(app_link)?.app_url + '/support',
                icon: 'pi pi-headset',
              },
            ],
          },
          section_six: {
            section_six_img_url: '/assets/images/mindset_loading/mindset3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle: 'Unlock exclusive features, advanced tools, and more!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },
          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your journey to a better mindset',
            boxes: [
              {
                title: 'Is it free to join?',
                sub_title: 'Yes, creating a profile and accessing resources is free.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I connect with coaches?',
                sub_title: 'Yes, you can connect with coaches and mentors.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there guided meditations?',
                sub_title: 'Yes, we offer guided meditations for mindfulness.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate mindset platform!',
            section_eight_subtitle: 'Join individuals and coaches worldwide!',
          },
        };
        break;
      }
      case 'tech':
      case 'technical_madness': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Technical Madness',
            sectionone_subtitle:
              'A marketplace for the latest technology products, gadgets, and services.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Products',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/products',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join as a Seller',
              sectionone_button_two_icon: 'pi pi-user-plus',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/seller',
            },
          },
          section_two: {
            section_two_img_url: '/assets/images/technical_madness/tech1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Latest Gadgets',
                sub_title: 'Discover the newest tech gadgets and devices.',
                url: this.getAppConfig(app_link)?.app_url + '/gadgets',
                last_box: false,
              },
              {
                title: 'Tech Services',
                sub_title: 'Find professional tech services and support.',
                url: this.getAppConfig(app_link)?.app_url + '/services',
                last_box: false,
              },
              {
                title: 'Software Solutions',
                sub_title: 'Explore innovative software and applications.',
                url: this.getAppConfig(app_link)?.app_url + '/software',
                last_box: false,
              },
              {
                title: 'Community Forums',
                sub_title: 'Join discussions and share knowledge with tech enthusiasts.',
                url: this.getAppConfig(app_link)?.app_url + '/forums',
                last_box: true,
              },
            ],
          },
          section_three: {
            section_three_title: 'Ready to explore the latest tech?',
            section_three_subtitle: 'Discover gadgets, services, and more today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },
          section_four: {
            section_four_img_url: '/assets/images/technical_madness/tech2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a tech seller?',
            section_four_subtitle: 'Join our platform and reach tech enthusiasts worldwide!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },
          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your tech journey starts here!',
            boxes_one: [
              {
                title: 'Latest Gadgets',
                sub_title: 'Discover the newest tech gadgets and devices.',
                url: this.getAppConfig(app_link)?.app_url + '/gadgets',
                icon: 'pi pi-mobile',
              },
              {
                title: 'Tech Services',
                sub_title: 'Find professional tech services and support.',
                url: this.getAppConfig(app_link)?.app_url + '/services',
                icon: 'pi pi-wrench',
              },
              {
                title: 'Software Solutions',
                sub_title: 'Explore innovative software and applications.',
                url: this.getAppConfig(app_link)?.app_url + '/software',
                icon: 'pi pi-code',
              },
            ],
            boxes_two: [
              {
                title: 'Community Forums',
                sub_title: 'Join discussions and share knowledge with tech enthusiasts.',
                url: this.getAppConfig(app_link)?.app_url + '/forums',
                icon: 'pi pi-comments',
              },
              {
                title: 'Customer Reviews',
                sub_title: 'Read reviews from other tech enthusiasts.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'pi pi-star',
              },
              {
                title: 'Fast Shipping',
                sub_title: 'Get your orders delivered quickly and securely.',
                url: this.getAppConfig(app_link)?.app_url + '/shipping',
                icon: 'pi pi-truck',
              },
            ],
          },
          section_six: {
            section_six_img_url: '/assets/images/technical_madness/tech3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle: 'Unlock exclusive discounts, early access to products, and more!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },
          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your tech journey starts here',
            boxes: [
              {
                title: 'Can I return products?',
                sub_title: 'Yes, we offer a hassle-free return policy.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there discounts for bulk orders?',
                sub_title: 'Yes, we offer discounts for larger orders.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I track my order?',
                sub_title: 'Yes, you can track your order status in real-time.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate tech platform!',
            section_eight_subtitle: 'Join tech enthusiasts and sellers worldwide!',
          },
        };
        break;
      }
      case 'crypto-blockchain':
      case 'crypto_blockchain':
      case 'crypto_nitro': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Crypto Nitro',
            sectionone_subtitle:
              'A hub for blockchain enthusiasts to trade, learn, and explore cryptocurrency innovations.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Crypto',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/crypto',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join the Community',
              sectionone_button_two_icon: 'pi pi-users',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/community',
            },
          },
          section_two: {
            section_two_img_url: '/assets/images/crypto_nitro/crypto1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Crypto Trading',
                sub_title: 'Trade cryptocurrencies with ease and security.',
                url: this.getAppConfig(app_link)?.app_url + '/trading',
                last_box: false,
              },
              {
                title: 'Blockchain Education',
                sub_title: 'Learn about blockchain technology and cryptocurrencies.',
                url: this.getAppConfig(app_link)?.app_url + '/education',
                last_box: false,
              },
              {
                title: 'Crypto Innovations',
                sub_title: 'Explore the latest innovations in the crypto space.',
                url: this.getAppConfig(app_link)?.app_url + '/innovations',
                last_box: false,
              },
              {
                title: 'Community Forums',
                sub_title: 'Join discussions and share knowledge with crypto enthusiasts.',
                url: this.getAppConfig(app_link)?.app_url + '/forums',
                last_box: true,
              },
            ],
          },
          section_three: {
            section_three_title: 'Ready to dive into crypto?',
            section_three_subtitle: 'Explore cryptocurrencies and blockchain today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },
          section_four: {
            section_four_img_url: '/assets/images/crypto_nitro/crypto2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a crypto expert?',
            section_four_subtitle: 'Join our platform and share your knowledge!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },
          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your crypto journey starts here!',
            boxes_one: [
              {
                title: 'Crypto Trading',
                sub_title: 'Trade cryptocurrencies with ease and security.',
                url: this.getAppConfig(app_link)?.app_url + '/trading',
                icon: 'pi pi-dollar',
              },
              {
                title: 'Blockchain Education',
                sub_title: 'Learn about blockchain technology and cryptocurrencies.',
                url: this.getAppConfig(app_link)?.app_url + '/education',
                icon: 'pi pi-book',
              },
              {
                title: 'Crypto Innovations',
                sub_title: 'Explore the latest innovations in the crypto space.',
                url: this.getAppConfig(app_link)?.app_url + '/innovations',
                icon: 'pi pi-lightbulb',
              },
            ],
            boxes_two: [
              {
                title: 'Community Forums',
                sub_title: 'Join discussions and share knowledge with crypto enthusiasts.',
                url: this.getAppConfig(app_link)?.app_url + '/forums',
                icon: 'pi pi-comments',
              },
              {
                title: 'Market Insights',
                sub_title: 'Stay updated with the latest crypto market trends.',
                url: this.getAppConfig(app_link)?.app_url + '/insights',
                icon: 'pi pi-chart-line',
              },
              {
                title: 'Dedicated Support',
                sub_title: 'Get help from our support team at every step.',
                url: this.getAppConfig(app_link)?.app_url + '/support',
                icon: 'pi pi-headset',
              },
            ],
          },
          section_six: {
            section_six_img_url: '/assets/images/crypto_nitro/crypto3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle: 'Unlock exclusive features, advanced tools, and more!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },
          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your crypto journey starts here',
            boxes: [
              {
                title: 'Is it free to join?',
                sub_title: 'Yes, creating a profile and exploring crypto is free.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I trade cryptocurrencies?',
                sub_title: 'Yes, you can trade cryptocurrencies securely on our platform.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there educational resources?',
                sub_title: 'Yes, we offer resources to learn about blockchain and crypto.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate crypto platform!',
            section_eight_subtitle: 'Join crypto enthusiasts and experts worldwide!',
          },
        };
        break;
      }
      case 'b-to-b':
      case 'b_to_b':
      case 'bsquare': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'B Square',
            sectionone_subtitle:
              'A platform for businesses to connect, trade, and collaborate seamlessly.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Businesses',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/catalog',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join the Network',
              sectionone_button_two_icon: 'pi pi-users',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/join',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/bsquare/bsquare1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Business Networking',
                sub_title: 'Connect with other businesses to explore collaboration opportunities.',
                url: this.getAppConfig(app_link)?.app_url + '/networking',
                last_box: false,
              },
              {
                title: 'Trade Marketplace',
                sub_title: 'Buy and sell products or services within a trusted business network.',
                url: this.getAppConfig(app_link)?.app_url + '/marketplace',
                last_box: false,
              },
              {
                title: 'Collaboration Tools',
                sub_title: 'Access tools and resources to streamline business partnerships.',
                url: this.getAppConfig(app_link)?.app_url + '/tools',
                last_box: false,
              },
              {
                title: 'Global Reach',
                sub_title: 'Expand your business connections across regions and industries.',
                url: this.getAppConfig(app_link)?.app_url + '/global',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready to grow your business?',
            section_three_subtitle:
              'Join B Square and start connecting with other businesses today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/bsquare/bsquare2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a business owner?',
            section_four_subtitle: 'Join B Square and expand your network with no fees!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Connect, trade, and collaborate with ease!',
            boxes_one: [
              {
                title: 'Business Directories',
                sub_title: 'Find and connect with businesses in your industry or region.',
                url: this.getAppConfig(app_link)?.app_url + '/directories',
                icon: 'qr_code',
              },
              {
                title: 'Secure Transactions',
                sub_title: 'Trade with confidence using our secure payment system.',
                url: this.getAppConfig(app_link)?.app_url + '/secure-transactions',
                icon: 'co_present',
              },
              {
                title: 'Collaboration Spaces',
                sub_title: 'Create shared workspaces for seamless collaboration.',
                url: this.getAppConfig(app_link)?.app_url + '/collaboration',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Business Insights',
                sub_title: 'Access analytics and insights to grow your business.',
                url: this.getAppConfig(app_link)?.app_url + '/insights',
                icon: 'contact_phone',
              },
              {
                title: 'Global Networking',
                sub_title: 'Connect with businesses worldwide to expand your reach.',
                url: this.getAppConfig(app_link)?.app_url + '/global-networking',
                icon: 'task_alt',
              },
              {
                title: 'Resource Library',
                sub_title: 'Access tools, templates, and guides to support your business.',
                url: this.getAppConfig(app_link)?.app_url + '/resources',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/bsquare/bsquare3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Unlock exclusive networking opportunities, advanced analytics, and priority support!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your business, our network',
            boxes: [
              {
                title: 'Is it free to join B Square?',
                sub_title: 'Yes, joining and connecting with businesses is completely free.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I sell products on B Square?',
                sub_title: 'Yes, you can list and sell products or services to other businesses.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there tools for collaboration?',
                sub_title: 'Yes, we provide tools to help businesses collaborate effectively.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate business network!',
            section_eight_subtitle: 'Join thousands of businesses growing together today!',
          },
        };
        break;
      }

      case 'independentcitizens':
      case 'digital-nomads':
      case 'digital_nomads': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Independent Citizens',
            sectionone_subtitle:
              'A platform designed for remote workers to find tools, resources, and a global community.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Resources',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/resources',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join the Community',
              sectionone_button_two_icon: 'pi pi-users',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/join',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/independentcitizens/independentcitizens1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Remote Work Tools',
                sub_title: 'Access tools and software to enhance your remote work experience.',
                url: this.getAppConfig(app_link)?.app_url + '/tools',
                last_box: false,
              },
              {
                title: 'Global Community',
                sub_title: 'Connect with remote workers worldwide to share tips and experiences.',
                url: this.getAppConfig(app_link)?.app_url + '/community',
                last_box: false,
              },
              {
                title: 'Resource Library',
                sub_title: 'Find guides, templates, and resources to boost your productivity.',
                url: this.getAppConfig(app_link)?.app_url + '/resources',
                last_box: false,
              },
              {
                title: 'Job Opportunities',
                sub_title: 'Discover remote job listings tailored to your skills.',
                url: this.getAppConfig(app_link)?.app_url + '/jobs',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready to thrive as a remote worker?',
            section_three_subtitle:
              'Join Independent Citizens and access the tools you need today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/independentcitizens/independentcitizens2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a remote worker?',
            section_four_subtitle: 'Join our community and access exclusive resources for free!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Empower your remote work journey',
            boxes_one: [
              {
                title: 'Tool Recommendations',
                sub_title:
                  'Discover the best tools for remote work, from productivity apps to communication platforms.',
                url: this.getAppConfig(app_link)?.app_url + '/tools',
                icon: 'qr_code',
              },
              {
                title: 'Community Forums',
                sub_title: 'Engage with other remote workers in discussions and forums.',
                url: this.getAppConfig(app_link)?.app_url + '/forums',
                icon: 'co_present',
              },
              {
                title: 'Skill Development',
                sub_title: 'Access courses and workshops to enhance your remote work skills.',
                url: this.getAppConfig(app_link)?.app_url + '/skills',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Remote Job Board',
                sub_title: 'Find job opportunities tailored for remote workers.',
                url: this.getAppConfig(app_link)?.app_url + '/jobs',
                icon: 'contact_phone',
              },
              {
                title: 'Productivity Tips',
                sub_title: 'Learn tips and tricks to stay productive while working remotely.',
                url: this.getAppConfig(app_link)?.app_url + '/productivity',
                icon: 'task_alt',
              },
              {
                title: 'Networking Events',
                sub_title: 'Join virtual events to connect with other remote professionals.',
                url: this.getAppConfig(app_link)?.app_url + '/events',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/independentcitizens/independentcitizens3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Unlock exclusive resources, advanced job search tools, and priority access to events!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your remote work, simplified',
            boxes: [
              {
                title: 'Is it free to join Independent Citizens?',
                sub_title: 'Yes, joining and accessing basic resources is completely free.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I find remote jobs here?',
                sub_title: 'Yes, we have a dedicated job board for remote work opportunities.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there networking opportunities?',
                sub_title: 'Yes, we host virtual events and forums for remote workers to connect.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate remote work community!',
            section_eight_subtitle: 'Join thousands of remote workers thriving together today!',
          },
        };
        break;
      }

      case 'gaming':
      case 'gamerworld': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Gamer World',
            sectionone_subtitle:
              'A community hub for gamers to discover games, tournaments, and connect with other players.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Games',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/games',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join Tournaments',
              sectionone_button_two_icon: 'pi pi-trophy',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/tournaments',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/gamerworld/gamerworld1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Game Discovery',
                sub_title: 'Explore a vast library of games across genres and platforms.',
                url: this.getAppConfig(app_link)?.app_url + '/games',
                last_box: false,
              },
              {
                title: 'Tournaments',
                sub_title: 'Compete in online tournaments and win exciting prizes.',
                url: this.getAppConfig(app_link)?.app_url + '/tournaments',
                last_box: false,
              },
              {
                title: 'Community Forums',
                sub_title: 'Connect with other gamers, share tips, and discuss strategies.',
                url: this.getAppConfig(app_link)?.app_url + '/forums',
                last_box: false,
              },
              {
                title: 'Gaming Gear',
                sub_title: 'Shop for the latest gaming accessories and equipment.',
                url: this.getAppConfig(app_link)?.app_url + '/gear',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready to level up your gaming experience?',
            section_three_subtitle: 'Join Gamer World and start exploring today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/gamerworld/gamerworld2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a gamer?',
            section_four_subtitle: 'Join Gamer World and connect with a global gaming community!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your gaming hub, reimagined',
            boxes_one: [
              {
                title: 'Game Reviews',
                sub_title: 'Read and write reviews for your favorite games.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'qr_code',
              },
              {
                title: 'Leaderboards',
                sub_title: 'Track your rankings and compete with other players.',
                url: this.getAppConfig(app_link)?.app_url + '/leaderboards',
                icon: 'co_present',
              },
              {
                title: 'Live Streams',
                sub_title: 'Watch live streams of top gamers and tournaments.',
                url: this.getAppConfig(app_link)?.app_url + '/streams',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Gaming News',
                sub_title: 'Stay updated with the latest news and updates in the gaming world.',
                url: this.getAppConfig(app_link)?.app_url + '/news',
                icon: 'contact_phone',
              },
              {
                title: 'Custom Profiles',
                sub_title: 'Create a personalized profile to showcase your gaming achievements.',
                url: this.getAppConfig(app_link)?.app_url + '/profiles',
                icon: 'task_alt',
              },
              {
                title: 'Team Finder',
                sub_title: 'Find teammates for multiplayer games and tournaments.',
                url: this.getAppConfig(app_link)?.app_url + '/teams',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/gamerworld/gamerworld3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Unlock exclusive tournaments, advanced leaderboards, and premium gaming content!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your gaming, your world',
            boxes: [
              {
                title: 'Can I join tournaments for free?',
                sub_title:
                  'Yes, many tournaments are free to join, with premium options available.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there tools for team building?',
                sub_title: 'Yes, use our team finder to connect with other players.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I watch live streams?',
                sub_title: 'Yes, watch live streams of top gamers and tournaments.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate gaming community!',
            section_eight_subtitle:
              'Join millions of gamers and elevate your gaming experience today!',
          },
        };
        break;
      }

      case 'erotic':
      case 'sensualshudder': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Sensual Shudder',
            sectionone_subtitle:
              'A tasteful and discreet platform for exploring sensual products and services.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Products',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/products',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join the Community',
              sectionone_button_two_icon: 'pi pi-users',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/join',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/sensualshudder/sensualshudder1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Discreet Shopping',
                sub_title: 'Explore sensual products with complete privacy and discretion.',
                url: this.getAppConfig(app_link)?.app_url + '/shopping',
                last_box: false,
              },
              {
                title: 'Expert Advice',
                sub_title: 'Access advice and tips from experts in sensual wellness.',
                url: this.getAppConfig(app_link)?.app_url + '/advice',
                last_box: false,
              },
              {
                title: 'Community Forums',
                sub_title:
                  'Connect with like-minded individuals in a safe and respectful environment.',
                url: this.getAppConfig(app_link)?.app_url + '/forums',
                last_box: false,
              },
              {
                title: 'Premium Services',
                sub_title: 'Discover exclusive services tailored to your sensual needs.',
                url: this.getAppConfig(app_link)?.app_url + '/services',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready to explore sensuality?',
            section_three_subtitle:
              'Join Sensual Shudder and discover a world of discreet pleasure.',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/sensualshudder/sensualshudder2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you a provider?',
            section_four_subtitle: 'Join our platform and offer your services with discretion.',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your sensual journey, redefined',
            boxes_one: [
              {
                title: 'Product Reviews',
                sub_title: 'Read and write reviews for sensual products and services.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'qr_code',
              },
              {
                title: 'Discreet Packaging',
                sub_title: 'All products are shipped with discreet packaging for privacy.',
                url: this.getAppConfig(app_link)?.app_url + '/packaging',
                icon: 'co_present',
              },
              {
                title: 'Expert Consultations',
                sub_title: 'Book private consultations with sensual wellness experts.',
                url: this.getAppConfig(app_link)?.app_url + '/consultations',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Community Events',
                sub_title: 'Join virtual events and workshops to explore sensuality.',
                url: this.getAppConfig(app_link)?.app_url + '/events',
                icon: 'contact_phone',
              },
              {
                title: 'Premium Memberships',
                sub_title: 'Access exclusive content and services with a premium membership.',
                url: this.getAppConfig(app_link)?.app_url + '/premium',
                icon: 'task_alt',
              },
              {
                title: 'Safe Environment',
                sub_title: 'Enjoy a safe and respectful space for exploring sensuality.',
                url: this.getAppConfig(app_link)?.app_url + '/safety',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/sensualshudder/sensualshudder3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Unlock exclusive content, advanced consultations, and priority access to events!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your sensuality, your privacy',
            boxes: [
              {
                title: 'Is my privacy guaranteed?',
                sub_title: 'Yes, all transactions and interactions are completely discreet.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I join as a service provider?',
                sub_title: 'Yes, join our platform and offer your services with discretion.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there community events?',
                sub_title: 'Yes, we host virtual events and workshops for our members.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate sensual community!',
            section_eight_subtitle: 'Join a respectful and discreet platform today!',
          },
        };
        break;
      }

      case 'roommatefinder':
      case 'roommate': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Roommate Finder',
            sectionone_subtitle:
              'A platform to connect individuals looking for shared living arrangements.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Find Roommates',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/roommates',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'List Your Space',
              sectionone_button_two_icon: 'pi pi-upload',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/list',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/roommatefinder/roommatefinder1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Roommate Matching',
                sub_title: 'Find compatible roommates based on lifestyle and preferences.',
                url: this.getAppConfig(app_link)?.app_url + '/matching',
                last_box: false,
              },
              {
                title: 'Shared Spaces',
                sub_title: 'List or find shared living spaces in your area.',
                url: this.getAppConfig(app_link)?.app_url + '/spaces',
                last_box: false,
              },
              {
                title: 'Secure Communication',
                sub_title: 'Communicate safely with potential roommates through our platform.',
                url: this.getAppConfig(app_link)?.app_url + '/communication',
                last_box: false,
              },
              {
                title: 'Rental Agreements',
                sub_title: 'Access tools to create and manage rental agreements.',
                url: this.getAppConfig(app_link)?.app_url + '/agreements',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Looking for a roommate?',
            section_three_subtitle: 'Find the perfect match and start sharing your space today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/roommatefinder/roommatefinder2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Have a space to share?',
            section_four_subtitle: 'List your space and find the perfect roommate!',
            section_four_button_one: {
              section_four_button_one_title: 'List Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your roommate search, simplified',
            boxes_one: [
              {
                title: 'Compatibility Matching',
                sub_title: 'Find roommates based on lifestyle, habits, and preferences.',
                url: this.getAppConfig(app_link)?.app_url + '/matching',
                icon: 'qr_code',
              },
              {
                title: 'Space Listings',
                sub_title: 'Browse or list shared living spaces with detailed descriptions.',
                url: this.getAppConfig(app_link)?.app_url + '/listings',
                icon: 'co_present',
              },
              {
                title: 'Secure Messaging',
                sub_title: 'Communicate with potential roommates securely through our platform.',
                url: this.getAppConfig(app_link)?.app_url + '/messaging',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Rental Tools',
                sub_title: 'Access tools to manage rental agreements and payments.',
                url: this.getAppConfig(app_link)?.app_url + '/rental-tools',
                icon: 'contact_phone',
              },
              {
                title: 'Roommate Reviews',
                sub_title: 'Read reviews from previous roommates to make informed decisions.',
                url: this.getAppConfig(app_link)?.app_url + '/reviews',
                icon: 'task_alt',
              },
              {
                title: 'Community Forums',
                sub_title: 'Join discussions and get tips on shared living arrangements.',
                url: this.getAppConfig(app_link)?.app_url + '/forums',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/roommatefinder/roommatefinder3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Unlock advanced matching tools, priority listings, and exclusive rental agreements!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your roommate, your space',
            boxes: [
              {
                title: 'Is it free to find a roommate?',
                sub_title: 'Yes, finding and connecting with roommates is completely free.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I list my space for free?',
                sub_title: 'Yes, listing your space is free, with premium options available.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there tools for rental agreements?',
                sub_title: 'Yes, we provide tools to create and manage rental agreements.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate roommate community!',
            section_eight_subtitle:
              'Find your perfect roommate and start sharing your space today!',
          },
        };
        break;
      }

      case 'aviation': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Aviation',
            sectionone_subtitle:
              'A specialized marketplace for aviation enthusiasts, professionals, and services.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Explore Services',
              sectionone_button_one_icon: 'pi pi-search',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/services',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join the Community',
              sectionone_button_two_icon: 'pi pi-users',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/join',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/aviation/aviation1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Aircraft Sales',
                sub_title: 'Buy or sell aircraft with trusted aviation professionals.',
                url: this.getAppConfig(app_link)?.app_url + '/sales',
                last_box: false,
              },
              {
                title: 'Flight Training',
                sub_title: 'Find flight schools and training programs to become a pilot.',
                url: this.getAppConfig(app_link)?.app_url + '/training',
                last_box: false,
              },
              {
                title: 'Maintenance Services',
                sub_title: 'Access maintenance and repair services for your aircraft.',
                url: this.getAppConfig(app_link)?.app_url + '/maintenance',
                last_box: false,
              },
              {
                title: 'Aviation Jobs',
                sub_title: 'Discover job opportunities in the aviation industry.',
                url: this.getAppConfig(app_link)?.app_url + '/jobs',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready to take flight?',
            section_three_subtitle: 'Explore aviation services and join the community today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/aviation/aviation2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Are you an aviation professional?',
            section_four_subtitle:
              'Join our platform and connect with aviation enthusiasts worldwide!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your aviation hub, reimagined',
            boxes_one: [
              {
                title: 'Aircraft Listings',
                sub_title: 'Browse or list aircraft for sale with detailed specifications.',
                url: this.getAppConfig(app_link)?.app_url + '/listings',
                icon: 'qr_code',
              },
              {
                title: 'Flight Schools',
                sub_title: 'Find accredited flight schools and training programs.',
                url: this.getAppConfig(app_link)?.app_url + '/schools',
                icon: 'co_present',
              },
              {
                title: 'Maintenance Providers',
                sub_title: 'Connect with trusted maintenance and repair services.',
                url: this.getAppConfig(app_link)?.app_url + '/maintenance',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Job Board',
                sub_title: 'Discover job opportunities in the aviation industry.',
                url: this.getAppConfig(app_link)?.app_url + '/jobs',
                icon: 'contact_phone',
              },
              {
                title: 'Community Forums',
                sub_title: 'Join discussions and share knowledge with aviation enthusiasts.',
                url: this.getAppConfig(app_link)?.app_url + '/forums',
                icon: 'task_alt',
              },
              {
                title: 'Aviation Events',
                sub_title: 'Attend aviation events, airshows, and conferences.',
                url: this.getAppConfig(app_link)?.app_url + '/events',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/aviation/aviation3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Unlock exclusive listings, advanced job search tools, and priority access to events!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your aviation, your passion',
            boxes: [
              {
                title: 'Can I list my aircraft for sale?',
                sub_title: 'Yes, you can list your aircraft for sale with detailed specifications.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there job opportunities for pilots?',
                sub_title: 'Yes, we have a dedicated job board for aviation professionals.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I attend aviation events?',
                sub_title: 'Yes, we provide access to aviation events and airshows.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate aviation community!',
            section_eight_subtitle: 'Join aviation enthusiasts and professionals worldwide today!',
          },
        };
        break;
      }

      case 'goalmanager':
      case 'goal_manager': {
        app_info = {
          app_url: this.getAppConfig(app_link)?.app_url,
          app_brand_name: this.getAppConfig(app_link)?.app_brand_name,
          community_application_type: this.getAppConfig(app_link)?.community_application_type,
          section_one: {
            sectionone_title: 'Goal Manager',
            sectionone_subtitle:
              'Plan, track, and achieve your goals. A platform designed to help you stay focused and turn your ambitions into accomplishments.',
            sectionone_button_one: {
              sectionone_button_one_title: 'Start Planning',
              sectionone_button_one_icon: 'pi pi-check',
              sectionone_button_one_iconpos: 'left',
              sectionone_button_one_severity: 'contrast',
              sectionone_button_one_link: this.getAppConfig(app_link)?.app_url + '/goals',
            },
            sectionone_button_two: {
              sectionone_button_two_title: 'Join the Community',
              sectionone_button_two_icon: 'pi pi-users',
              sectionone_button_two_iconpos: 'left',
              sectionone_button_two_severity: 'contrast',
              sectionone_button_two_link: this.getAppConfig(app_link)?.app_url + '/community',
            },
          },

          section_two: {
            section_two_img_url: '/assets/images/goalmanager/goalmanager1.jpg',
            section_two_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            boxes: [
              {
                title: 'Goal Tracking',
                sub_title: 'Set and track your goals with visual progress indicators.',
                url: this.getAppConfig(app_link)?.app_url + '/tracking',
                last_box: false,
              },
              {
                title: 'Task Management',
                sub_title: 'Break down your goals into manageable tasks and stay organized.',
                url: this.getAppConfig(app_link)?.app_url + '/tasks',
                last_box: false,
              },
              {
                title: 'Community Support',
                sub_title: 'Connect with others to stay motivated and share your progress.',
                url: this.getAppConfig(app_link)?.app_url + '/support',
                last_box: false,
              },
              {
                title: 'Achievement Badges',
                sub_title: 'Earn badges and rewards for completing your goals.',
                url: this.getAppConfig(app_link)?.app_url + '/badges',
                last_box: true,
              },
            ],
          },

          section_three: {
            section_three_title: 'Ready to achieve your goals?',
            section_three_subtitle: 'Start planning and tracking your goals today!',
            section_three_button_one: {
              section_three_button_one_title: 'Get Started',
              section_three_button_one_icon: 'pi pi-arrow-right',
              section_three_button_one_iconpos: 'right',
              section_three_button_one_link: '/cognitive-erp/create',
            },
          },

          section_four: {
            section_four_img_url: '/assets/images/goalmanager/goalmanager2.jpg',
            section_four_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_four_title: 'Need motivation?',
            section_four_subtitle: 'Join the Goal Manager community and stay inspired!',
            section_four_button_one: {
              section_four_button_one_title: 'Join Now',
              section_four_button_one_icon: 'pi pi-arrow-right',
              section_four_button_one_iconpos: 'right',
              section_four_button_one_link: '/cognitive-erp/create',
            },
          },

          section_five: {
            section_five_title: 'Explore the features',
            section_five_subtitle: 'Your goals, simplified',
            boxes_one: [
              {
                title: 'Goal Setting',
                sub_title: 'Define your goals and set deadlines to stay on track.',
                url: this.getAppConfig(app_link)?.app_url + '/goals',
                icon: 'qr_code',
              },
              {
                title: 'Progress Visualization',
                sub_title: 'Track your progress with charts and milestones.',
                url: this.getAppConfig(app_link)?.app_url + '/progress',
                icon: 'co_present',
              },
              {
                title: 'Collaborative Goals',
                sub_title: 'Work with others to achieve shared objectives.',
                url: this.getAppConfig(app_link)?.app_url + '/collaboration',
                icon: 'event',
              },
            ],
            boxes_two: [
              {
                title: 'Daily Reminders',
                sub_title: 'Stay on track with reminders and notifications.',
                url: this.getAppConfig(app_link)?.app_url + '/reminders',
                icon: 'contact_phone',
              },
              {
                title: 'Achievement Badges',
                sub_title: 'Celebrate milestones with rewards and badges.',
                url: this.getAppConfig(app_link)?.app_url + '/badges',
                icon: 'task_alt',
              },
              {
                title: 'Resource Library',
                sub_title: 'Access expert tips, guides, and templates for goal planning.',
                url: this.getAppConfig(app_link)?.app_url + '/resources',
                icon: 'cloud_download',
              },
            ],
          },

          section_six: {
            section_six_img_url: '/assets/images/goalmanager/goalmanager3.jpg',
            section_six_img_alt: this.getAppConfig(app_link)?.app_brand_name,
            section_six_title: 'Premium Subscription',
            section_six_subtitle:
              'Unlock advanced analytics, exclusive resources, and one-on-one coaching to fast-track your success!',
            section_six_button_one: {
              section_six_button_one_title: 'Subscribe Now',
              section_six_button_one_icon: 'pi pi-plus',
              section_six_button_one_iconpos: 'left',
              section_six_button_one_link: '/personal-subscription',
            },
          },

          section_seven: {
            section_seven_title: 'Frequently Asked Questions (FAQ)',
            section_seven_subtitle: 'Your goals, within reach',
            boxes: [
              {
                title: 'Can I share my goals with others?',
                sub_title:
                  'Yes, you can collaborate on shared goals and invite friends or colleagues.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Are there tools for habit tracking?',
                sub_title: 'Absolutely! Build and track daily habits to stay consistent.',
                url: '/academy',
                icon: 'live_help',
              },
              {
                title: 'Can I adjust my goals mid-way?',
                sub_title: 'Of course! Goals are flexible and can be updated as you progress.',
                url: '/academy',
                icon: 'live_help',
              },
            ],
          },
          section_eight: {
            section_eight_title: 'Help us build the ultimate Goal Manager platform!',
            section_eight_subtitle:
              'Achieve more with tailored tools and a supportive community today!',
          },
        };
        break;
      }
    }

    return app_info;
  }

  getAppHeaderDatas(page: any) {
    let app_info: any = null;
    let app_helper_common_data: any = null;
    switch (page) {
      case 'homepage': {
        app_info = {
          header_main_title: 'Join to',
          header_type_title: [
            this.translateService.instant('Communities'),
            this.translateService.instant('Marketplaces'),
            this.translateService.instant('Businesses'),
            this.translateService.instant('Networks'),
            this.translateService.instant('People'),
            this.translateService.instant('OffConOn'),
          ],
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: '/assets/images/app_meta/home_page.jpg',
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: this.getAppConfig('botanix')?.app_brand_name,
              icon: this.getAppConfig('botanix')?.icon,
              url: this.getAppConfig('botanix')?.app_url,
            },
            {
              label: this.getAppConfig('pets')?.app_brand_name,
              icon: this.getAppConfig('pets')?.icon,
              url: this.getAppConfig('pets')?.app_url,
            },
            {
              label: this.getAppConfig('market-space')?.app_brand_name,
              icon: this.getAppConfig('market-space')?.icon,
              url: this.getAppConfig('market-space')?.app_url,
            },
            {
              label: this.getAppConfig('bucket_list')?.app_brand_name,
              icon: this.getAppConfig('bucket_list')?.icon,
              url: this.getAppConfig('bucket_list')?.app_url,
            },
            {
              label: this.getAppConfig('second_chance')?.app_brand_name,
              icon: this.getAppConfig('second_chance')?.icon,
              url: this.getAppConfig('second_chance')?.app_url,
            },
            {
              label: 'More',
              icon: 'more_horiz',
              url: '/apps',
            },
          ],
        };
        break;
      }

      case 'botanix':
      case 'garden': {
        app_helper_common_data = this.getAppData('botanix');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('garden')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('garden')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('garden')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'language_exchange':
      case 'speakup': {
        app_helper_common_data = this.getAppData('language_exchange');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('language_exchange')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('language_exchange')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('language_exchange')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'business_networking':
      case 'bizconnect': {
        app_helper_common_data = this.getAppData('business_networking');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('business_networking')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('business_networking')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('business_networking')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'doctor_finder':
      case 'medspot': {
        app_helper_common_data = this.getAppData('doctor_finder');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('doctor_finder')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('doctor_finder')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('doctor_finder')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'beautywave':
      case 'wellness_beauty': {
        app_helper_common_data = this.getAppData('wellness_beauty');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('wellness_beauty')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('wellness_beauty')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('business_networking')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'pets':
      case 'petnest': {
        app_helper_common_data = this.getAppData('pets');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('pets')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('pets')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('pets')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'skilllink':
      case 'skill_link':
      case 'skill-link': {
        app_helper_common_data = this.getAppData('skilllink');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('skilllink')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('skilllink')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('skilllink')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'begreen':
      case 'be_green':
      case 'be-green': {
        app_helper_common_data = this.getAppData('begreen');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('begreen')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('begreen')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('begreen')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'secondchance':
      case 'second-chance':
      case 'second_chance': {
        app_helper_common_data = this.getAppData('secondchance');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('secondchance')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('secondchance')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('secondchance')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'events':
      case 'eventix': {
        app_helper_common_data = this.getAppData('events');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('events')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('events')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('events')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'renting':
      case 'rentspace': {
        app_helper_common_data = this.getAppData('renting');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('renting')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('renting')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('renting')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'booking':
      case 'scheduly': {
        app_helper_common_data = this.getAppData('booking');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('booking')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('booking')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('booking')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'job':
      case 'careerpath':
      case 'career-path': {
        app_helper_common_data = this.getAppData('job');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('job')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('job')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('job')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'digitalproducts':
      case 'digital_downloads':
      case 'digital_product':
      case 'pixelmarket': {
        app_helper_common_data = this.getAppData('pixelmarket');
        app_info = {
          header_main_title: 'Instant download',
          header_sub_title: 'Digital product resource',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('pixelmarket')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              url:
                app_helper_common_data?.app_url + app_helper_common_data?.downloads?.products?.url,
              icon: 'cloud_download',
              label: app_helper_common_data?.downloads?.products?.name,
            },
            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('pixelmarket')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'bucket-list':
      case 'bucket_list': {
        app_helper_common_data = this.getAppData('bucket_list');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('bucket_list')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('bucket_list')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('bucket_list')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'travelbuddies':
      case 'travel-buddies':
      case 'travel_buddies': {
        app_helper_common_data = this.getAppData('travelbuddies');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('travelbuddies')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('travelbuddies')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('travelbuddies')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'collectors': {
        app_helper_common_data = this.getAppData('collectors');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('collectors')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('collectors')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('collectors')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'realestate':
      case 'real-estate':
      case 'real_estate': {
        app_helper_common_data = this.getAppData('realestate');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('realestate')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('realestate')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('realestate')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'boat':
      case 'boats-yachts': {
        app_helper_common_data = this.getAppData('boat');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('boat')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('boat')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('boat')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'cars-wheels':
      case 'cars': {
        app_helper_common_data = this.getAppData('cars');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('cars')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('cars')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('cars')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'cooking': {
        app_helper_common_data = this.getAppData('cooking');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('cooking')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('cooking')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('cooking')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'market-space':
      case 'market_space':
      case 'physical_product': {
        app_helper_common_data = this.getAppData('market_space');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('market_space')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('market_space')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('market_space')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'taskly': {
        app_helper_common_data = this.getAppData('taskly');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('taskly')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('taskly')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('taskly')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'fitness': {
        app_helper_common_data = this.getAppData('fitness');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('fitness')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('fitness')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('fitness')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'office-meeting':
      case 'coworking_space':
      case 'meetingpoint': {
        app_helper_common_data = this.getAppData('meetingpoint');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('meetingpoint')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('meetingpoint')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('meetingpoint')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'grocery':
      case 'grocery_to_home': {
        app_helper_common_data = this.getAppData('grocery');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('grocery')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('grocery')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('grocery')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'fashion_and_style':
      case 'fashion': {
        app_helper_common_data = this.getAppData('fashion');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('fashion')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('fashion')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('fashion')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'home_and_style':
      case 'home-design':
      case 'home_design': {
        app_helper_common_data = this.getAppData('home_design');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('home_design')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('home_design')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('home_design')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'media-channels':
      case 'media_channels': {
        app_helper_common_data = this.getAppData('media_channels');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('media_channels')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('media_channels')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('media_channels')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'taxi': {
        app_helper_common_data = this.getAppData('taxi');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('taxi')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('taxi')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('taxi')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'passionist':
      case 'diy-handmade':
      case 'diy_handmade': {
        app_helper_common_data = this.getAppData('passionist');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('passionist')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('passionist')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('passionist')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'volunteer': {
        app_helper_common_data = this.getAppData('volunteer');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('volunteer')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('volunteer')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('volunteer')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'agriculture-livestock':
      case 'agriculture_livestock': {
        app_helper_common_data = this.getAppData('agriculture_livestock');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('agriculture_livestock')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('agriculture_livestock')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('agriculture_livestock')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'kids':
      case 'kids_space': {
        app_helper_common_data = this.getAppData('kids');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('kids')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('kids')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('kids')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'one-love':
      case 'dating': {
        app_helper_common_data = this.getAppData('dating');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('dating')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('dating')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('dating')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'team_up': {
        app_helper_common_data = this.getAppData('team_up');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('team_up')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('team_up')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('team_up')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'mindset_loading':
      case 'mindset': {
        app_helper_common_data = this.getAppData('mindset');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('mindset')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('mindset')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('mindset')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'tech':
      case 'technical_madness': {
        app_helper_common_data = this.getAppData('tech');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('tech')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('tech')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('tech')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'crypto-blockchain':
      case 'crypto_blockchain':
      case 'crypto_nitro': {
        app_helper_common_data = this.getAppData('crypto_nitro');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('crypto_nitro')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('crypto_nitro')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('crypto_nitro')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'b-to-b':
      case 'b_to_b':
      case 'bsquare': {
        app_helper_common_data = this.getAppData('bsquare');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('bsquare')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('bsquare')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('bsquare')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'independentcitizens':
      case 'digital-nomads':
      case 'digital_nomads': {
        app_helper_common_data = this.getAppData('independentcitizens');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('independentcitizens')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('independentcitizens')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('independentcitizens')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'gaming':
      case 'gamerworld': {
        app_helper_common_data = this.getAppData('gaming');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('gaming')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('gaming')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('gaming')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'erotic':
      case 'sensualshudder': {
        app_helper_common_data = this.getAppData('sensualshudder');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('sensualshudder')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('sensualshudder')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('sensualshudder')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'roommatefinder':
      case 'roommate': {
        app_helper_common_data = this.getAppData('roommatefinder');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('roommatefinder')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('roommatefinder')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('roommatefinder')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'aviation': {
        app_helper_common_data = this.getAppData('aviation');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('aviation')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('aviation')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('aviation')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }

      case 'goalmanager':
      case 'goal_manager': {
        app_helper_common_data = this.getAppData('goalmanager');
        app_info = {
          header_main_title: 'Join to OffConOn Community',
          header_sub_title: 'Unlock a World of Possibilities',
          back_type: 'video', //video
          backgroundImage: this.getAppConfig('goalmanager')?.app_brand_img_url,
          backgroundVideo: '/assets/images/common/test_video.mp4',
          placeholder: 'What are you looking for?',
          header_boxes: [
            {
              label: 'Stores',
              url: this.getAppConfig('goalmanager')?.app_url + '/store-locator',
              icon: 'travel_explore',
            },

            {
              url:
                app_helper_common_data?.app_url +
                app_helper_common_data?.business_store?.products?.url,
              icon: 'storefront',
              label: app_helper_common_data?.business_store?.products?.name,
            },

            {
              label: app_helper_common_data?.live_plants?.name,
              url: app_helper_common_data?.app_url + app_helper_common_data?.live_plants?.url,
              icon: 'yard',
            },

            {
              label: 'More',
              icon: 'more_horiz',
              url: this.getAppConfig('goalmanager')?.app_url + '/dashboard',
            },
          ],
        };
        break;
      }
    }

    return app_info;
  }

  getModuleConfig(app_link: string[] | string | boolean = false) {
    let app_info: any = null;
    app_info = [
      {
        type_enum: 'crm',
        icon: 'group',
        url: '/cognitive-erp/crm',
        name: 'Customer Relationship Management (CRM)',
        sub_title: 'Build lasting customer relationships with ease!',
        short_desc:
          'Success starts with great customer relationships. Cognitive ERP’s CRM system helps you track customer data, sales opportunities, and activity all in one place. Powered by AI, it allows you to make informed decisions while protecting your data with role-based access.',
        img_path: '/assets/images/landing/modules/crm.jpg',
        visibility: true,
      },
      {
        type_enum: 'video_calling',
        icon: 'videocam',
        url: '/cognitive-erp/video-calling',
        name: 'Chat and Secure Video Calling',
        sub_title: 'Empower your team today!',
        short_desc:
          'Our fully integrated video conferencing system includes whiteboards, document collaboration, and screen sharing. Maximize productivity with the right communication tools.',
        img_path: '/assets/images/landing/modules/video_calling.jpg',
        visibility: true,
      },
      {
        type_enum: 'website_manager',
        icon: 'web',
        url: '/cognitive-erp/website-manager',
        name: 'Website and Application Manager',
        sub_title: 'Create your presence effortlessly!',
        short_desc:
          'Easily set up online websites and applications with Cognitive ERP. Manage your brand’s online presence through pre-made templates or custom designs – no coding required.',
        img_path: '/assets/images/landing/modules/website_manager.jpg',
        visibility: true,
      },
      {
        type_enum: 'rpa',
        icon: 'offline_bolt',
        url: '/cognitive-erp/rpa',
        name: 'Robotic Process Automation (RPA)',
        sub_title: 'Automation for growth!',
        short_desc:
          'Eliminate manual tasks and increase efficiency with our RPA features. Automate processes and focus on growing your business.',
        img_path: '/assets/images/landing/modules/rpa.jpg',
        visibility: true,
      },
      {
        type_enum: 'hrms',
        icon: 'people',
        url: '/cognitive-erp/hrms',
        name: 'Human Resource Management System (HRMS)',
        sub_title: 'Simplify HR tasks today!',
        short_desc:
          'Automate and simplify employee data management, recruitment, and performance tracking with Cognitive ERP’s HRMS.',
        img_path: '/assets/images/landing/modules/hrms.jpg',
        visibility: true,
      },
      {
        type_enum: 'selling_products',
        icon: 'shopping_cart',
        url: '/cognitive-erp/selling-products',
        name: 'Selling Physical Products',
        sub_title: 'Sell smarter and faster!',
        short_desc:
          'Automate the entire sales process with real-time inventory updates and multi-channel support for selling physical products.',
        img_path: '/assets/images/landing/modules/selling_products.jpg',
        visibility: true,
      },
      {
        type_enum: 'services',
        icon: 'build',
        url: '/cognitive-erp/services',
        name: 'Offer Your Professional Services',
        sub_title: 'Effortless service management!',
        short_desc:
          'Set up service offerings, manage client relationships, and automate processes to deliver professional services with ease.',
        img_path: '/assets/images/landing/modules/services.jpg',
        visibility: true,
      },
      {
        type_enum: 'digital_products',
        icon: 'cloud_download',
        url: '/cognitive-erp/digital-products',
        name: 'Digital Products',
        sub_title: 'Streamline your sales!',
        short_desc:
          'Sell digital goods seamlessly. From downloads to media, Cognitive ERP offers an efficient platform for selling non-physical products.',
        img_path: '/assets/images/landing/modules/digital_products.jpg',
        visibility: true,
      },
      {
        type_enum: 'subscription_channel',
        icon: 'subscriptions',
        url: '/cognitive-erp/subscription-channel',
        name: 'Subscription Media Channel',
        sub_title: 'Deliver content effortlessly!',
        short_desc:
          'Launch and manage a subscription-based media channel. Cognitive ERP provides tools to deliver content and manage subscribers seamlessly.',
        img_path: '/assets/images/landing/modules/subscription_channel.jpg',
        visibility: true,
      },
      {
        type_enum: 'rentals',
        icon: 'event_seat',
        url: '/cognitive-erp/rentals',
        name: 'Renting of Products',
        sub_title: 'Simplify rental management!',
        short_desc:
          'Automate bookings and inventory tracking with Cognitive ERP’s rental module for an efficient and accurate customer experience.',
        img_path: '/assets/images/landing/modules/rentals.jpg',
        visibility: true,
      },
      {
        type_enum: 'bookable_events',
        icon: 'event',
        url: '/cognitive-erp/bookable-events',
        name: 'Offer Bookable Events and Places',
        sub_title: 'Manage events effortlessly!',
        short_desc:
          'Simplify event bookings and venue reservations with Cognitive ERP. From booking to payment, streamline the entire process.',
        img_path: '/assets/images/landing/modules/bookable_events.jpg',
        visibility: true,
      },
      {
        type_enum: 'food_and_drink',
        icon: 'local_dining',
        url: '/cognitive-erp/food-and-drink',
        name: 'Food and Drink Sales',
        sub_title: 'Run your F&B business smarter!',
        short_desc:
          'Streamline order management, inventory, and customer experiences for your food and beverage business with Cognitive ERP.',
        img_path: '/assets/images/landing/modules/food_and_drink.jpg',
        visibility: true,
      },
      {
        type_enum: 'labor_market',
        icon: 'work',
        url: '/cognitive-erp/labor-market',
        name: 'Labor Market',
        sub_title: 'Connect job seekers with opportunities!',
        short_desc:
          'Match businesses with job seekers. Post jobs, manage applications, and simplify hiring processes with Cognitive ERP.',
        img_path: '/assets/images/landing/modules/labor_market.jpg',
        visibility: true,
      },
      {
        type_enum: 'task_manager',
        icon: 'check_circle',
        url: '/cognitive-erp/task-manager',
        name: 'Task Manager',
        sub_title: 'Stay on track and meet deadlines!',
        short_desc:
          'Plan, track, and manage tasks and projects efficiently with Cognitive ERP’s task manager.',
        img_path: '/assets/images/landing/modules/task_manager.jpg',
        visibility: true,
      },
      {
        type_enum: 'bi_reports',
        icon: 'analytics',
        url: '/cognitive-erp/bi-reports',
        name: 'Business Intelligence Reports (BI)',
        sub_title: 'Make data-driven decisions!',
        short_desc:
          'Access actionable insights with Cognitive ERP’s BI reports. Stay ahead in a competitive landscape.',
        img_path: '/assets/images/landing/modules/bi_reports.jpg',
        visibility: true,
      },
      {
        type_enum: 'physical_products',
        icon: 'store',
        url: '/cognitive-erp/physical-products',
        name: 'Management of Physical Products',
        sub_title: 'Streamline retail operations!',
        short_desc:
          'Manage inventory and customer engagement across every touchpoint with Cognitive ERP’s retail tools.',
        img_path: '/assets/images/landing/modules/physical_products.jpg',
        visibility: true,
      },
      {
        type_enum: 'company_stream',
        icon: 'chat',
        url: '/cognitive-erp/company-stream',
        name: 'Company Stream',
        sub_title: 'Stay connected and informed!',
        short_desc:
          'Collaborate effortlessly with Cognitive ERP’s communication tools in an immersive, automated workspace.',
        img_path: '/assets/images/landing/modules/company_stream.jpg',
        visibility: true,
      },
      {
        type_enum: 'org_chart',
        icon: 'account_tree',
        url: '/cognitive-erp/org-chart',
        name: 'Org Chart',
        sub_title: 'Visualize your company’s structure!',
        short_desc:
          'Represent employees and positions clearly with Cognitive ERP’s org chart module.',
        img_path: '/assets/images/landing/modules/org_chart.jpg',
        visibility: true,
      },
      {
        type_enum: 'sales_team',
        icon: 'group_add',
        url: '/cognitive-erp/sales-team',
        name: 'Sales Team',
        sub_title: 'Optimize sales performance!',
        short_desc:
          'Track performance, guide strategies, and improve profitability across all channels with Cognitive ERP’s sales tools.',
        img_path: '/assets/images/landing/modules/sales_team.jpg',
        visibility: true,
      },
      {
        type_enum: 'target_market',
        icon: 'ads_click',
        url: '/cognitive-erp/target-market',
        name: 'Target Market',
        sub_title: 'Reach the right audience!',
        short_desc:
          'Define and target specific customer groups with personalized offers using Cognitive ERP’s data-driven marketing tools.',
        img_path: '/assets/images/landing/modules/target_market.jpg',
        visibility: true,
      },
      {
        type_enum: 'email_accounts',
        icon: 'email',
        url: '/cognitive-erp/email-accounts',
        name: 'Email Accounts',
        sub_title: 'Centralize your communication!',
        short_desc:
          'Manage all your email accounts efficiently in one place with Cognitive ERP’s email tools.',
        img_path: '/assets/images/landing/modules/email_accounts.jpg',
        visibility: true,
      },
      {
        type_enum: 'rbac',
        icon: 'lock',
        url: '/cognitive-erp/rbac',
        name: 'Role-Based Access Control (RBAC)',
        sub_title: 'Ensure data security!',
        short_desc:
          'Protect your data with role-based access. Employees only access modules relevant to their role.',
        img_path: '/assets/images/landing/modules/rbac.jpg',
        visibility: true,
      },
      {
        type_enum: 'worktime_manager',
        icon: 'schedule',
        url: '/cognitive-erp/worktime-manager',
        name: 'Worktime Manager',
        sub_title: 'Boost productivity!',
        short_desc:
          'Track and manage employee work hours, automate schedules, and monitor productivity with Cognitive ERP’s worktime manager.',
        img_path: '/assets/images/landing/modules/worktime_manager.jpg',
        visibility: false,
      },
      {
        type_enum: 'social_connections',
        icon: 'public',
        url: '/cognitive-erp/social-connections',
        name: 'Social Connections',
        sub_title: 'Engage customers effectively!',
        short_desc:
          'Deliver personalized, seamless experiences with integrated social connections that drive loyalty.',
        img_path: '/assets/images/landing/modules/social_connections.jpg',
        visibility: true,
      },
      {
        type_enum: 'marketing_tools',
        icon: 'campaign',
        url: '/cognitive-erp/marketing-tools',
        name: 'Marketing Tools',
        sub_title: 'Optimize your campaigns!',
        short_desc:
          'Maximize marketing efforts with connected customer profiles, real-time intelligence, and personalized campaigns.',
        img_path: '/assets/images/landing/modules/marketing_tools.jpg',
        visibility: true,
      },
      {
        type_enum: 'location_management',
        icon: 'location_on',
        url: '/cognitive-erp/location-management',
        name: 'Location Management',
        sub_title: 'Manage locations efficiently!',
        short_desc:
          'Ensure consistency across branches and streamline operations in one central system with Cognitive ERP.',
        img_path: '/assets/images/landing/modules/location_management.jpg',
        visibility: true,
      },
      {
        type_enum: 'accounting',
        icon: 'account_balance',
        url: '/cognitive-erp/accounting',
        name: 'Accounting and Invoicing',
        sub_title: 'Simplify your finances!',
        short_desc:
          'Automate accounting processes, cross-channel invoicing, and financial tracking with Cognitive ERP.',
        img_path: '/assets/images/landing/modules/accounting.jpg',
        visibility: false,
      },
      {
        type_enum: 'tax_management',
        icon: 'money',
        url: '/cognitive-erp/tax-management',
        name: 'Tax Management',
        sub_title: 'Ensure compliance!',
        short_desc:
          'Simplify tax processes and ensure compliance with Cognitive ERP’s automated tax management tools.',
        img_path: '/assets/images/landing/modules/tax_management.jpg',
        visibility: true,
      },
      {
        type_enum: 'procurement',
        icon: 'shopping_basket',
        url: '/cognitive-erp/procurement',
        name: 'Procurement',
        sub_title: 'Streamline purchasing!',
        short_desc: 'Automate purchasing and supplier management processes with Cognitive ERP.',
        img_path: '/assets/images/landing/modules/procurement.jpg',
        visibility: true,
      },
      {
        type_enum: 'transportation',
        icon: 'local_shipping',
        url: '/cognitive-erp/transportation-management',
        name: 'Transportation Management',
        sub_title: 'Optimize logistics!',
        short_desc:
          'Manage transportation logistics, optimize routes, and track deliveries efficiently with Cognitive ERP.',
        img_path: '/assets/images/landing/modules/transportation.jpg',
        visibility: true,
      },
      {
        type_enum: 'payment_management',
        icon: 'payment',
        url: '/cognitive-erp/payment-management',
        name: 'Payment Management',
        sub_title: 'Secure your transactions!',
        short_desc:
          'Handle payment processing securely and efficiently with Cognitive ERP’s integrated payment solutions.',
        img_path: '/assets/images/landing/modules/payment_management.jpg',
        visibility: true,
      },
      {
        type_enum: 'bank_accounts',
        icon: 'account_balance_wallet',
        url: '/cognitive-erp/bank-accounts',
        name: 'Bank Account Management',
        sub_title: 'Track your finances!',
        short_desc:
          'Manage multiple bank accounts, track payments, transfers, and balances with ease using Cognitive ERP.',
        img_path: '/assets/images/landing/modules/bank_accounts.jpg',
        visibility: true,
      },
      {
        type_enum: 'auction_management',
        icon: 'gavel',
        url: '/cognitive-erp/auction-management',
        name: 'Auction Management',
        sub_title: 'Run auctions seamlessly!',
        short_desc:
          'Automate every step of the auction process, from bidding to final sales, with Cognitive ERP’s dedicated tools.',
        img_path: '/assets/images/landing/modules/auction_management.jpg',
        visibility: true,
      },
      {
        type_enum: 'multi_branch',
        icon: 'business',
        url: '/cognitive-erp/multiple-branches',
        name: 'Multiple Branches and Subsidiaries',
        sub_title: 'Expand your reach!',
        short_desc:
          'Manage multiple branches and subsidiaries in one system. Analyze performance holistically or individually with Cognitive ERP.',
        img_path: '/assets/images/landing/modules/multi_branch.jpg',
        visibility: true,
      },
      {
        type_enum: 'coworker_list',
        icon: 'business',
        url: '/cognitive-erp/coworker-list',
        name: 'Coworkers',
        sub_title: 'Organize and collaborate effectively!',
        short_desc:
          'Manage coworker information, assign roles, and enhance team collaboration with ease.',
        img_path: '/assets/images/landing/modules/coworker_list.jpg',
        visibility: true,
      },
      {
        type_enum: 'notes',
        icon: 'note',
        url: '/cognitive-erp/notes',
        name: 'Notes',
        sub_title: 'Keep track of everything important!',
        short_desc:
          'Create, manage, and organize your notes efficiently to stay on top of your tasks.',
        img_path: '/assets/images/landing/modules/notes.jpg',
        visibility: true,
      },
      {
        type_enum: 'drive',
        icon: 'cloud',
        url: '/cognitive-erp/drive',
        name: 'Drive',
        sub_title: 'Secure and share your files!',
        short_desc: 'Store, share, and collaborate on files seamlessly with Cognitive ERP Drive.',
        img_path: '/assets/images/landing/modules/drive.jpg',
        visibility: true,
      },
      {
        type_enum: 'contacts',
        icon: 'contacts',
        url: '/cognitive-erp/contacts',
        name: 'Contacts',
        sub_title: 'Your network, simplified!',
        short_desc:
          'Organize and access your contacts effortlessly for better communication and collaboration.',
        img_path: '/assets/images/landing/modules/contacts.jpg',
        visibility: true,
      },
      {
        type_enum: 'media_gallery',
        icon: 'photo_library',
        url: '/cognitive-erp/media-gallery',
        name: 'Media Gallery',
        sub_title: 'Centralized media management!',
        short_desc: 'Organize, store, and retrieve your media files in one convenient place.',
        img_path: '/assets/images/landing/modules/media_gallery.jpg',
        visibility: true,
      },
      {
        type_enum: 'activity_stream',
        icon: 'timeline',
        url: '/cognitive-erp/activity-stream',
        name: 'Activity Stream',
        sub_title: 'Stay updated in real-time!',
        short_desc:
          'Monitor and track activities across your organization with an intuitive activity stream.',
        img_path: '/assets/images/landing/modules/activity_stream.jpg',
        visibility: true,
      },
      {
        type_enum: 'discount',
        icon: 'local_offer',
        url: '/cognitive-erp/discounts',
        name: 'Discounts / Offers',
        sub_title: 'Boost sales with strategic offers!',
        short_desc:
          'Create, manage, and track discounts and offers to engage customers effectively.',
        img_path: '/assets/images/landing/modules/discount.jpg',
        visibility: true,
      },
      {
        type_enum: 'reputation_manager',
        icon: 'thumb_up',
        url: '/cognitive-erp/reputation-manager',
        name: 'Reputation Manager',
        sub_title: 'Protect and enhance your brand!',
        short_desc:
          'Monitor and manage your business reputation across various channels with ease.',
        img_path: '/assets/images/landing/modules/reputation_manager.jpg',
        visibility: true,
      },
      {
        type_enum: 'insurance_management',
        icon: 'security',
        url: '/cognitive-erp/insurance-management',
        name: 'Insurance Manager',
        sub_title: 'Simplify insurance workflows!',
        short_desc:
          'Manage policies, claims, and insurance documentation seamlessly with Cognitive ERP.',
        img_path: '/assets/images/landing/modules/insurance_management.jpg',
        visibility: true,
      },
    ];

    if (app_link) {
      if (Array.isArray(app_link)) {
        return app_info
          .map((something: any) => {
            if (app_link.includes(something.type_enum)) {
              return something;
            }
          })
          .filter((something: any) => something);
      } else {
        return app_info.find((info: any) => info.type_enum === app_link);
      }
    }
    return app_info;
  }

  getAppMarketingDatas(page: any) {
    let app_info: any = null;

    switch (page) {
      case 'homepage': {
        app_info = {
          module_boxes: [
            {
              name: this.getModuleConfig('crm')?.name,
              icon: this.getModuleConfig('crm')?.icon,
              url: this.getModuleConfig('crm')?.url,
              short_desc: this.getModuleConfig('crm')?.short_desc,
            },
            {
              name: this.getModuleConfig('rpa')?.name,
              icon: this.getModuleConfig('rpa')?.icon,
              url: this.getModuleConfig('rpa')?.url,
              short_desc: this.getModuleConfig('rpa')?.short_desc,
            },
            {
              name: this.getModuleConfig('hrms')?.name,
              icon: this.getModuleConfig('hrms')?.icon,
              url: this.getModuleConfig('hrms')?.url,
              short_desc: this.getModuleConfig('hrms')?.short_desc,
            },
            {
              name: this.getModuleConfig('bi_reports')?.name,
              icon: this.getModuleConfig('bi_reports')?.icon,
              url: this.getModuleConfig('bi_reports')?.url,
              short_desc: this.getModuleConfig('bi_reports')?.short_desc,
            },
            {
              name: this.getModuleConfig('physical_products')?.name,
              icon: this.getModuleConfig('physical_products')?.icon,
              url: this.getModuleConfig('physical_products')?.url,
              short_desc: this.getModuleConfig('physical_products')?.short_desc,
            },
            {
              name: this.getModuleConfig('video_calling')?.name,
              icon: this.getModuleConfig('video_calling')?.icon,
              url: this.getModuleConfig('video_calling')?.url,
              short_desc: this.getModuleConfig('video_calling')?.short_desc,
            },
          ],
        };
        break;
      }
    }

    return app_info;
  }

  allIndustryMarketing() {
    let app_info: any = null;

    app_info = [
      {
        type_enum: 'doctors',
        title: this.getIndustryDatas('doctors')?.['page_name'],
        link: this.getIndustryDatas('doctors')?.['url'],
      },
      {
        type_enum: 'consultation',
        title: this.getIndustryDatas('consultation')?.['page_name'],
        link: this.getIndustryDatas('consultation')?.['url'],
      },
      {
        type_enum: 'beauty',
        title: this.getIndustryDatas('beauty')?.['page_name'],
        link: this.getIndustryDatas('beauty')?.['url'],
      },
      {
        type_enum: 'handyman',
        title: this.getIndustryDatas('handyman')?.['page_name'],
        link: this.getIndustryDatas('handyman')?.['url'],
      },
      {
        type_enum: 'sittings',
        title: this.getIndustryDatas('sittings')?.['page_name'],
        link: this.getIndustryDatas('sittings')?.['url'],
      },
      {
        type_enum: 'renting',
        title: this.getIndustryDatas('renting')?.['page_name'],
        link: this.getIndustryDatas('renting')?.['url'],
      },
      {
        type_enum: 'booking',
        title: this.getIndustryDatas('booking')?.['page_name'],
        link: this.getIndustryDatas('booking')?.['url'],
      },
      {
        type_enum: 'restaurants',
        title: this.getIndustryDatas('restaurants')?.['page_name'],
        link: this.getIndustryDatas('restaurants')?.['url'],
      },
      {
        type_enum: 'food_delivery',
        title: this.getIndustryDatas('food_delivery')?.['page_name'],
        link: this.getIndustryDatas('food_delivery')?.['url'],
      },
      {
        type_enum: 'retail',
        title: this.getIndustryDatas('retail')?.['page_name'],
        link: this.getIndustryDatas('retail')?.['url'],
      },
      {
        type_enum: 'digital_product',
        title: this.getIndustryDatas('digital_product')?.['page_name'],
        link: this.getIndustryDatas('digital_product')?.['url'],
      },
      {
        type_enum: 'digital_service',
        title: this.getIndustryDatas('digital_service')?.['page_name'],
        link: this.getIndustryDatas('digital_service')?.['url'],
      },
      {
        type_enum: 'digital_media',
        title: this.getIndustryDatas('digital_media')?.['page_name'],
        link: this.getIndustryDatas('digital_media')?.['url'],
      },
      {
        type_enum: 'education',
        title: this.getIndustryDatas('education')?.['page_name'],
        link: this.getIndustryDatas('education')?.['url'],
      },
      {
        type_enum: 'healthcare',
        title: this.getIndustryDatas('healthcare')?.['page_name'],
        link: this.getIndustryDatas('healthcare')?.['url'],
      },
      {
        type_enum: 'esoteric',
        title: this.getIndustryDatas('esoteric')?.['page_name'],
        link: this.getIndustryDatas('esoteric')?.['url'],
      },
      {
        type_enum: 'agriculture',
        title: this.getIndustryDatas('agriculture')?.['page_name'],
        link: this.getIndustryDatas('agriculture')?.['url'],
      },
      {
        type_enum: 'hospitality',
        title: this.getIndustryDatas('hospitality')?.['page_name'],
        link: this.getIndustryDatas('hospitality')?.['url'],
      },
      {
        type_enum: 'travel',
        title: this.getIndustryDatas('travel')?.['page_name'],
        link: this.getIndustryDatas('travel')?.['url'],
      },
      {
        type_enum: 'real_estate',
        title: this.getIndustryDatas('real_estate')?.['page_name'],
        link: this.getIndustryDatas('real_estate')?.['url'],
      },
      {
        type_enum: 'head_hunters',
        title: this.getIndustryDatas('head_hunters')?.['page_name'],
        link: this.getIndustryDatas('head_hunters')?.['url'],
      },
      {
        type_enum: 'blogger',
        title: this.getIndustryDatas('blogger')?.['page_name'],
        link: this.getIndustryDatas('blogger')?.['url'],
      },
    ];

    return app_info;
  }

  getIndustryDatas(page: any) {
    let app_info: any = null;
    switch (page) {
      case 'doctors': {
        app_info = {
          type_enum: 'doctors',
          url: 'doctors', //also used as meta url part
          page_name: 'Doctors',
          first_section: {
            title: 'Are you a Doctor?', //also used as meta title
            desc: 'Take your doctor business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/doctor1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/doctor2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],

            /*sections: [
              {
                type_enum: 'rentals',
                name: 'Overwrite Renting of Products',
              },
              {
                type_enum: 'rpa',
                name: 'rparparparparparparparparpa',
              },

            ],*/
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'consultation': {
        app_info = {
          type_enum: 'consultation',
          url: 'consultation', //also used as meta url part
          page_name: 'Consultants',
          first_section: {
            title: 'Are you a Consultant?', //also used as meta title
            desc: 'Take your consultation business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/consultation1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/consultation2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'beauty': {
        app_info = {
          type_enum: 'beauty',
          url: 'beauty', //also used as meta url part
          page_name: 'Beauty',
          first_section: {
            title: 'Are you working at Beauty industry?', //also used as meta title
            desc: 'Take your beauty business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/beauty1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/beauty2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'handyman': {
        app_info = {
          type_enum: 'handyman',
          url: 'handyman', //also used as meta url part
          page_name: 'Service Providers',
          first_section: {
            title: 'Are you a Service provider?', //also used as meta title
            desc: 'Take your business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/handyman1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/handyman2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'sittings': {
        app_info = {
          type_enum: 'sittings',
          url: 'sittings', //also used as meta url part
          page_name: 'Sittings',
          first_section: {
            title: 'Are you doing Sittings?', //also used as meta title
            desc: 'Take your business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/sittings1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/sittings2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'renting': {
        app_info = {
          type_enum: 'renting',
          url: 'renting', //also used as meta url part
          page_name: 'Renting',
          first_section: {
            title: 'Are you Renting out something?', //also used as meta title
            desc: 'Take your renting business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/renting1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/renting2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'booking': {
        app_info = {
          type_enum: 'booking',
          url: 'booking', //also used as meta url part
          page_name: 'Booking',
          first_section: {
            title: 'Are you a the Booking business?', //also used as meta title
            desc: 'Take your booking business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/booking1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/booking2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'restaurants': {
        app_info = {
          type_enum: 'restaurants',
          url: 'restaurants', //also used as meta url part
          page_name: 'Restaurants',
          first_section: {
            title: 'Do you have a Restaurant?', //also used as meta title
            desc: 'Take your restaurant to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/restaurants1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/restaurants2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'food_delivery': {
        app_info = {
          type_enum: 'food_delivery',
          url: 'food_delivery', //also used as meta url part
          page_name: 'Food Delivery',
          first_section: {
            title: 'Are you in the Food delivery business?', //also used as meta title
            desc: 'Take your business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/food_delivery1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/food_delivery2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'retail': {
        app_info = {
          type_enum: 'retail',
          url: 'retail', //also used as meta url part
          page_name: 'Retails',
          first_section: {
            title: 'Are you the Retail sector?', //also used as meta title
            desc: 'Take your business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/retail1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/retail2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'digital_product': {
        app_info = {
          type_enum: 'digital_product',
          url: 'digital_product', //also used as meta url part
          page_name: 'Digital Products',
          first_section: {
            title: 'Are you selling Digital Products?', //also used as meta title
            desc: 'Take your business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/digital_product1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/digital_product2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'digital_service': {
        app_info = {
          type_enum: 'digital_service',
          url: 'digital_service', //also used as meta url part
          page_name: 'Digital Services',
          first_section: {
            title: 'Are you Selling Digital Services?', //also used as meta title
            desc: 'Take your business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/digital_service1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/digital_service2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'digital_media': {
        app_info = {
          type_enum: 'digital_media',
          url: 'digital_media', //also used as meta url part
          page_name: 'Digital Media',
          first_section: {
            title: 'Are you Selling Digital Media?', //also used as meta title
            desc: 'Take your business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/digital_media1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/digital_media2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'education': {
        app_info = {
          type_enum: 'education',
          url: 'education', //also used as meta url part
          page_name: 'Education',
          first_section: {
            title: 'Are you teaching something?', //also used as meta title
            desc: 'Take your education business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/education1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/education2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'healthcare': {
        app_info = {
          type_enum: 'healthcare',
          url: 'healthcare', //also used as meta url part
          page_name: 'Healthcare',
          first_section: {
            title: 'Are you in the Healthcare industry?', //also used as meta title
            desc: 'Take your business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/healthcare1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/healthcare2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'esoteric': {
        app_info = {
          type_enum: 'esoteric',
          url: 'esoteric', //also used as meta url part
          page_name: 'Esoteric',
          first_section: {
            title: 'Are you in an Esoteric business?', //also used as meta title
            desc: 'Take your business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/esoteric1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/esoteric2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'agriculture': {
        app_info = {
          type_enum: 'agriculture',
          url: 'agriculture', //also used as meta url part
          page_name: 'Farming / Agriculture',
          first_section: {
            title: 'Are you in the Farming / Agriculture business?', //also used as meta title
            desc: 'Take your business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational processes. From managing employees to automating sales, our system is designed to revolutionize how you run your business, powered by the latest deep learning technology.', //also used as meta desc
            img_path: '/assets/images/landing/agriculture1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',

            img_path: '/assets/images/landing/agriculture2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'hospitality': {
        app_info = {
          type_enum: 'hospitality',
          url: 'hospitality', //also used as meta url part
          page_name: 'Hospitality',
          first_section: {
            title: 'Are you the Hospitality business?', //also used as meta title
            desc: 'Take your business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/hospitality1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/hospitality2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'travel': {
        app_info = {
          type_enum: 'travel',
          url: 'travel', //also used as meta url part
          page_name: 'Travel',
          first_section: {
            title: 'Are you the Travel industry?', //also used as meta title
            desc: 'Take your travel business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/travel1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/travel2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'real_estate': {
        app_info = {
          type_enum: 'real_estate',
          url: 'real_estate', //also used as meta url part
          page_name: 'Real Estate',
          first_section: {
            title: 'Are you the Real Estate business?', //also used as meta title
            desc: 'Take your business to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/real_estate1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/real_estate2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'head_hunters': {
        app_info = {
          type_enum: 'head_hunters',
          url: 'head_hunters', //also used as meta url part
          page_name: 'Head Hunter Agencies',
          first_section: {
            title: 'Are you Recruiter?', //also used as meta title
            desc: 'Take your Head Hunter Agency to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/head_hunters1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/head_hunters2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
      case 'blogger': {
        app_info = {
          type_enum: 'blogger',
          url: 'blogger', //also used as meta url part
          page_name: 'Bloggers',
          first_section: {
            title: 'Are you a Blogger?', //also used as meta title
            desc: 'Take your Blog to the next level with Offconon – a fully digitized, ready-to-use business management system that covers all your operational needs.', //also used as meta desc
            img_path: '/assets/images/landing/blogger1.jpg', //also used as meta img
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          second_section: {
            title: 'Features',
            desc: 'One System, Many Solutions',
            img_path: '/assets/images/landing/blogger2.jpg',
            img_alt: 'Information',
            info_boxes: [
              {
                title: 'Set up your business / profile',
                desc: 'Create your online presence and gain trust in no time. No website required.',
                icon: 'business_center',
              },
              {
                title: 'Online Booking',
                desc: "Don't miss out on potential bookings. Make it quick and easy for clients to book themselves, and watch your calendar fill up.",
                icon: 'bookmark',
              },
              {
                title: 'Handle cancellations',
                desc: 'Simplify cancellation. Let clients cancel their appointments easily and quick. What is more, you can eliminate last minute cancellation. Don’t let unreliable clients influence your income. Hold them accountable to protect your time and money.',
                icon: 'cancel',
              },
              {
                title: 'Availability, short breaks, off days',
                desc: 'Our calendar is fully personalizable to meet the individual preferences of your business. You decide how and when your clients can book. We’ll take care of the rest.',
                icon: 'calendar_today',
              },
              {
                title: 'Employee management',
                desc: 'Handle your employees based on their popularity and occupation. Create separate calendars for them,or you can easily let the system allow parallel booking at the same time. You can design the calendar with different colours.',
                icon: 'supervisor_account',
              },
              {
                title: 'Busy time period vs. downtime',
                desc: 'Make the best use of your downtime and busiest time. Give a discount for these periods of the day to increase booking. In the same place, add an extra charge for busy periods of the year (e.g. holidays,wedding days).',
                icon: 'redo',
              },
              {
                title: 'Marketing tools',
                desc: 'Beauty wave module (on the core/individual site) is where you can promote your business, keep your books filled, and build loyal client relationships. ',
                icon: 'offline_bolt',
              },
              {
                title: 'Client management',
                desc: 'Get organized and build valuable, durable relationships with a dynamic and stable client database at hand.',
                icon: 'perm_contact_calendar',
              },
            ],
          },
          third_section: {
            title: 'Step into the Digital Business World!',
            desc: 'Ready for better management and operations? Boost your productivity and flexibility, gain a comprehensive view of your business, and embrace next-generation technology. With Offconon, you can manage everything in one place – seamlessly and efficiently.',
            button: [
              //max 2
              {
                title: 'Create your account',
                icon_option: true, //false if hide icon
                icon: 'pi pi-arrow-right',
                iconPos: 'right', //leave blank, default left
                open_url: '/cognitive-erp/create',
                severity: '', //optional
              },
            ],
          },
          fourth_section: {
            all: false, //true / false
            pages: [
              'crm',
              'video_calling',
              'website_manager',
              'rpa',
              'hrms',
              'selling_products',
              'services',
              'digital_products',
              'subscription_channel',
              'rentals',
              'bookable_events',
              'food_and_drink',
              'labor_market',
              'task_manager',
              'bi_reports',
              'physical_products',
              'company_stream',
              'org_chart',
              'sales_team',
              'target_market',
              'email_accounts',
              'rbac',
              'worktime_manager',
              'social_connections',
              'marketing_tools',
              'location_management',
              'accounting',
              'tax_management',
              'procurement',
              'transportation',
              'payment_management',
              'bank_accounts',
              'auction_management',
              'multi_branch',
              'coworker_list',
              'notes',
              'drive',
              'contacts',
              'media_gallery',
              'activity_stream',
              'discount',
              'reputation_manager',
              'insurance_management',
            ],
          },
          fifth_section: {
            title: 'Ready to Get Started?',
            desc: 'Start working with Offconon that can provide everything you need to manage your business.',
            button_data: {
              title: 'Create your account',
              icon_option: true, //false if hide icon
              icon: 'pi pi-arrow-right',
              iconPos: 'right', //leave blank, default left
              open_url: '/cognitive-erp/create',
              severity: '', //optional
            },
          },
          module_boxes: [], //remove all [] if you don't want to display
          share: {
            title: 'Millions of business people are looking for new opportunity!',
            desc: 'Help us build a fantastic community. A quick share helps us a lot!',
          },
        };
        break;
      }
    }

    return app_info;
  }
}
